import React, { useEffect, useMemo, useState } from "react";
import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserPool,
  CognitoUserAttribute
} from "amazon-cognito-identity-js";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import "./styles.css";
import { stage2ReviewStatus, stage2Translations } from "./i18n/stage2";

const logoSrc = `${process.env.PUBLIC_URL}/banxito-logo.png`;

const config = {
  apiBaseUrl:
    process.env.REACT_APP_BANXITO_API_BASE_URL ??
    "https://02z5eytx7i.execute-api.us-east-1.amazonaws.com",
  userPoolId: process.env.REACT_APP_BANXITO_USER_POOL_ID ?? "us-east-1_rDaPhaYTl",
  userPoolClientId:
    process.env.REACT_APP_BANXITO_USER_POOL_CLIENT_ID ?? "4qp34crrai3hjo3ureo4ibb03d",
  stripePublishableKey: process.env.REACT_APP_BANXITO_STRIPE_PUBLISHABLE_KEY ?? ""
};

const storageKeys = {
  idToken: "banxitoSponsor.idToken",
  email: "banxitoSponsor.email",
  fullName: "banxitoSponsor.fullName",
  pendingSignupProfile: "banxitoSponsor.pendingSignupProfile",
  language: "banxitoSponsor.language"
};

const languageOptions = [
  { value: "en", label: "English" },
  { value: "es", label: "Español" },
  { value: "fr", label: "Français" },
  { value: "fil", label: "Filipino" },
  { value: "ar", label: "العربية" },
  { value: "hi", label: "हिन्दी" },
  { value: "vi", label: "Tiếng Việt" },
  { value: "pa", label: "ਪੰਜਾਬੀ" },
  { value: "sw", label: "Kiswahili" }
];

const rtlLanguages = new Set(["ar"]);

const sponsorTranslations = {
  en: {},
  es: {
    "Language": "Idioma",
    "Banxito Sponsor": "Patrocinador Banxito",
    "Banxito Sponsor MVP": "MVP Patrocinador Banxito",
    "Sponsor care services for family": "Financia cuidado para tu familia",
    "Sign in, create a recipient, purchase restricted service allowances, and track every authorization and fulfillment event from one mobile-first dashboard.": "Inicia sesión, agrega familiares, financia categorías y sigue cada pago y servicio desde un panel móvil.",
    "Sign in": "Iniciar sesión",
    "Create account": "Crear cuenta",
    "Create sponsor account": "Crear cuenta de patrocinador",
    "Verification code": "Código de verificación",
    "Confirm account": "Confirmar cuenta",
    "Sponsor email": "Correo del patrocinador",
    "Send reset code": "Enviar código",
    "Reset code": "Código de reinicio",
    "Update password": "Actualizar contraseña",
    "Forgot password?": "¿Olvidaste tu contraseña?",
    "First name": "Nombre",
    "Last name": "Apellido",
    "Full name": "Nombre completo",
    "Email": "Correo",
    "Phone": "Teléfono",
    "Password": "Contraseña",
    "Confirm password": "Confirmar contraseña",
    "Current password": "Contraseña actual",
    "New password": "Nueva contraseña",
    "Confirm new password": "Confirmar nueva contraseña",
    "Hide": "Ocultar",
    "Show": "Mostrar",
    "Home": "Inicio",
    "Fund": "Financiar",
    "Activity": "Actividad",
    "Me": "Yo",
    "Sign out": "Cerrar sesión",
    "Recipients": "Familiares",
    "Purchased allowances": "Total financiado",
    "Pending purchases": "Pagos pendientes",
    "Active allowances": "Billeteras activas",
    "Add recipient": "Agregar familiar",
    "Recipient setup": "Configurar familiar",
    "Add a family member": "Agregar familiar",
    "Save recipient": "Guardar familiar",
    "Existing recipients and setup codes live on Home under Recipient snapshot.": "Los familiares existentes y sus códigos están en Inicio, bajo Resumen de familiares.",
    "Activity & history": "Actividad e historial",
    "Sponsor operations view": "Vista de actividad",
    "Filter activity": "Filtrar actividad",
    "Slice by recipient, status, and period": "Filtrar por familiar, estado y periodo",
    "Visible events": "Eventos visibles",
    "Allowances purchased": "Financiado en vista",
    "Care delivered": "Cuidado entregado",
    "Redeemed in view": "Usado en vista",
    "Service allowance activity": "Movimientos",
    "Sponsor-visible service events": "Pagos visibles para el patrocinador",
    "Allowance purchase": "Evento de financiamiento",
    "Payment method": "Método de pago",
    "Banxito card payment": "Pago con tarjeta Banxito",
    "Created": "Creado",
    "Completed": "Completado",
    "Not completed yet": "Aún no completado",
    "Category usage": "Uso por categoría",
    "Where sponsored care services were fulfilled": "Dónde se usó realmente el apoyo",
    "Delivered care": "Cuidado entregado",
    "Provider events, receipts, and proof of service": "Servicios, recibos y comprobantes del proveedor",
    "Delivered service": "Servicio entregado",
    "Provider": "Proveedor",
    "Amount used": "Monto usado",
    "Claim status": "Estado de reclamo",
    "No claim yet": "Sin reclamo",
    "Receipt": "Recibo",
    "Open provider receipt": "Abrir recibo",
    "Receipt not uploaded yet": "Recibo aún no cargado",
    "Purchase allowance": "Financiar familiar",
    "Recipient": "Familiar",
    "Relationship": "Relación",
    "Country": "País",
    "Locale": "Idioma local",
    "Currency": "Moneda",
    "Category": "Categoría",
    "Amount": "Monto",
    "Saved payment method": "Método de pago guardado",
    "Add card": "Agregar tarjeta",
    "Save card securely": "Guardar tarjeta de forma segura",
    "Change password": "Cambiar contraseña",
    "Payment": "Pago",
    "Security": "Seguridad",
    "Healthcare": "Salud",
    "Education": "Educación",
    "Essentials": "Esenciales",
    "Specialized Care": "Atención especializada",
    "Brother": "Hermano",
    "Child": "Hijo/a",
    "Cousin": "Primo/a",
    "Father": "Padre",
    "Friend": "Amigo/a",
    "Grandparent": "Abuelo/a",
    "Mother": "Madre",
    "Sister": "Hermana",
    "Spouse": "Cónyuge",
    "Bangladesh": "Bangladés",
    "Dominican Republic": "República Dominicana",
    "Ghana": "Ghana",
    "Guatemala": "Guatemala",
    "India": "India",
    "Kenya": "Kenia",
    "Mexico": "México",
    "Nigeria": "Nigeria",
    "Pakistan": "Pakistán",
    "Philippines": "Filipinas",
    "Pending": "Pendiente",
    "Completed": "Completado",
    "Purchased": "Financiado",
    "Waiting For Recipient Setup": "Esperando configuración del familiar",
    "Waiting for recipient setup": "Esperando configuración del familiar"
  },
  fr: {
    "Language": "Langue",
    "Banxito Sponsor": "Sponsor Banxito",
    "Banxito Sponsor MVP": "MVP Sponsor Banxito",
    "Sponsor care services for family": "Financez les soins de votre famille",
    "Sign in, create a recipient, purchase restricted service allowances, and track every authorization and fulfillment event from one mobile-first dashboard.": "Connectez-vous, ajoutez des proches, financez des catégories et suivez chaque paiement et service depuis un tableau de bord mobile.",
    "Sign in": "Se connecter",
    "Create account": "Créer un compte",
    "Create sponsor account": "Créer un compte sponsor",
    "Verification code": "Code de vérification",
    "Confirm account": "Confirmer le compte",
    "Sponsor email": "E-mail du sponsor",
    "Send reset code": "Envoyer le code",
    "Reset code": "Code de réinitialisation",
    "Update password": "Mettre à jour le mot de passe",
    "Forgot password?": "Mot de passe oublié ?",
    "First name": "Prénom",
    "Last name": "Nom",
    "Full name": "Nom complet",
    "Email": "E-mail",
    "Phone": "Téléphone",
    "Password": "Mot de passe",
    "Confirm password": "Confirmer le mot de passe",
    "Current password": "Mot de passe actuel",
    "New password": "Nouveau mot de passe",
    "Confirm new password": "Confirmer le nouveau mot de passe",
    "Hide": "Masquer",
    "Show": "Afficher",
    "Home": "Accueil",
    "Fund": "Financer",
    "Activity": "Activité",
    "Me": "Moi",
    "Sign out": "Se déconnecter",
    "Recipients": "Proches",
    "Purchased allowances": "Total financé",
    "Pending purchases": "Paiements en attente",
    "Active allowances": "Portefeuilles actifs",
    "Add recipient": "Ajouter un proche",
    "Recipient setup": "Configuration du proche",
    "Add a family member": "Ajouter un membre de la famille",
    "Save recipient": "Enregistrer le proche",
    "Existing recipients and setup codes live on Home under Recipient snapshot.": "Les proches existants et leurs codes se trouvent dans Accueil, sous Aperçu des bénéficiaires.",
    "Activity & history": "Activité et historique",
    "Sponsor operations view": "Vue d'activité",
    "Filter activity": "Filtrer l'activité",
    "Slice by recipient, status, and period": "Filtrer par proche, statut et période",
    "Visible events": "Événements visibles",
    "Allowances purchased": "Financé dans cette vue",
    "Care delivered": "Soins livrés",
    "Redeemed in view": "Utilisé dans cette vue",
    "Service allowance activity": "Service activity",
    "Sponsor-visible service events": "Paiements visibles par le sponsor",
    "Allowance purchase": "Événement de financement",
    "Payment method": "Moyen de paiement",
    "Banxito card payment": "Paiement par carte Banxito",
    "Created": "Créé",
    "Completed": "Terminé",
    "Not completed yet": "Pas encore terminé",
    "Category usage": "Utilisation par catégorie",
    "Where sponsored care services were fulfilled": "Où le soutien financé a été utilisé",
    "Delivered care": "Soins livrés",
    "Provider events, receipts, and proof of service": "Services, reçus et preuves du fournisseur",
    "Delivered service": "Service livré",
    "Provider": "Fournisseur",
    "Amount used": "Montant utilisé",
    "Claim status": "Statut de la demande",
    "No claim yet": "Aucune demande",
    "Receipt": "Reçu",
    "Open provider receipt": "Ouvrir le reçu",
    "Receipt not uploaded yet": "Reçu non encore téléversé",
    "Purchase allowance": "Financer un proche",
    "Recipient": "Bénéficiaire",
    "Relationship": "Lien",
    "Country": "Pays",
    "Locale": "Langue locale",
    "Currency": "Devise",
    "Category": "Catégorie",
    "Amount": "Montant",
    "Saved payment method": "Moyen de paiement enregistré",
    "Add card": "Ajouter une carte",
    "Save card securely": "Enregistrer la carte",
    "Change password": "Changer le mot de passe",
    "Payment": "Paiement",
    "Security": "Sécurité",
    "Healthcare": "Santé",
    "Education": "Éducation",
    "Essentials": "Essentiels",
    "Specialized Care": "Soins spécialisés",
    "Brother": "Frère",
    "Child": "Enfant",
    "Cousin": "Cousin(e)",
    "Father": "Père",
    "Friend": "Ami(e)",
    "Grandparent": "Grand-parent",
    "Mother": "Mère",
    "Sister": "Sœur",
    "Spouse": "Conjoint(e)",
    "Bangladesh": "Bangladesh",
    "Dominican Republic": "République dominicaine",
    "Ghana": "Ghana",
    "Guatemala": "Guatemala",
    "India": "Inde",
    "Kenya": "Kenya",
    "Mexico": "Mexique",
    "Nigeria": "Nigéria",
    "Pakistan": "Pakistan",
    "Philippines": "Philippines",
    "Pending": "En attente",
    "Completed": "Terminé",
    "Purchased": "Financé",
    "Waiting For Recipient Setup": "En attente de configuration",
    "Waiting for recipient setup": "En attente de configuration"
  },
  fil: {
    "Language": "Wika",
    "Banxito Sponsor": "Banxito Sponsor",
    "Banxito Sponsor MVP": "Banxito Sponsor MVP",
    "Sponsor care services for family": "Magpadala ng care para sa pamilya",
    "Sign in, create a recipient, purchase restricted service allowances, and track every authorization and fulfillment event from one mobile-first dashboard.": "Mag-sign in, magdagdag ng recipient, mag-fund ng category wallets, at subaybayan ang payments at services sa mobile-first dashboard.",
    "Sign in": "Mag-sign in",
    "Create account": "Gumawa ng account",
    "Create sponsor account": "Gumawa ng sponsor account",
    "Verification code": "Verification code",
    "Confirm account": "Kumpirmahin ang account",
    "Sponsor email": "Sponsor email",
    "Send reset code": "Ipadala ang reset code",
    "Reset code": "Reset code",
    "Update password": "I-update ang password",
    "Forgot password?": "Nakalimutan ang password?",
    "First name": "Pangalan",
    "Last name": "Apelyido",
    "Full name": "Buong pangalan",
    "Email": "Email",
    "Phone": "Telepono",
    "Password": "Password",
    "Confirm password": "Kumpirmahin ang password",
    "Current password": "Kasalukuyang password",
    "New password": "Bagong password",
    "Confirm new password": "Kumpirmahin ang bagong password",
    "Hide": "Itago",
    "Show": "Ipakita",
    "Home": "Home",
    "Fund": "Fund",
    "Activity": "Activity",
    "Me": "Ako",
    "Sign out": "Mag-sign out",
    "Recipients": "Recipients",
    "Purchased allowances": "Kabuuang funded",
    "Pending purchases": "Pending purchases",
    "Active allowances": "Active allowances",
    "Add recipient": "Magdagdag ng recipient",
    "Recipient setup": "Setup ng recipient",
    "Add a family member": "Magdagdag ng kapamilya",
    "Save recipient": "I-save ang recipient",
    "Existing recipients and setup codes live on Home under Recipient snapshot.": "Makikita ang existing recipients at setup codes sa Home sa ilalim ng Buod ng recipient.",
    "Activity & history": "Aktibidad at history",
    "Sponsor operations view": "View ng sponsor activity",
    "Filter activity": "I-filter ang activity",
    "Slice by recipient, status, and period": "I-filter ayon sa recipient, status, at panahon",
    "Visible events": "Nakikitang events",
    "Allowances purchased": "Naipadala sa view",
    "Care delivered": "Care na naibigay",
    "Redeemed in view": "Nagamit sa view",
    "Service allowance activity": "Mga transaction",
    "Sponsor-visible service events": "Mga bayad na nakikita ng sponsor",
    "Allowance purchase": "Allowance purchase",
    "Payment method": "Paraan ng bayad",
    "Banxito card payment": "Banxito card payment",
    "Created": "Ginawa",
    "Completed": "Tapos",
    "Not completed yet": "Hindi pa tapos",
    "Category usage": "Paggamit ng category",
    "Where sponsored care services were fulfilled": "Saan talaga nagamit ang suporta",
    "Delivered care": "Naibigay na care",
    "Provider events, receipts, and proof of service": "Provider events, resibo, at proof of service",
    "Delivered service": "Naibigay na serbisyo",
    "Provider": "Provider",
    "Amount used": "Nagamit na halaga",
    "Claim status": "Status ng claim",
    "No claim yet": "Wala pang claim",
    "Receipt": "Resibo",
    "Open provider receipt": "Buksan ang resibo",
    "Receipt not uploaded yet": "Wala pang resibo",
    "Purchase allowance": "Purchase allowance",
    "Recipient": "Recipient",
    "Relationship": "Relasyon",
    "Country": "Bansa",
    "Locale": "Lokal na wika",
    "Currency": "Currency",
    "Category": "Category",
    "Amount": "Halaga",
    "Saved payment method": "Saved payment method",
    "Add card": "Magdagdag ng card",
    "Save card securely": "I-save ang card nang secure",
    "Change password": "Palitan ang password",
    "Payment": "Payment",
    "Security": "Security",
    "Healthcare": "Kalusugan",
    "Education": "Edukasyon",
    "Essentials": "Pangunahing kailangan",
    "Specialized Care": "Specialized care",
    "Brother": "Kapatid na lalaki",
    "Child": "Anak",
    "Cousin": "Pinsan",
    "Father": "Ama",
    "Friend": "Kaibigan",
    "Grandparent": "Lolo/Lola",
    "Mother": "Ina",
    "Sister": "Kapatid na babae",
    "Spouse": "Asawa",
    "Philippines": "Pilipinas",
    "Pending": "Pending",
    "Completed": "Completed",
    "Purchased": "Purchased",
    "Waiting For Recipient Setup": "Waiting for recipient setup",
    "Waiting for recipient setup": "Waiting for recipient setup"
  },
  ar: {
    "Language": "اللغة",
    "Banxito Sponsor": "راعي Banxito",
    "Banxito Sponsor MVP": "راعي Banxito",
    "Sponsor care services for family": "موّل الرعاية لعائلتك",
    "Sign in, create a recipient, purchase restricted service allowances, and track every authorization and fulfillment event from one mobile-first dashboard.": "سجل الدخول، أضف مستفيدين، موّل فئات الرعاية، وتابع كل دفعة وخدمة من لوحة واحدة.",
    "Sign in": "تسجيل الدخول",
    "Create account": "إنشاء حساب",
    "Create sponsor account": "إنشاء حساب راعٍ",
    "Verification code": "رمز التحقق",
    "Confirm account": "تأكيد الحساب",
    "Sponsor email": "بريد الراعي",
    "Send reset code": "إرسال رمز",
    "Reset code": "رمز إعادة التعيين",
    "Update password": "تحديث كلمة المرور",
    "Forgot password?": "هل نسيت كلمة المرور؟",
    "First name": "الاسم الأول",
    "Last name": "اسم العائلة",
    "Full name": "الاسم الكامل",
    "Email": "البريد الإلكتروني",
    "Phone": "الهاتف",
    "Password": "كلمة المرور",
    "Confirm password": "تأكيد كلمة المرور",
    "Current password": "كلمة المرور الحالية",
    "New password": "كلمة مرور جديدة",
    "Confirm new password": "تأكيد كلمة المرور الجديدة",
    "Hide": "إخفاء",
    "Show": "إظهار",
    "Home": "الرئيسية",
    "Fund": "تمويل",
    "Activity": "النشاط",
    "Me": "أنا",
    "Sign out": "تسجيل الخروج",
    "Recipients": "المستفيدون",
    "Purchased allowances": "إجمالي التمويل",
    "Pending purchases": "مدفوعات معلقة",
    "Active allowances": "محافظ نشطة",
    "Add recipient": "إضافة مستفيد",
    "Recipient setup": "إعداد المستفيد",
    "Add a family member": "إضافة فرد من العائلة",
    "Save recipient": "حفظ المستفيد",
    "Existing recipients and setup codes live on Home under Recipient snapshot.": "تظهر رموز الإعداد والمستفيدون الحاليون في الرئيسية ضمن ملخص المستفيدين.",
    "Activity & history": "النشاط والسجل",
    "Sponsor operations view": "عرض نشاط المرسل",
    "Filter activity": "تصفية النشاط",
    "Slice by recipient, status, and period": "تصفية حسب المستفيد والحالة والفترة",
    "Visible events": "الأحداث الظاهرة",
    "Allowances purchased": "الممول في العرض",
    "Care delivered": "الرعاية المقدمة",
    "Redeemed in view": "المستخدم في العرض",
    "Service allowance activity": "سجل المعاملات",
    "Sponsor-visible service events": "مدفوعات يراها المرسل",
    "Allowance purchase": "حدث تمويل",
    "Payment method": "طريقة الدفع",
    "Banxito card payment": "دفع ببطاقة Banxito",
    "Created": "تم الإنشاء",
    "Completed": "مكتمل",
    "Not completed yet": "لم يكتمل بعد",
    "Category usage": "استخدام الفئات",
    "Where sponsored care services were fulfilled": "أين تم استخدام الدعم الممول",
    "Delivered care": "الرعاية المقدمة",
    "Provider events, receipts, and proof of service": "خدمات المزود والإيصالات وإثبات الخدمة",
    "Delivered service": "الخدمة المقدمة",
    "Provider": "المزود",
    "Amount used": "المبلغ المستخدم",
    "Claim status": "حالة المطالبة",
    "No claim yet": "لا توجد مطالبة بعد",
    "Receipt": "إيصال",
    "Open provider receipt": "فتح الإيصال",
    "Receipt not uploaded yet": "لم يتم رفع الإيصال بعد",
    "Purchase allowance": "تمويل مستفيد",
    "Recipient": "المستفيد",
    "Relationship": "العلاقة",
    "Country": "البلد",
    "Locale": "اللغة المحلية",
    "Currency": "العملة",
    "Category": "الفئة",
    "Amount": "المبلغ",
    "Saved payment method": "طريقة الدفع المحفوظة",
    "Add card": "إضافة بطاقة",
    "Save card securely": "حفظ البطاقة بأمان",
    "Change password": "تغيير كلمة المرور",
    "Payment": "الدفع",
    "Security": "الأمان",
    "Healthcare": "الصحة",
    "Education": "التعليم",
    "Essentials": "الأساسيات",
    "Specialized Care": "رعاية متخصصة",
    "Brother": "أخ",
    "Child": "ابن/ابنة",
    "Cousin": "قريب",
    "Father": "أب",
    "Friend": "صديق",
    "Grandparent": "جد/جدة",
    "Mother": "أم",
    "Sister": "أخت",
    "Spouse": "زوج/زوجة",
    "Mexico": "المكسيك",
    "India": "الهند",
    "Philippines": "الفلبين",
    "Ghana": "غانا",
    "Kenya": "كينيا",
    "Pending": "قيد الانتظار",
    "Completed": "مكتمل",
    "Purchased": "ممول",
    "Waiting For Recipient Setup": "بانتظار إعداد المستفيد",
    "Waiting for recipient setup": "بانتظار إعداد المستفيد"
  },
  hi: {
    "Language": "भाषा",
    "Banxito Sponsor": "Banxito प्रायोजक",
    "Banxito Sponsor MVP": "Banxito प्रायोजक MVP",
    "Sponsor care services for family": "घर पर परिवार की देखभाल फंड करें",
    "Sign in, create a recipient, purchase restricted service allowances, and track every authorization and fulfillment event from one mobile-first dashboard.": "साइन इन करें, प्राप्तकर्ता जोड़ें, श्रेणी वॉलेट फंड करें, और हर भुगतान व सेवा घटना को मोबाइल डैशबोर्ड से ट्रैक करें।",
    "Sign in": "साइन इन",
    "Create account": "खाता बनाएं",
    "Create sponsor account": "प्रायोजक खाता बनाएं",
    "Verification code": "सत्यापन कोड",
    "Confirm account": "खाता पुष्टि करें",
    "Sponsor email": "प्रायोजक ईमेल",
    "Send reset code": "रीसेट कोड भेजें",
    "Reset code": "रीसेट कोड",
    "Update password": "पासवर्ड अपडेट करें",
    "Forgot password?": "पासवर्ड भूल गए?",
    "First name": "पहला नाम",
    "Last name": "अंतिम नाम",
    "Full name": "पूरा नाम",
    "Email": "ईमेल",
    "Phone": "फ़ोन",
    "Password": "पासवर्ड",
    "Confirm password": "पासवर्ड पुष्टि करें",
    "Current password": "वर्तमान पासवर्ड",
    "New password": "नया पासवर्ड",
    "Confirm new password": "नया पासवर्ड पुष्टि करें",
    "Hide": "छिपाएं",
    "Show": "दिखाएं",
    "Home": "होम",
    "Fund": "फंड",
    "Activity": "गतिविधि",
    "Me": "मैं",
    "Sign out": "साइन आउट",
    "Recipients": "प्राप्तकर्ता",
    "Purchased allowances": "कुल फंड",
    "Pending purchases": "लंबित भुगतान",
    "Active allowances": "सक्रिय वॉलेट",
    "Add recipient": "प्राप्तकर्ता जोड़ें",
    "Recipient setup": "प्राप्तकर्ता सेटअप",
    "Add a family member": "परिवार सदस्य जोड़ें",
    "Save recipient": "प्राप्तकर्ता सेव करें",
    "Existing recipients and setup codes live on Home under Recipient snapshot.": "मौजूदा प्राप्तकर्ता और सेटअप कोड होम में प्राप्तकर्ता सारांश के नीचे दिखते हैं.",
    "Activity & history": "गतिविधि और इतिहास",
    "Sponsor operations view": "स्पॉन्सर गतिविधि दृश्य",
    "Filter activity": "गतिविधि फ़िल्टर करें",
    "Slice by recipient, status, and period": "प्राप्तकर्ता, स्थिति और अवधि से फ़िल्टर करें",
    "Visible events": "दिख रहे इवेंट",
    "Allowances purchased": "इस दृश्य में फंड",
    "Care delivered": "दी गई देखभाल",
    "Redeemed in view": "इस दृश्य में उपयोग",
    "Service allowance activity": "लेन-देन सूची",
    "Sponsor-visible service events": "स्पॉन्सर को दिखने वाले भुगतान",
    "Allowance purchase": "फंडिंग इवेंट",
    "Payment method": "भुगतान विधि",
    "Banxito card payment": "Banxito कार्ड भुगतान",
    "Created": "बनाया गया",
    "Completed": "पूरा हुआ",
    "Not completed yet": "अभी पूरा नहीं हुआ",
    "Category usage": "श्रेणी उपयोग",
    "Where sponsored care services were fulfilled": "फंड की गई सहायता कहाँ उपयोग हुई",
    "Delivered care": "दी गई देखभाल",
    "Provider events, receipts, and proof of service": "प्रदाता सेवाएं, रसीदें और सेवा प्रमाण",
    "Delivered service": "दी गई सेवा",
    "Provider": "प्रदाता",
    "Amount used": "उपयोग राशि",
    "Claim status": "क्लेम स्थिति",
    "No claim yet": "अभी कोई क्लेम नहीं",
    "Receipt": "रसीद",
    "Open provider receipt": "प्रदाता रसीद खोलें",
    "Receipt not uploaded yet": "रसीद अभी अपलोड नहीं हुई",
    "Purchase allowance": "प्राप्तकर्ता को फंड करें",
    "Recipient": "प्राप्तकर्ता",
    "Relationship": "रिश्ता",
    "Country": "देश",
    "Locale": "स्थानीय भाषा",
    "Currency": "मुद्रा",
    "Category": "श्रेणी",
    "Amount": "राशि",
    "Saved payment method": "सहेजी गई भुगतान विधि",
    "Add card": "कार्ड जोड़ें",
    "Save card securely": "कार्ड सुरक्षित रूप से सहेजें",
    "Change password": "पासवर्ड बदलें",
    "Payment": "भुगतान",
    "Security": "सुरक्षा",
    "Healthcare": "स्वास्थ्य",
    "Education": "शिक्षा",
    "Essentials": "ज़रूरी चीज़ें",
    "Specialized Care": "विशेष देखभाल",
    "Brother": "भाई",
    "Child": "बच्चा",
    "Cousin": "चचेरा/ममेरा",
    "Father": "पिता",
    "Friend": "मित्र",
    "Grandparent": "दादा-दादी",
    "Mother": "माता",
    "Sister": "बहन",
    "Spouse": "जीवनसाथी",
    "Mexico": "मेक्सिको",
    "India": "भारत",
    "Philippines": "फिलीपींस",
    "Ghana": "घाना",
    "Kenya": "केन्या",
    "Pending": "लंबित",
    "Completed": "पूर्ण",
    "Purchased": "फंडेड",
    "Waiting For Recipient Setup": "प्राप्तकर्ता सेटअप की प्रतीक्षा",
    "Waiting for recipient setup": "प्राप्तकर्ता सेटअप की प्रतीक्षा"
  }
};

const languageDisplayLabels = {
  en: "English",
  es: "Español",
  fr: "Français",
  fil: "Filipino",
  ar: "العربية",
  hi: "हिन्दी",
  vi: "Tiếng Việt",
  pa: "ਪੰਜਾਬੀ",
  sw: "Kiswahili"
};

const sponsorCleanTranslations = {
  es: {
    "Sponsor home": "Inicio del patrocinador",
    "Support overview": "Resumen de apoyo",
    "See every recipient, service allowance, and fulfillment event in one place before you purchase the next care category.": "Vea cada familiar, saldo y pago en un solo lugar antes de financiar la próxima categoría.",
    "Linked family members": "Familiares vinculados",
    "Recipient snapshot": "Resumen de familiares",
    "No recipients yet": "Aún no hay familiares",
    "Create the first recipient to purchase healthcare, grocery, tuition, or home support allowances.": "Agregue el primer familiar para financiar salud, comida, educación o apoyo del hogar.",
    "Family member": "Familiar",
    "Setup code": "Código de configuración",
    "Share this code so the recipient can finish setting up their account.": "Comparta este código para que el familiar termine de configurar su cuenta.",
    "Expires": "Vence",
    "This recipient still needs a setup code before they can connect their account.": "Este familiar todavía necesita un código antes de conectar su cuenta.",
    "Hide code": "Ocultar código",
    "Show code": "Mostrar código",
    "Copy code": "Copiar código",
    "New code": "Nuevo código",
    "Create code": "Crear código",
    "No service allowances yet. The first successful purchase can be assigned by category here.": "Todavía no hay saldos financiados. El primer pago exitoso se podrá asignar por categoría aquí.",
    "Purchase allowance": "Financiar familiar",
    "Choose a recipient and manage their service allowances": "Elija un familiar y administre sus fondos",
    "Choose recipient": "Elegir familiar",
    "Current available allowance": "Fondos disponibles actuales",
    "Last allowance purchase": "Último financiamiento exitoso",
    "No service allowance purchase yet for this recipient.": "Aún no hay financiamiento exitoso para este familiar.",
    "Choose a recipient first": "Primero elija un familiar",
    "Banxito will then show active allowances, past allowances, and the next purchase options for that family member.": "Banxito mostrará los fondos activos, fondos anteriores y opciones de financiamiento para ese familiar.",
    "Allowance views": "Vista de fondos",
    "Current and past allowances": "Fondos actuales y anteriores",
    "Current allowances": "Fondos actuales",
    "Past allowances": "Fondos anteriores",
    "Active allowance": "Financiamiento activo",
    "Available now": "Disponible ahora",
    "Remaining allowance": "Saldo actual",
    "Started from": "Comenzó con",
    "No current category allowances": "No hay saldos por categoría",
    "This recipient does not have active Banxito category allowances yet. Purchase a new allowance to create them.": "Este familiar aún no tiene saldos activos por categoría. Inicie un nuevo financiamiento para crearlos.",
    "Add allowance": "Agregar más dinero",
    "Purchase new allowance": "Nuevo financiamiento",
    "Keep current split": "Mantener distribución actual",
    "Adjust split before paying": "Ajustar distribución antes de pagar",
    "Reassign current allowance": "Redistribuir fondos actuales",
    "Adjust the active category allowance": "Ajustar saldos por categoría",
    "Remaining allowance:": "Saldo actual:",
    "Reallocated total:": "Total redistribuido:",
    "Confirm reallocation": "Confirmar redistribución",
    "Saving...": "Guardando...",
    "No past allowances yet": "Aún no hay fondos anteriores",
    "Once an allowance has been fully used, Banxito will keep it here as read-only history.": "Cuando un financiamiento se use por completo, Banxito lo guardará aquí como historial de solo lectura.",
    "Read only": "Solo lectura",
    "This allowance has been fully used and cannot be changed.": "Este financiamiento ya se usó por completo y no se puede cambiar.",
    "Plan allowance purchase": "Planificar financiamiento",
    "Add allowance to the active fund": "Agregar más dinero al fondo activo",
    "Purchase a new service allowance": "Iniciar un nuevo financiamiento",
    "Allowance amount in USD": "Monto en USD",
    "Allowance choice": "Opción de financiamiento",
    "Category split": "Distribución por categoría",
    "Keep current category split": "Mantener distribución actual",
    "Adjust before paying": "Ajustar antes de pagar",
    "Pricing band": "Rango de tarifa",
    "Recipient allowance": "Monto para el familiar",
    "This is the service allowance Banxito will make available to the recipient.": "Este es el monto que Banxito colocará en la cuenta del familiar.",
    "Banxito fee": "Tarifa Banxito",
    "Total sponsor charge": "Total a pagar",
    "Recipient sees": "El familiar verá",
    "Estimate pending": "Estimación pendiente",
    "Planned category split for this allowance:": "Distribución prevista para este financiamiento:",
    "Enter normal dollar amounts like 30.00 and 10.00. Banxito will apply this split automatically after payment succeeds.": "Ingrese montos normales como 30.00 y 10.00. Banxito aplicará esta distribución automáticamente después del pago.",
    "Banxito will preserve the current category proportions for this allowance increase.": "Banxito mantendrá las proporciones actuales para esta recarga.",
    "Banxito will use the category amounts you set below for this allowance increase.": "Banxito usará los montos por categoría que indique abajo para esta recarga.",
    "Amount in USD": "Monto en USD",
    "Planned to assign": "Planeado para asignar",
    "Left to assign": "Falta asignar",
    "Continue allowance purchase": "Continuar financiamiento",
    "Payment": "Pago",
    "Saved payment method": "Método de pago guardado",
    "Current card": "Tarjeta actual",
    "Card": "Tarjeta",
    "ending in": "termina en",
    "Used for repeat allowance purchases, allowance increases, and future scheduled support.": "Se usa para repetir financiamientos, recargas automáticas y apoyo programado.",
    "No saved card yet": "Aún no hay tarjeta guardada",
    "Add a card here later to make repeat allowance purchases faster and to support automatic or monthly allowance increases.": "Agregue una tarjeta para financiar más rápido y permitir recargas automáticas o mensuales.",
    "Opening secure form...": "Abriendo formulario seguro...",
    "Update card": "Actualizar tarjeta",
    "Remove saved card": "Eliminar tarjeta guardada",
    "Security": "Seguridad",
    "Save profile updates": "Guardar cambios"
  },
  fr: {
    "Sponsor home": "Accueil sponsor",
    "Support overview": "Vue d'ensemble du soutien",
    "See every recipient, service allowance, and fulfillment event in one place before you purchase the next care category.": "Consultez chaque proche, solde et paiement au même endroit avant de financer la prochaine catégorie.",
    "Linked family members": "Proches liés",
    "Recipient snapshot": "Aperçu des bénéficiaires",
    "No recipients yet": "Aucun proche pour le moment",
    "Create the first recipient to purchase healthcare, grocery, tuition, or home support allowances.": "Ajoutez un premier proche pour financer santé, courses, scolarité ou aide au logement.",
    "Family member": "Proche",
    "Setup code": "Code de configuration",
    "Share this code so the recipient can finish setting up their account.": "Partagez ce code pour que le bénéficiaire termine la création de son compte.",
    "Expires": "Expire le",
    "This recipient still needs a setup code before they can connect their account.": "Ce bénéficiaire a encore besoin d'un code pour connecter son compte.",
    "Hide code": "Masquer le code",
    "Show code": "Afficher le code",
    "Copy code": "Copier le code",
    "New code": "Nouveau code",
    "Create code": "Créer un code",
    "No service allowances yet. The first successful purchase can be assigned by category here.": "Aucun solde financé pour le moment. Le premier paiement réussi pourra être réparti par catégorie ici.",
    "Purchase allowance": "Financer un bénéficiaire",
    "Choose a recipient and manage their service allowances": "Choisissez un bénéficiaire et gérez ses fonds",
    "Choose recipient": "Choisir un bénéficiaire",
    "Current available allowance": "Fonds disponibles",
    "Last allowance purchase": "Dernier financement réussi",
    "No service allowance purchase yet for this recipient.": "Aucun financement réussi pour ce bénéficiaire.",
    "Choose a recipient first": "Choisissez d'abord un bénéficiaire",
    "Banxito will then show active allowances, past allowances, and the next purchase options for that family member.": "Banxito affichera les fonds actifs, l'historique et les options de financement pour ce proche.",
    "Allowance views": "Vue des fonds",
    "Current and past allowances": "Fonds actuels et passés",
    "Current allowances": "Fonds actuels",
    "Past allowances": "Fonds passés",
    "Active allowance": "Financement actif",
    "Available now": "Disponible maintenant",
    "Remaining allowance": "Solde actuel",
    "Started from": "Démarré avec",
    "No current category allowances": "Aucun solde par catégorie",
    "This recipient does not have active Banxito category allowances yet. Purchase a new allowance to create them.": "Ce bénéficiaire n'a pas encore de soldes actifs par catégorie. Lancez un nouveau financement pour les créer.",
    "Add allowance": "Ajouter de l'argent",
    "Purchase new allowance": "Nouveau financement",
    "Keep current split": "Garder la répartition",
    "Adjust split before paying": "Ajuster avant paiement",
    "Reassign current allowance": "Réaffecter les fonds actuels",
    "Adjust the active category allowance": "Ajuster les soldes par catégorie",
    "Remaining allowance:": "Solde actuel :",
    "Reallocated total:": "Total réaffecté :",
    "Confirm reallocation": "Confirmer la réaffectation",
    "Saving...": "Enregistrement...",
    "No past allowances yet": "Aucun fonds passé",
    "Once an allowance has been fully used, Banxito will keep it here as read-only history.": "Lorsqu'un financement est entièrement utilisé, Banxito le conserve ici en lecture seule.",
    "Read only": "Lecture seule",
    "This allowance has been fully used and cannot be changed.": "Ce financement a été entièrement utilisé et ne peut plus être modifié.",
    "Plan allowance purchase": "Planifier le financement",
    "Add allowance to the active fund": "Ajouter de l'argent au fonds actif",
    "Purchase a new service allowance": "Créer un nouveau financement",
    "Allowance amount in USD": "Montant en USD",
    "Allowance choice": "Choix de financement",
    "Category split": "Répartition par catégorie",
    "Keep current category split": "Garder la répartition actuelle",
    "Adjust before paying": "Ajuster avant paiement",
    "Pricing band": "Barème",
    "Recipient allowance": "Montant bénéficiaire",
    "This is the service allowance Banxito will make available to the recipient.": "C'est le montant que Banxito placera sur le compte du bénéficiaire.",
    "Banxito fee": "Frais Banxito",
    "Total sponsor charge": "Total à payer",
    "Recipient sees": "Le bénéficiaire voit",
    "Estimate pending": "Estimation en attente",
    "Planned category split for this allowance:": "Répartition prévue pour ce financement :",
    "Enter normal dollar amounts like 30.00 and 10.00. Banxito will apply this split automatically after payment succeeds.": "Saisissez des montants comme 30.00 et 10.00. Banxito appliquera automatiquement cette répartition après paiement.",
    "Banxito will preserve the current category proportions for this allowance increase.": "Banxito conservera les proportions actuelles pour cette recharge.",
    "Banxito will use the category amounts you set below for this allowance increase.": "Banxito utilisera les montants par catégorie saisis ci-dessous.",
    "Amount in USD": "Montant en USD",
    "Planned to assign": "Prévu à affecter",
    "Left to assign": "Reste à affecter",
    "Continue allowance purchase": "Continuer",
    "Payment": "Paiement",
    "Saved payment method": "Moyen de paiement enregistré",
    "Current card": "Carte actuelle",
    "Card": "Carte",
    "ending in": "se termine par",
    "Used for repeat allowance purchases, allowance increases, and future scheduled support.": "Utilisé pour les financements répétés, recharges et soutiens programmés.",
    "No saved card yet": "Aucune carte enregistrée",
    "Add a card here later to make repeat allowance purchases faster and to support automatic or monthly allowance increases.": "Ajoutez une carte pour accélérer les futurs financements et activer les recharges automatiques ou mensuelles.",
    "Opening secure form...": "Ouverture du formulaire sécurisé...",
    "Update card": "Mettre à jour la carte",
    "Remove saved card": "Supprimer la carte",
    "Security": "Sécurité",
    "Save profile updates": "Enregistrer"
  },
  fil: {
    "Sponsor home": "Sponsor home",
    "Support overview": "Support overview",
    "See every recipient, service allowance, and fulfillment event in one place before you purchase the next care category.": "Tingnan ang bawat recipient, wallet, at payment event sa iisang lugar bago mag-fund ng susunod na category.",
    "Linked family members": "Nakakonektang pamilya",
    "Recipient snapshot": "Recipient snapshot",
    "No recipients yet": "Wala pang recipient",
    "Create the first recipient to purchase healthcare, grocery, tuition, or home support allowances.": "Magdagdag ng unang recipient para magsimulang mag-fund ng healthcare, groceries, tuition, o home support.",
    "Family member": "Family member",
    "Setup code": "Setup code",
    "Share this code so the recipient can finish setting up their account.": "Ibahagi ang code na ito para matapos ng recipient ang account setup.",
    "Expires": "Mag-e-expire",
    "This recipient still needs a setup code before they can connect their account.": "Kailangan pa ng setup code ng recipient bago makonekta ang account.",
    "Hide code": "Itago ang code",
    "Show code": "Ipakita ang code",
    "Copy code": "Kopyahin ang code",
    "New code": "Bagong code",
    "Create code": "Gumawa ng code",
    "Purchase allowance": "Purchase allowance",
    "Choose a recipient and manage their service allowances": "Pumili ng recipient at pamahalaan ang funds",
    "Choose recipient": "Pumili ng recipient",
    "Current available allowance": "Available funds ngayon",
    "Last allowance purchase": "Huling successful funding",
    "Current allowances": "Current funds",
    "Past allowances": "Past funds",
    "Add allowance": "Magdagdag ng pera",
    "Purchase new allowance": "Bagong funding",
    "Keep current split": "Panatilihin ang split",
    "Adjust split before paying": "Ayusin bago magbayad",
    "Plan allowance purchase": "Planuhin ang funding",
    "Allowance amount in USD": "Allowance amount sa USD",
    "Recipient allowance": "Funding ng recipient",
    "Banxito fee": "Banxito fee",
    "Total sponsor charge": "Total charge",
    "Recipient sees": "Makikita ng recipient",
    "Continue allowance purchase": "Magpatuloy",
    "Saved payment method": "Saved payment method",
    "Current card": "Kasalukuyang card",
    "No saved card yet": "Wala pang saved card",
    "Update card": "I-update ang card",
    "Remove saved card": "Tanggalin ang saved card",
    "Save profile updates": "I-save ang profile"
  },
  ar: {
    "Sponsor home": "الرئيسية",
    "Support overview": "ملخص الدعم",
    "See every recipient, service allowance, and fulfillment event in one place before you purchase the next care category.": "شاهد كل مستفيد ورصيد وعملية دفع في مكان واحد قبل تمويل الفئة التالية.",
    "Linked family members": "أفراد العائلة المرتبطون",
    "Recipient snapshot": "ملخص المستفيدين",
    "No recipients yet": "لا يوجد مستفيدون بعد",
    "Create the first recipient to purchase healthcare, grocery, tuition, or home support allowances.": "أضف أول مستفيد لبدء تمويل الصحة أو الطعام أو التعليم أو دعم المنزل.",
    "Family member": "فرد العائلة",
    "Setup code": "رمز الإعداد",
    "Share this code so the recipient can finish setting up their account.": "شارك هذا الرمز حتى يتمكن المستفيد من إكمال إعداد حسابه.",
    "Expires": "ينتهي في",
    "This recipient still needs a setup code before they can connect their account.": "ما زال هذا المستفيد بحاجة إلى رمز إعداد قبل ربط حسابه.",
    "Hide code": "إخفاء الرمز",
    "Show code": "عرض الرمز",
    "Copy code": "نسخ الرمز",
    "New code": "رمز جديد",
    "Create code": "إنشاء رمز",
    "Purchase allowance": "تمويل مستفيد",
    "Choose a recipient and manage their service allowances": "اختر مستفيداً وأدر أمواله",
    "Choose recipient": "اختر مستفيداً",
    "Current available allowance": "الرصيد المتاح حالياً",
    "Last allowance purchase": "آخر تمويل ناجح",
    "Current allowances": "الأموال الحالية",
    "Past allowances": "الأموال السابقة",
    "Add allowance": "إضافة أموال",
    "Purchase new allowance": "تمويل جديد",
    "Keep current split": "الاحتفاظ بالتوزيع",
    "Adjust split before paying": "تعديل التوزيع قبل الدفع",
    "Plan allowance purchase": "تخطيط التمويل",
    "Allowance amount in USD": "مبلغ التمويل بالدولار",
    "Recipient allowance": "تمويل المستفيد",
    "Banxito fee": "رسوم Banxito",
    "Total sponsor charge": "إجمالي الدفع",
    "Recipient sees": "يرى المستفيد",
    "Continue allowance purchase": "متابعة التمويل",
    "Saved payment method": "طريقة الدفع المحفوظة",
    "Current card": "البطاقة الحالية",
    "No saved card yet": "لا توجد بطاقة محفوظة بعد",
    "Update card": "تحديث البطاقة",
    "Remove saved card": "إزالة البطاقة",
    "Save profile updates": "حفظ التغييرات"
  },
  hi: {
    "Sponsor home": "स्पॉन्सर होम",
    "Support overview": "सहायता सारांश",
    "See every recipient, service allowance, and fulfillment event in one place before you purchase the next care category.": "अगली श्रेणी को फंड करने से पहले हर प्राप्तकर्ता, वॉलेट और भुगतान को एक जगह देखें।",
    "Linked family members": "जुड़े परिवार सदस्य",
    "Recipient snapshot": "प्राप्तकर्ता सारांश",
    "No recipients yet": "अभी कोई प्राप्तकर्ता नहीं",
    "Create the first recipient to purchase healthcare, grocery, tuition, or home support allowances.": "स्वास्थ्य, किराना, शिक्षा या घर के समर्थन के लिए पहला प्राप्तकर्ता जोड़ें।",
    "Family member": "परिवार सदस्य",
    "Setup code": "सेटअप कोड",
    "Share this code so the recipient can finish setting up their account.": "यह कोड साझा करें ताकि प्राप्तकर्ता अपना खाता सेट कर सके।",
    "Expires": "समाप्त",
    "This recipient still needs a setup code before they can connect their account.": "इस प्राप्तकर्ता को खाता जोड़ने से पहले सेटअप कोड चाहिए।",
    "Hide code": "कोड छुपाएँ",
    "Show code": "कोड दिखाएँ",
    "Copy code": "कोड कॉपी करें",
    "New code": "नया कोड",
    "Create code": "कोड बनाएं",
    "Purchase allowance": "प्राप्तकर्ता को फंड करें",
    "Choose a recipient and manage their service allowances": "प्राप्तकर्ता चुनें और फंड प्रबंधित करें",
    "Choose recipient": "प्राप्तकर्ता चुनें",
    "Current available allowance": "वर्तमान उपलब्ध राशि",
    "Last allowance purchase": "अंतिम सफल फंडिंग",
    "Current allowances": "वर्तमान फंड",
    "Past allowances": "पिछले फंड",
    "Add allowance": "और पैसा जोड़ें",
    "Purchase new allowance": "नई फंडिंग शुरू करें",
    "Keep current split": "मौजूदा विभाजन रखें",
    "Adjust split before paying": "भुगतान से पहले विभाजन बदलें",
    "Plan allowance purchase": "फंडिंग योजना",
    "Allowance amount in USD": "USD में फंडिंग राशि",
    "Recipient allowance": "प्राप्तकर्ता फंडिंग",
    "Banxito fee": "Banxito शुल्क",
    "Total sponsor charge": "कुल शुल्क",
    "Recipient sees": "प्राप्तकर्ता देखेगा",
    "Continue allowance purchase": "फंडिंग जारी रखें",
    "Saved payment method": "सहेजी गई भुगतान विधि",
    "Current card": "वर्तमान कार्ड",
    "No saved card yet": "अभी कोई कार्ड सहेजा नहीं गया",
    "Update card": "कार्ड अपडेट करें",
    "Remove saved card": "सहेजा कार्ड हटाएँ",
    "Save profile updates": "प्रोफ़ाइल सेव करें"
  }
};

for (const [languageCode, translations] of Object.entries(sponsorCleanTranslations)) {
  sponsorTranslations[languageCode] = {
    ...sponsorTranslations[languageCode],
    ...translations
  };
}

const sponsorTranslationPolish = {
  es: {
    "Support overview": "Resumen de apoyo",
    "See every recipient, service allowance, and fulfillment event in one place before you purchase the next care category.": "Vea cada familiar, saldo y pago en un solo lugar antes de financiar la siguiente categoría.",
    "Purchase allowance": "Financiar familiar",
    "Add recipient": "Agregar familiar",
    "Linked family members": "Familiares vinculados",
    "Recipient snapshot": "Resumen de familiares",
    "Account": "Cuenta",
    "Sponsor profile": "Perfil del patrocinador",
    "Finish setup": "Terminar configuración",
    "Create your sponsor profile": "Crear su perfil de patrocinador",
    "Activate sponsor profile": "Activar perfil",
    "Payment setup missing": "Falta configurar pagos",
    "Add the Banxito payment public key to load the secure payment form.": "Agregue la clave pública de pago de Banxito para cargar el formulario seguro.",
    "Change password": "Cambiar contraseña",
    "Update password": "Actualizar contraseña",
    "Add card": "Agregar tarjeta",
    "Home": "Inicio",
    "Fund": "Financiar",
    "Activity": "Actividad",
    "Me": "Yo"
  },
  fr: {
    "Support overview": "Vue d'ensemble du soutien",
    "See every recipient, service allowance, and fulfillment event in one place before you purchase the next care category.": "Consultez chaque proche, solde et paiement au même endroit avant de financer la prochaine catégorie.",
    "Purchase allowance": "Financer un proche",
    "Add recipient": "Ajouter un proche",
    "Linked family members": "Proches liés",
    "Recipient snapshot": "Aperçu des bénéficiaires",
    "Account": "Compte",
    "Sponsor profile": "Profil du sponsor",
    "Finish setup": "Terminer la configuration",
    "Create your sponsor profile": "Créer votre profil sponsor",
    "Activate sponsor profile": "Activer le profil",
    "Payment setup missing": "Configuration de paiement manquante",
    "Add the Banxito payment public key to load the secure payment form.": "Ajoutez la clé publique de paiement Banxito pour charger le formulaire sécurisé.",
    "Change password": "Changer le mot de passe",
    "Update password": "Mettre à jour le mot de passe",
    "Add card": "Ajouter une carte",
    "Home": "Accueil",
    "Fund": "Financer",
    "Activity": "Activité",
    "Me": "Moi"
  },
  fil: {
    "Sponsor home": "Home ng sponsor",
    "Support overview": "Buod ng suporta",
    "See every recipient, service allowance, and fulfillment event in one place before you purchase the next care category.": "Tingnan ang bawat recipient, balanse, at bayad sa iisang lugar bago magpadala para sa susunod na kategorya.",
    "Recipients": "Mga recipient",
    "Purchased allowances": "Kabuuang naipadala",
    "Pending purchases": "Naghihintay na bayad",
    "Active allowances": "Aktibong wallets",
    "Purchase allowance": "Pondohan ang recipient",
    "Add recipient": "Magdagdag ng recipient",
    "Linked family members": "Nakakonektang pamilya",
    "Recipient snapshot": "Buod ng recipient",
    "Account": "Account",
    "Sponsor profile": "Profile ng sponsor",
    "Finish setup": "Tapusin ang setup",
    "Create your sponsor profile": "Gawin ang sponsor profile",
    "Activate sponsor profile": "I-activate ang profile",
    "Current allowances": "Kasalukuyang pondo",
    "Past allowances": "Nakaraang pondo",
    "Payment setup missing": "Kulang ang setup ng bayad",
    "Add the Banxito payment public key to load the secure payment form.": "Idagdag ang Banxito payment public key para lumabas ang secure payment form.",
    "Change password": "Palitan ang password",
    "Update password": "I-update ang password",
    "Add card": "Magdagdag ng card",
    "Home": "Home",
    "Fund": "Pondo",
    "Activity": "Aktibidad",
    "Me": "Ako",
    "Purchased": "Naipadala",
    "Waiting For Recipient Setup": "Naghihintay sa setup ng recipient"
  },
  ar: {
    "Support overview": "ملخص الدعم",
    "Purchase allowance": "تمويل مستفيد",
    "Add recipient": "إضافة مستفيد",
    "Linked family members": "أفراد العائلة المرتبطون",
    "Recipient snapshot": "ملخص المستفيدين",
    "Account": "الحساب",
    "Sponsor profile": "ملف المرسل",
    "Finish setup": "إنهاء الإعداد",
    "Create your sponsor profile": "إنشاء ملف المرسل",
    "Activate sponsor profile": "تفعيل الملف",
    "Payment setup missing": "إعداد الدفع غير مكتمل",
    "Add the Banxito payment public key to load the secure payment form.": "أضف مفتاح الدفع العام من Banxito لعرض نموذج الدفع الآمن.",
    "Change password": "تغيير كلمة المرور",
    "Update password": "تحديث كلمة المرور",
    "Add card": "إضافة بطاقة",
    "Home": "الرئيسية",
    "Fund": "تمويل",
    "Activity": "النشاط",
    "Me": "أنا"
  },
  hi: {
    "Support overview": "सहायता सारांश",
    "Purchase allowance": "प्राप्तकर्ता को फंड करें",
    "Add recipient": "प्राप्तकर्ता जोड़ें",
    "Linked family members": "जुड़े परिवार सदस्य",
    "Recipient snapshot": "प्राप्तकर्ता सारांश",
    "Account": "खाता",
    "Sponsor profile": "स्पॉन्सर प्रोफाइल",
    "Finish setup": "सेटअप पूरा करें",
    "Create your sponsor profile": "अपनी स्पॉन्सर प्रोफाइल बनाएं",
    "Activate sponsor profile": "प्रोफाइल सक्रिय करें",
    "Payment setup missing": "भुगतान सेटअप अधूरा है",
    "Add the Banxito payment public key to load the secure payment form.": "सुरक्षित भुगतान फॉर्म लोड करने के लिए Banxito payment public key जोड़ें.",
    "Change password": "पासवर्ड बदलें",
    "Update password": "पासवर्ड अपडेट करें",
    "Add card": "कार्ड जोड़ें",
    "Home": "होम",
    "Fund": "फंड",
    "Activity": "गतिविधि",
    "Me": "मैं"
  }
};

for (const [languageCode, translations] of Object.entries(sponsorTranslationPolish)) {
  sponsorTranslations[languageCode] = {
    ...sponsorTranslations[languageCode],
    ...translations
  };
}

const sponsorTopOffTranslations = {
  es: {
    "Health": "Salud",
    "Healthcare": "Salud",
    "Education": "Educación",
    "Essentials": "Esenciales",
    "Home": "Inicio",
    "Specialized Care": "Atención especializada",
    "Auto top-off on": "Recarga automática activa",
    "Auto top-off paused": "Recarga automática pausada",
    "Auto top-off off": "Recarga automática desactivada",
    "Auto top-off not set": "Recarga automática no configurada",
    "Adds": "Agrega",
    "when this category drops below": "cuando esta categoría baja de",
    "Let Banxito remind or automatically increase this category allowance later when its remaining allowance gets low.": "Banxito puede recordarle o recargar automáticamente esta categoría cuando el saldo esté bajo.",
    "Save a card first": "Guarde una tarjeta primero",
    "Automatic top-off needs a saved payment method. Banxito stores only safe card metadata, not the full card number.": "La recarga automática necesita un método de pago guardado. Banxito solo guarda datos seguros de la tarjeta, no el número completo.",
    "Add saved card under Me": "Agregar tarjeta guardada en Yo",
    "Uses": "Usa",
    "Banxito stores only safe card metadata.": "Banxito solo guarda datos seguros de la tarjeta.",
    "Hide settings": "Ocultar configuración",
    "Edit top-off": "Editar recarga",
    "Set auto top-off": "Configurar recarga automática",
    "Pause": "Pausar",
    "Turn back on": "Activar de nuevo",
    "When": "Cuando",
    "drops below": "baja de",
    "local": "local",
    "Add this much (USD)": "Agregar este monto (USD)",
    "Monthly safety limit (USD)": "Límite mensual de seguridad (USD)",
    "Max allowance increases per month": "Máximo de recargas por mes",
    "Wait at least this many days between allowance increases": "Esperar al menos estos días entre recargas",
    "Saving top-off...": "Guardando recarga...",
    "Enable automatic top-off": "Activar recarga automática",
    "Add a saved card under Me before turning this on.": "Agregue una tarjeta guardada en Yo antes de activar esto.",
    "The monthly safety limit must be at least as much as one top-off.": "El límite mensual debe ser al menos igual a una recarga.",
    "Banxito checks this category automatically and uses your saved card only when the remaining allowance drops below your limit.": "Banxito revisa esta categoría automáticamente y usa su tarjeta guardada solo cuando el saldo baja de su límite."
  },
  fr: {
    "Health": "Santé",
    "Healthcare": "Santé",
    "Education": "Éducation",
    "Essentials": "Essentiels",
    "Home": "Accueil",
    "Specialized Care": "Soins spécialisés",
    "Auto top-off on": "Recharge automatique activée",
    "Auto top-off paused": "Recharge automatique en pause",
    "Auto top-off off": "Recharge automatique désactivée",
    "Auto top-off not set": "Recharge automatique non configurée",
    "Adds": "Ajoute",
    "when this category drops below": "lorsque cette catégorie passe sous",
    "Let Banxito remind or automatically increase this category allowance later when its remaining allowance gets low.": "Banxito peut vous rappeler ou recharger automatiquement cette catégorie lorsque son solde devient faible.",
    "Save a card first": "Enregistrez d'abord une carte",
    "Automatic top-off needs a saved payment method. Banxito stores only safe card metadata, not the full card number.": "La recharge automatique nécessite un moyen de paiement enregistré. Banxito conserve uniquement des informations sécurisées de carte, jamais le numéro complet.",
    "Add saved card under Me": "Ajouter une carte dans Moi",
    "Uses": "Utilise",
    "Banxito stores only safe card metadata.": "Banxito conserve uniquement des informations sécurisées de carte.",
    "Hide settings": "Masquer les réglages",
    "Edit top-off": "Modifier la recharge",
    "Set auto top-off": "Configurer la recharge automatique",
    "Pause": "Mettre en pause",
    "Turn back on": "Réactiver",
    "When": "Quand",
    "drops below": "passe sous",
    "local": "locale",
    "Add this much (USD)": "Ajouter ce montant (USD)",
    "Monthly safety limit (USD)": "Limite mensuelle de sécurité (USD)",
    "Max allowance increases per month": "Nombre max de recharges par mois",
    "Wait at least this many days between allowance increases": "Attendre au moins ce nombre de jours entre les recharges",
    "Saving top-off...": "Enregistrement de la recharge...",
    "Enable automatic top-off": "Activer la recharge automatique",
    "Add a saved card under Me before turning this on.": "Ajoutez une carte enregistrée dans Moi avant d'activer cette option.",
    "The monthly safety limit must be at least as much as one top-off.": "La limite mensuelle doit être au moins égale à une recharge.",
    "Banxito checks this category automatically and uses your saved card only when the remaining allowance drops below your limit.": "Banxito vérifie cette catégorie automatiquement et utilise votre carte enregistrée seulement lorsque le solde passe sous votre limite."
  },
  fil: {
    "Health": "Kalusugan",
    "Healthcare": "Kalusugan",
    "Education": "Edukasyon",
    "Essentials": "Pangunahing kailangan",
    "Home": "Home",
    "Specialized Care": "Espesyal na pangangalaga",
    "Auto top-off on": "Naka-on ang auto top-off",
    "Auto top-off paused": "Naka-pause ang auto top-off",
    "Auto top-off off": "Naka-off ang auto top-off",
    "Auto top-off not set": "Hindi pa naka-set ang auto top-off",
    "Adds": "Magdadagdag ng",
    "when this category drops below": "kapag bumaba ang category na ito sa",
    "Let Banxito remind or automatically increase this category allowance later when its remaining allowance gets low.": "Puwedeng magpaalala o awtomatikong mag-refill ang Banxito kapag mababa na ang balanse ng category na ito.",
    "Save a card first": "Mag-save muna ng card",
    "Automatic top-off needs a saved payment method. Banxito stores only safe card metadata, not the full card number.": "Kailangan ng saved payment method para sa automatic top-off. Safe card metadata lang ang iniimbak ng Banxito, hindi ang buong card number.",
    "Add saved card under Me": "Magdagdag ng saved card sa Ako",
    "Uses": "Gagamitin ang",
    "Banxito stores only safe card metadata.": "Safe card metadata lang ang iniimbak ng Banxito.",
    "Hide settings": "Itago ang settings",
    "Edit top-off": "I-edit ang top-off",
    "Set auto top-off": "I-set ang auto top-off",
    "Pause": "I-pause",
    "Turn back on": "I-on muli",
    "When": "Kapag ang",
    "drops below": "ay bumaba sa",
    "local": "lokal",
    "Add this much (USD)": "Magdagdag ng ganito kalaki (USD)",
    "Monthly safety limit (USD)": "Buwanang safety limit (USD)",
    "Max allowance increases per month": "Pinakamaraming top-off bawat buwan",
    "Wait at least this many days between allowance increases": "Maghintay ng ganitong karaming araw sa pagitan ng top-offs",
    "Saving top-off...": "Sine-save ang top-off...",
    "Enable automatic top-off": "I-enable ang automatic top-off",
    "Add a saved card under Me before turning this on.": "Magdagdag muna ng saved card sa Ako bago ito i-on.",
    "The monthly safety limit must be at least as much as one top-off.": "Ang buwanang safety limit ay dapat hindi bababa sa isang top-off.",
    "Banxito checks this category automatically and uses your saved card only when the remaining allowance drops below your limit.": "Awtomatikong chine-check ng Banxito ang category na ito at gagamitin lang ang saved card kapag bumaba ang balanse sa limit mo."
  },
  ar: {
    "Health": "الصحة",
    "Healthcare": "الصحة",
    "Education": "التعليم",
    "Essentials": "الاحتياجات الأساسية",
    "Home": "الرئيسية",
    "Specialized Care": "رعاية متخصصة",
    "Auto top-off on": "إعادة التعبئة التلقائية مفعلة",
    "Auto top-off paused": "إعادة التعبئة التلقائية متوقفة مؤقتاً",
    "Auto top-off off": "إعادة التعبئة التلقائية غير مفعلة",
    "Auto top-off not set": "إعادة التعبئة التلقائية غير مضبوطة",
    "Adds": "يضيف",
    "when this category drops below": "عندما ينخفض رصيد هذه الفئة عن",
    "Let Banxito remind or automatically increase this category allowance later when its remaining allowance gets low.": "يمكن أن يذكرك Banxito أو يعيد تعبئة هذه الفئة تلقائياً عندما ينخفض رصيدها.",
    "Save a card first": "احفظ بطاقة أولاً",
    "Automatic top-off needs a saved payment method. Banxito stores only safe card metadata, not the full card number.": "تحتاج إعادة التعبئة التلقائية إلى طريقة دفع محفوظة. يحفظ Banxito بيانات آمنة فقط عن البطاقة، وليس رقم البطاقة الكامل.",
    "Add saved card under Me": "أضف بطاقة محفوظة من صفحة أنا",
    "Uses": "يستخدم",
    "Banxito stores only safe card metadata.": "يحفظ Banxito بيانات آمنة فقط عن البطاقة.",
    "Hide settings": "إخفاء الإعدادات",
    "Edit top-off": "تعديل إعادة التعبئة",
    "Set auto top-off": "ضبط إعادة التعبئة التلقائية",
    "Pause": "إيقاف مؤقت",
    "Turn back on": "إعادة التفعيل",
    "When": "عندما",
    "drops below": "ينخفض عن",
    "local": "محلي",
    "Add this much (USD)": "أضف هذا المبلغ (USD)",
    "Monthly safety limit (USD)": "الحد الشهري الآمن (USD)",
    "Max allowance increases per month": "الحد الأقصى لإعادة التعبئة شهرياً",
    "Wait at least this many days between allowance increases": "انتظر هذا العدد من الأيام على الأقل بين عمليات التعبئة",
    "Saving top-off...": "جارٍ حفظ إعادة التعبئة...",
    "Enable automatic top-off": "تفعيل إعادة التعبئة التلقائية",
    "Add a saved card under Me before turning this on.": "أضف بطاقة محفوظة من صفحة أنا قبل تفعيل ذلك.",
    "The monthly safety limit must be at least as much as one top-off.": "يجب أن يكون الحد الشهري الآمن مساوياً على الأقل لمبلغ تعبئة واحدة.",
    "Banxito checks this category automatically and uses your saved card only when the remaining allowance drops below your limit.": "يتحقق Banxito من هذه الفئة تلقائياً ويستخدم بطاقتك المحفوظة فقط عندما ينخفض الرصيد عن الحد الذي حددته."
  },
  hi: {
    "Health": "स्वास्थ्य",
    "Healthcare": "स्वास्थ्य",
    "Education": "शिक्षा",
    "Essentials": "ज़रूरी चीज़ें",
    "Home": "होम",
    "Specialized Care": "विशेष देखभाल",
    "Auto top-off on": "ऑटो टॉप-ऑफ चालू है",
    "Auto top-off paused": "ऑटो टॉप-ऑफ रोका गया है",
    "Auto top-off off": "ऑटो टॉप-ऑफ बंद है",
    "Auto top-off not set": "ऑटो टॉप-ऑफ सेट नहीं है",
    "Adds": "जोड़ेगा",
    "when this category drops below": "जब यह श्रेणी इससे नीचे जाएगी",
    "Let Banxito remind or automatically increase this category allowance later when its remaining allowance gets low.": "Banxito आपको याद दिला सकता है या बैलेंस कम होने पर इस श्रेणी को अपने आप भर सकता है।",
    "Save a card first": "पहले कार्ड सेव करें",
    "Automatic top-off needs a saved payment method. Banxito stores only safe card metadata, not the full card number.": "ऑटो टॉप-ऑफ के लिए सेव की गई भुगतान विधि चाहिए। Banxito केवल सुरक्षित कार्ड जानकारी रखता है, पूरा कार्ड नंबर नहीं।",
    "Add saved card under Me": "Me में सेव कार्ड जोड़ें",
    "Uses": "उपयोग होगा",
    "Banxito stores only safe card metadata.": "Banxito केवल सुरक्षित कार्ड जानकारी रखता है।",
    "Hide settings": "सेटिंग छुपाएँ",
    "Edit top-off": "टॉप-ऑफ बदलें",
    "Set auto top-off": "ऑटो टॉप-ऑफ सेट करें",
    "Pause": "रोकें",
    "Turn back on": "फिर चालू करें",
    "When": "जब",
    "drops below": "इससे नीचे जाए",
    "local": "स्थानीय",
    "Add this much (USD)": "इतना जोड़ें (USD)",
    "Monthly safety limit (USD)": "मासिक सुरक्षा सीमा (USD)",
    "Max allowance increases per month": "हर महीने अधिकतम टॉप-ऑफ",
    "Wait at least this many days between allowance increases": "टॉप-ऑफ के बीच कम से कम इतने दिन रुकें",
    "Saving top-off...": "टॉप-ऑफ सेव हो रहा है...",
    "Enable automatic top-off": "ऑटो टॉप-ऑफ चालू करें",
    "Add a saved card under Me before turning this on.": "इसे चालू करने से पहले Me में सेव कार्ड जोड़ें।",
    "The monthly safety limit must be at least as much as one top-off.": "मासिक सुरक्षा सीमा कम से कम एक टॉप-ऑफ जितनी होनी चाहिए।",
    "Banxito checks this category automatically and uses your saved card only when the remaining allowance drops below your limit.": "Banxito इस श्रेणी को अपने आप जांचता है और बैलेंस आपकी सीमा से नीचे आने पर ही सेव कार्ड का उपयोग करता है।"
  }
};

for (const [languageCode, translations] of Object.entries(sponsorTopOffTranslations)) {
  sponsorTranslations[languageCode] = {
    ...sponsorTranslations[languageCode],
    ...translations
  };
}

const sponsorAdditionalLanguageTranslations = {
  vi: {
    "Language": "Ngôn ngữ",
    "Banxito Sponsor": "Nhà tài trợ Banxito",
    "Banxito Sponsor MVP": "MVP Nhà tài trợ Banxito",
    "Sponsor care services for family": "Hỗ trợ chăm sóc cho gia đình ở quê nhà",
    "Sign in": "Đăng nhập",
    "Create account": "Tạo tài khoản",
    "Create sponsor account": "Tạo tài khoản nhà tài trợ",
    "Verification code": "Mã xác minh",
    "Confirm account": "Xác nhận tài khoản",
    "Sponsor email": "Email nhà tài trợ",
    "Forgot password?": "Quên mật khẩu?",
    "Send reset code": "Gửi mã đặt lại",
    "Reset code": "Mã đặt lại",
    "Update password": "Cập nhật mật khẩu",
    "First name": "Tên",
    "Last name": "Họ",
    "Full name": "Họ và tên",
    "Email": "Email",
    "Phone": "Điện thoại",
    "Password": "Mật khẩu",
    "Confirm password": "Xác nhận mật khẩu",
    "Current password": "Mật khẩu hiện tại",
    "New password": "Mật khẩu mới",
    "Confirm new password": "Xác nhận mật khẩu mới",
    "Hide": "Ẩn",
    "Show": "Hiện",
    "Home": "Trang chủ",
    "Fund": "Nạp tiền",
    "Activity": "Hoạt động",
    "Me": "Tôi",
    "Sign out": "Đăng xuất",
    "Sponsor home": "Trang chủ nhà tài trợ",
    "Support overview": "Tổng quan hỗ trợ",
    "See every recipient, service allowance, and fulfillment event in one place before you purchase the next care category.": "Xem mọi người nhận, ví và sự kiện thanh toán ở một nơi trước khi nạp tiền cho danh mục tiếp theo.",
    "Recipients": "Người nhận",
    "Purchased allowances": "Tổng đã nạp",
    "Pending purchases": "Thanh toán đang chờ",
    "Active allowances": "Ví đang hoạt động",
    "Purchase allowance": "Nạp tiền cho người nhận",
    "Add recipient": "Thêm người nhận",
    "Linked family members": "Người thân đã liên kết",
    "Recipient snapshot": "Tóm tắt người nhận",
    "No recipients yet": "Chưa có người nhận",
    "Create the first recipient to purchase healthcare, grocery, tuition, or home support allowances.": "Thêm người nhận đầu tiên để bắt đầu hỗ trợ y tế, thực phẩm, học phí hoặc nhà cửa.",
    "Family member": "Người thân",
    "Setup code": "Mã thiết lập",
    "Share this code so the recipient can finish setting up their account.": "Chia sẻ mã này để người nhận hoàn tất thiết lập tài khoản.",
    "Expires": "Hết hạn",
    "Hide code": "Ẩn mã",
    "Show code": "Hiện mã",
    "Copy code": "Sao chép mã",
    "Create code": "Tạo mã",
    "New code": "Mã mới",
    "Recipient setup": "Thiết lập người nhận",
    "Add a family member": "Thêm người thân",
    "Save recipient": "Lưu người nhận",
    "Relationship": "Mối quan hệ",
    "Country": "Quốc gia",
    "Locale": "Ngôn ngữ/khu vực",
    "Choose recipient": "Chọn người nhận",
    "Current allowances": "Quỹ hiện tại",
    "Past allowances": "Quỹ đã dùng",
    "Current available allowance": "Số tiền hiện có",
    "Last allowance purchase": "Lần nạp thành công gần nhất",
    "Add allowance": "Thêm tiền",
    "Purchase new allowance": "Tạo khoản hỗ trợ mới",
    "Keep current split": "Giữ phân bổ hiện tại",
    "Adjust split before paying": "Điều chỉnh phân bổ trước khi trả",
    "Allowance amount in USD": "Số tiền nạp bằng USD",
    "Recipient allowance": "Số tiền vào tài khoản người nhận",
    "Banxito fee": "Phí Banxito",
    "Total sponsor charge": "Tổng tiền nhà tài trợ trả",
    "Recipient sees": "Người nhận thấy",
    "Continue allowance purchase": "Tiếp tục nạp tiền",
    "Payment": "Thanh toán",
    "Saved payment method": "Phương thức thanh toán đã lưu",
    "No saved card yet": "Chưa có thẻ đã lưu",
    "Add card": "Thêm thẻ",
    "Update card": "Cập nhật thẻ",
    "Remove saved card": "Xóa thẻ đã lưu",
    "Save card securely": "Lưu thẻ an toàn",
    "Save profile updates": "Lưu cập nhật hồ sơ",
    "Security": "Bảo mật",
    "Change password": "Đổi mật khẩu",
    "Health": "Sức khỏe",
    "Healthcare": "Y tế",
    "Education": "Giáo dục",
    "Essentials": "Thiết yếu",
    "Specialized Care": "Chăm sóc đặc biệt",
    "Active": "Đang hoạt động",
    "Pending": "Đang chờ",
    "Purchased": "Đã nạp",
    "Completed": "Hoàn tất",
    "Waiting For Recipient Setup": "Đang chờ người nhận thiết lập",
    "Auto top-off on": "Tự động nạp bổ sung đang bật",
    "Auto top-off paused": "Tự động nạp bổ sung tạm dừng",
    "Auto top-off off": "Tự động nạp bổ sung đã tắt",
    "Auto top-off not set": "Chưa cài tự động nạp bổ sung",
    "Save a card first": "Lưu thẻ trước",
    "Automatic top-off needs a saved payment method. Banxito stores only safe card metadata, not the full card number.": "Tự động nạp bổ sung cần phương thức thanh toán đã lưu. Banxito chỉ lưu thông tin thẻ an toàn, không lưu toàn bộ số thẻ.",
    "Add saved card under Me": "Thêm thẻ đã lưu trong mục Tôi",
    "Set auto top-off": "Cài tự động nạp bổ sung",
    "Enable automatic top-off": "Bật tự động nạp bổ sung",
    "Hide settings": "Ẩn cài đặt",
    "Edit top-off": "Sửa nạp bổ sung"
  },
  pa: {
    "Language": "ਭਾਸ਼ਾ",
    "Banxito Sponsor": "Banxito ਸਪਾਂਸਰ",
    "Banxito Sponsor MVP": "Banxito ਸਪਾਂਸਰ MVP",
    "Sponsor care services for family": "ਘਰ ਪਰਿਵਾਰ ਲਈ ਦੇਖਭਾਲ ਭੇਜੋ",
    "Sign in": "ਸਾਈਨ ਇਨ",
    "Create account": "ਖਾਤਾ ਬਣਾਓ",
    "Create sponsor account": "ਸਪਾਂਸਰ ਖਾਤਾ ਬਣਾਓ",
    "Verification code": "ਤਸਦੀਕ ਕੋਡ",
    "Confirm account": "ਖਾਤਾ ਪੁਸ਼ਟੀ ਕਰੋ",
    "Sponsor email": "ਸਪਾਂਸਰ ਈਮੇਲ",
    "Forgot password?": "ਪਾਸਵਰਡ ਭੁੱਲ ਗਏ?",
    "Send reset code": "ਰੀਸੈਟ ਕੋਡ ਭੇਜੋ",
    "Reset code": "ਰੀਸੈਟ ਕੋਡ",
    "Update password": "ਪਾਸਵਰਡ ਅਪਡੇਟ ਕਰੋ",
    "First name": "ਪਹਿਲਾ ਨਾਮ",
    "Last name": "ਆਖਰੀ ਨਾਮ",
    "Full name": "ਪੂਰਾ ਨਾਮ",
    "Email": "ਈਮੇਲ",
    "Phone": "ਫੋਨ",
    "Password": "ਪਾਸਵਰਡ",
    "Confirm password": "ਪਾਸਵਰਡ ਪੁਸ਼ਟੀ ਕਰੋ",
    "Current password": "ਮੌਜੂਦਾ ਪਾਸਵਰਡ",
    "New password": "ਨਵਾਂ ਪਾਸਵਰਡ",
    "Confirm new password": "ਨਵੇਂ ਪਾਸਵਰਡ ਦੀ ਪੁਸ਼ਟੀ",
    "Hide": "ਲੁਕਾਓ",
    "Show": "ਦਿਖਾਓ",
    "Home": "ਘਰ",
    "Fund": "ਫੰਡ",
    "Activity": "ਸਰਗਰਮੀ",
    "Me": "ਮੈਂ",
    "Sign out": "ਸਾਈਨ ਆਉਟ",
    "Sponsor home": "ਸਪਾਂਸਰ ਘਰ",
    "Support overview": "ਸਹਾਇਤਾ ਸੰਖੇਪ",
    "See every recipient, service allowance, and fulfillment event in one place before you purchase the next care category.": "ਅਗਲੀ ਸੇਵਾ ਸ਼੍ਰੇਣੀ ਨੂੰ ਫੰਡ ਕਰਨ ਤੋਂ ਪਹਿਲਾਂ ਹਰ ਪ੍ਰਾਪਤਕਰਤਾ, ਵਾਲਿਟ ਅਤੇ ਭੁਗਤਾਨ ਇਕ ਥਾਂ ਵੇਖੋ।",
    "Recipients": "ਪ੍ਰਾਪਤਕਰਤਾ",
    "Purchased allowances": "ਕੁੱਲ ਫੰਡ",
    "Pending purchases": "ਬਕਾਇਆ ਭੁਗਤਾਨ",
    "Active allowances": "ਸਰਗਰਮ ਵਾਲਿਟ",
    "Purchase allowance": "ਪ੍ਰਾਪਤਕਰਤਾ ਨੂੰ ਫੰਡ ਕਰੋ",
    "Add recipient": "ਪ੍ਰਾਪਤਕਰਤਾ ਸ਼ਾਮਲ ਕਰੋ",
    "Linked family members": "ਜੁੜੇ ਪਰਿਵਾਰਕ ਮੈਂਬਰ",
    "Recipient snapshot": "ਪ੍ਰਾਪਤਕਰਤਾ ਸੰਖੇਪ",
    "No recipients yet": "ਹਾਲੇ ਕੋਈ ਪ੍ਰਾਪਤਕਰਤਾ ਨਹੀਂ",
    "Family member": "ਪਰਿਵਾਰਕ ਮੈਂਬਰ",
    "Setup code": "ਸੈੱਟਅਪ ਕੋਡ",
    "Share this code so the recipient can finish setting up their account.": "ਇਹ ਕੋਡ ਸਾਂਝਾ ਕਰੋ ਤਾਂ ਜੋ ਪ੍ਰਾਪਤਕਰਤਾ ਆਪਣਾ ਖਾਤਾ ਪੂਰਾ ਕਰ ਸਕੇ।",
    "Expires": "ਮਿਆਦ ਮੁੱਕਦੀ ਹੈ",
    "Hide code": "ਕੋਡ ਲੁਕਾਓ",
    "Show code": "ਕੋਡ ਦਿਖਾਓ",
    "Copy code": "ਕੋਡ ਕਾਪੀ ਕਰੋ",
    "Create code": "ਕੋਡ ਬਣਾਓ",
    "New code": "ਨਵਾਂ ਕੋਡ",
    "Recipient setup": "ਪ੍ਰਾਪਤਕਰਤਾ ਸੈੱਟਅਪ",
    "Add a family member": "ਪਰਿਵਾਰਕ ਮੈਂਬਰ ਸ਼ਾਮਲ ਕਰੋ",
    "Save recipient": "ਪ੍ਰਾਪਤਕਰਤਾ ਸੇਵ ਕਰੋ",
    "Relationship": "ਰਿਸ਼ਤਾ",
    "Country": "ਦੇਸ਼",
    "Locale": "ਭਾਸ਼ਾ/ਇਲਾਕਾ",
    "Choose recipient": "ਪ੍ਰਾਪਤਕਰਤਾ ਚੁਣੋ",
    "Current allowances": "ਮੌਜੂਦਾ ਫੰਡ",
    "Past allowances": "ਪੁਰਾਣੇ ਫੰਡ",
    "Current available allowance": "ਮੌਜੂਦਾ ਉਪਲਬਧ ਰਕਮ",
    "Last allowance purchase": "ਆਖਰੀ ਸਫਲ ਫੰਡਿੰਗ",
    "Add allowance": "ਹੋਰ ਪੈਸੇ ਜੋੜੋ",
    "Purchase new allowance": "ਨਵੀਂ ਫੰਡਿੰਗ ਸ਼ੁਰੂ ਕਰੋ",
    "Keep current split": "ਮੌਜੂਦਾ ਵੰਡ ਰੱਖੋ",
    "Adjust split before paying": "ਭੁਗਤਾਨ ਤੋਂ ਪਹਿਲਾਂ ਵੰਡ ਬਦਲੋ",
    "Allowance amount in USD": "USD ਵਿੱਚ ਫੰਡਿੰਗ ਰਕਮ",
    "Recipient allowance": "ਪ੍ਰਾਪਤਕਰਤਾ ਲਈ ਰਕਮ",
    "Banxito fee": "Banxito ਫੀਸ",
    "Total sponsor charge": "ਕੁੱਲ ਸਪਾਂਸਰ ਚਾਰਜ",
    "Recipient sees": "ਪ੍ਰਾਪਤਕਰਤਾ ਵੇਖਦਾ ਹੈ",
    "Continue allowance purchase": "ਫੰਡਿੰਗ ਜਾਰੀ ਰੱਖੋ",
    "Payment": "ਭੁਗਤਾਨ",
    "Saved payment method": "ਸੇਵ ਕੀਤਾ ਭੁਗਤਾਨ ਤਰੀਕਾ",
    "No saved card yet": "ਹਾਲੇ ਕੋਈ ਸੇਵ ਕਾਰਡ ਨਹੀਂ",
    "Add card": "ਕਾਰਡ ਸ਼ਾਮਲ ਕਰੋ",
    "Update card": "ਕਾਰਡ ਅਪਡੇਟ ਕਰੋ",
    "Remove saved card": "ਸੇਵ ਕਾਰਡ ਹਟਾਓ",
    "Save card securely": "ਕਾਰਡ ਸੁਰੱਖਿਅਤ ਸੇਵ ਕਰੋ",
    "Security": "ਸੁਰੱਖਿਆ",
    "Change password": "ਪਾਸਵਰਡ ਬਦਲੋ",
    "Health": "ਸਿਹਤ",
    "Healthcare": "ਸਿਹਤ ਸੇਵਾ",
    "Education": "ਸਿੱਖਿਆ",
    "Essentials": "ਜ਼ਰੂਰੀ ਚੀਜ਼ਾਂ",
    "Specialized Care": "ਖਾਸ ਦੇਖਭਾਲ",
    "Active": "ਸਰਗਰਮ",
    "Pending": "ਬਕਾਇਆ",
    "Purchased": "ਫੰਡ ਕੀਤਾ",
    "Completed": "ਪੂਰਾ",
    "Waiting For Recipient Setup": "ਪ੍ਰਾਪਤਕਰਤਾ ਸੈੱਟਅਪ ਦੀ ਉਡੀਕ",
    "Auto top-off not set": "ਆਟੋ ਟਾਪ-ਆਫ਼ ਸੈੱਟ ਨਹੀਂ",
    "Save a card first": "ਪਹਿਲਾਂ ਕਾਰਡ ਸੇਵ ਕਰੋ",
    "Automatic top-off needs a saved payment method. Banxito stores only safe card metadata, not the full card number.": "ਆਟੋ ਟਾਪ-ਆਫ਼ ਲਈ ਸੇਵ ਕੀਤਾ ਭੁਗਤਾਨ ਤਰੀਕਾ ਚਾਹੀਦਾ ਹੈ। Banxito ਸਿਰਫ਼ ਸੁਰੱਖਿਅਤ ਕਾਰਡ ਜਾਣਕਾਰੀ ਰੱਖਦਾ ਹੈ, ਪੂਰਾ ਕਾਰਡ ਨੰਬਰ ਨਹੀਂ।",
    "Add saved card under Me": "ਮੈਂ ਵਿੱਚ ਸੇਵ ਕਾਰਡ ਸ਼ਾਮਲ ਕਰੋ",
    "Set auto top-off": "ਆਟੋ ਟਾਪ-ਆਫ਼ ਸੈੱਟ ਕਰੋ",
    "Enable automatic top-off": "ਆਟੋ ਟਾਪ-ਆਫ਼ ਚਾਲੂ ਕਰੋ",
    "Hide settings": "ਸੈਟਿੰਗਾਂ ਲੁਕਾਓ",
    "Edit top-off": "ਟਾਪ-ਆਫ਼ ਸੋਧੋ"
  },
  sw: {
    "Language": "Lugha",
    "Banxito Sponsor": "Mfadhili wa Banxito",
    "Banxito Sponsor MVP": "MVP ya Mfadhili wa Banxito",
    "Sponsor care services for family": "Tuma msaada wa huduma kwa familia nyumbani",
    "Sign in": "Ingia",
    "Create account": "Fungua akaunti",
    "Create sponsor account": "Fungua akaunti ya mfadhili",
    "Verification code": "Msimbo wa uthibitisho",
    "Confirm account": "Thibitisha akaunti",
    "Sponsor email": "Barua pepe ya mfadhili",
    "Forgot password?": "Umesahau nenosiri?",
    "Send reset code": "Tuma msimbo wa kuweka upya",
    "Reset code": "Msimbo wa kuweka upya",
    "Update password": "Sasisha nenosiri",
    "First name": "Jina la kwanza",
    "Last name": "Jina la mwisho",
    "Full name": "Jina kamili",
    "Email": "Barua pepe",
    "Phone": "Simu",
    "Password": "Nenosiri",
    "Confirm password": "Thibitisha nenosiri",
    "Current password": "Nenosiri la sasa",
    "New password": "Nenosiri jipya",
    "Confirm new password": "Thibitisha nenosiri jipya",
    "Hide": "Ficha",
    "Show": "Onyesha",
    "Home": "Mwanzo",
    "Fund": "Weka fedha",
    "Activity": "Shughuli",
    "Me": "Mimi",
    "Sign out": "Ondoka",
    "Sponsor home": "Nyumbani kwa mfadhili",
    "Support overview": "Muhtasari wa msaada",
    "See every recipient, service allowance, and fulfillment event in one place before you purchase the next care category.": "Tazama wapokeaji, pochi, na malipo yote mahali pamoja kabla ya kufadhili huduma inayofuata.",
    "Recipients": "Wapokeaji",
    "Purchased allowances": "Jumla iliyowekwa",
    "Pending purchases": "Malipo yanayosubiri",
    "Active allowances": "Pochi zinazoendelea",
    "Purchase allowance": "Mfadhili mpokeaji",
    "Add recipient": "Ongeza mpokeaji",
    "Linked family members": "Wanafamilia waliounganishwa",
    "Recipient snapshot": "Muhtasari wa mpokeaji",
    "No recipients yet": "Bado hakuna wapokeaji",
    "Family member": "Mwanafamilia",
    "Setup code": "Msimbo wa kuunganisha",
    "Share this code so the recipient can finish setting up their account.": "Shiriki msimbo huu ili mpokeaji akamilishe akaunti yake.",
    "Expires": "Inaisha",
    "Hide code": "Ficha msimbo",
    "Show code": "Onyesha msimbo",
    "Copy code": "Nakili msimbo",
    "Create code": "Tengeneza msimbo",
    "New code": "Msimbo mpya",
    "Recipient setup": "Usanidi wa mpokeaji",
    "Add a family member": "Ongeza mwanafamilia",
    "Save recipient": "Hifadhi mpokeaji",
    "Relationship": "Uhusiano",
    "Country": "Nchi",
    "Locale": "Lugha/eneo",
    "Choose recipient": "Chagua mpokeaji",
    "Current allowances": "Fedha za sasa",
    "Past allowances": "Fedha zilizotumika",
    "Current available allowance": "Fedha zilizopo sasa",
    "Last allowance purchase": "Ufadhili wa mwisho uliofanikiwa",
    "Add allowance": "Ongeza pesa",
    "Purchase new allowance": "Anza ufadhili mpya",
    "Keep current split": "Acha mgao wa sasa",
    "Adjust split before paying": "Badili mgao kabla ya kulipa",
    "Allowance amount in USD": "Kiasi cha ufadhili kwa USD",
    "Recipient allowance": "Kiasi cha mpokeaji",
    "Banxito fee": "Ada ya Banxito",
    "Total sponsor charge": "Jumla utakayolipa",
    "Recipient sees": "Mpokeaji ataona",
    "Continue allowance purchase": "Endelea kufadhili",
    "Payment": "Malipo",
    "Saved payment method": "Njia ya malipo iliyohifadhiwa",
    "No saved card yet": "Bado hakuna kadi iliyohifadhiwa",
    "Add card": "Ongeza kadi",
    "Update card": "Sasisha kadi",
    "Remove saved card": "Ondoa kadi iliyohifadhiwa",
    "Save card securely": "Hifadhi kadi kwa usalama",
    "Save profile updates": "Hifadhi mabadiliko ya wasifu",
    "Security": "Usalama",
    "Change password": "Badili nenosiri",
    "Health": "Afya",
    "Healthcare": "Huduma ya afya",
    "Education": "Elimu",
    "Essentials": "Mahitaji muhimu",
    "Specialized Care": "Huduma maalum",
    "Active": "Inatumika",
    "Pending": "Inasubiri",
    "Purchased": "Imefadhiliwa",
    "Completed": "Imekamilika",
    "Waiting For Recipient Setup": "Inasubiri mpokeaji ajisajili",
    "Auto top-off on": "Kuongeza kiotomatiki kumewashwa",
    "Auto top-off paused": "Kuongeza kiotomatiki kumesitishwa",
    "Auto top-off off": "Kuongeza kiotomatiki kumezimwa",
    "Auto top-off not set": "Kuongeza kiotomatiki hakijawekwa",
    "Save a card first": "Hifadhi kadi kwanza",
    "Automatic top-off needs a saved payment method. Banxito stores only safe card metadata, not the full card number.": "Kuongeza kiotomatiki kunahitaji njia ya malipo iliyohifadhiwa. Banxito huhifadhi tu taarifa salama za kadi, si nambari kamili ya kadi.",
    "Add saved card under Me": "Ongeza kadi iliyohifadhiwa kwenye Mimi",
    "Set auto top-off": "Weka kuongeza kiotomatiki",
    "Enable automatic top-off": "Washa kuongeza kiotomatiki",
    "Hide settings": "Ficha mipangilio",
    "Edit top-off": "Hariri kuongeza"
  }
};

for (const [languageCode, translations] of Object.entries(sponsorAdditionalLanguageTranslations)) {
  sponsorTranslations[languageCode] = {
    ...sponsorTranslations[languageCode],
    ...translations
  };
}

for (const [languageCode, translations] of Object.entries(stage2Translations)) {
  sponsorTranslations[languageCode] = {
    ...sponsorTranslations[languageCode],
    ...translations
  };
}

const sponsorFinalExperienceTranslations = {
  es: {
    "Language": "Idioma",
    "Banxito Sponsor": "Patrocinador Banxito",
    "Sponsor home": "Inicio del patrocinador",
    "Support overview": "Resumen de apoyo",
    "See every recipient, service allowance, and fulfillment event in one place before you purchase the next care category.": "Vea cada familiar, saldo y pago en un solo lugar antes de financiar la siguiente categoría.",
    "Recipients": "Familiares",
    "Purchased allowances": "Total financiado",
    "Pending purchases": "Pagos pendientes",
    "Active allowances": "Billeteras activas",
    "Purchase allowance": "Financiar familiar",
    "Add recipient": "Agregar familiar",
    "Linked family members": "Familiares vinculados",
    "Recipient snapshot": "Resumen de familiares",
    "Current allowances": "Fondos actuales",
    "Past allowances": "Fondos anteriores",
    "Add allowance": "Agregar dinero",
    "Purchase new allowance": "Iniciar nuevo apoyo",
    "Reassign current allowance": "Redistribuir fondos actuales",
    "Add allowance to the active fund": "Agregar dinero al fondo activo",
    "Review and complete payment": "Revisar y completar pago",
    "Allowance amount": "Monto de apoyo",
    "Banxito fee": "Cargo de Banxito",
    "Total charge": "Cargo total",
    "Recipient estimate": "Estimado para el familiar",
    "Allowance FX snapshot": "Tipo de cambio del apoyo",
    "Complete allowance purchase": "Completar apoyo",
    "Ready for payment": "Listo para pagar",
    "Saved payment method": "Método de pago guardado",
    "No saved card yet": "Aún no hay tarjeta guardada",
    "Add card": "Agregar tarjeta",
    "Update card": "Actualizar tarjeta",
    "Remove saved card": "Eliminar tarjeta guardada",
    "Save card securely": "Guardar tarjeta de forma segura",
    "Health": "Salud",
    "Healthcare": "Salud",
    "Education": "Educación",
    "Essentials": "Esenciales",
    "Home": "Inicio",
    "Specialized Care": "Atención especializada",
    "Auto top-off on": "Recarga automática activa",
    "Auto top-off paused": "Recarga automática pausada",
    "Auto top-off off": "Recarga automática desactivada",
    "Auto top-off not set": "Recarga automática no configurada",
    "Adds": "Agrega",
    "when this category drops below": "cuando esta categoría baja de",
    "Let Banxito remind or automatically increase this category allowance later when its remaining allowance gets low.": "Banxito puede recordarle o recargar automáticamente esta categoría cuando el saldo esté bajo.",
    "Save a card first": "Guarde una tarjeta primero",
    "Automatic top-off needs a saved payment method. Banxito stores only safe card metadata, not the full card number.": "La recarga automática necesita un método de pago guardado. Banxito solo guarda datos seguros de la tarjeta, no el número completo.",
    "Add saved card under Me": "Agregar tarjeta guardada en Yo",
    "Uses": "Usa",
    "Banxito stores only safe card metadata.": "Banxito solo guarda datos seguros de la tarjeta.",
    "Hide settings": "Ocultar configuración",
    "Edit top-off": "Editar recarga",
    "Set auto top-off": "Configurar recarga automática",
    "Pause": "Pausar",
    "Turn back on": "Activar de nuevo",
    "When": "Cuando",
    "drops below": "baja de",
    "local": "local",
    "Add this much (USD)": "Agregar este monto (USD)",
    "Monthly safety limit (USD)": "Límite mensual de seguridad (USD)",
    "Max allowance increases per month": "Máximo de recargas por mes",
    "Wait at least this many days between allowance increases": "Esperar al menos estos días entre recargas",
    "Saving top-off...": "Guardando recarga...",
    "Enable automatic top-off": "Activar recarga automática",
    "Add a saved card under Me before turning this on.": "Agregue una tarjeta guardada en Yo antes de activar esto.",
    "The monthly safety limit must be at least as much as one top-off.": "El límite mensual debe ser al menos igual a una recarga.",
    "Banxito checks this category automatically and uses your saved card only when the remaining allowance drops below your limit.": "Banxito revisa esta categoría automáticamente y usa su tarjeta guardada solo cuando el saldo baja de su límite."
  },
  fr: {
    "Language": "Langue",
    "Banxito Sponsor": "Sponsor Banxito",
    "Sponsor home": "Accueil sponsor",
    "Support overview": "Vue d'ensemble du soutien",
    "See every recipient, service allowance, and fulfillment event in one place before you purchase the next care category.": "Consultez chaque proche, solde et paiement au même endroit avant de financer la prochaine catégorie.",
    "Recipients": "Proches",
    "Purchased allowances": "Total financé",
    "Pending purchases": "Paiements en attente",
    "Active allowances": "Portefeuilles actifs",
    "Purchase allowance": "Financer un proche",
    "Add recipient": "Ajouter un proche",
    "Linked family members": "Proches liés",
    "Recipient snapshot": "Aperçu des bénéficiaires",
    "Current allowances": "Fonds actuels",
    "Past allowances": "Fonds passés",
    "Add allowance": "Ajouter de l'argent",
    "Purchase new allowance": "Nouveau soutien",
    "Reassign current allowance": "Réaffecter les fonds actuels",
    "Add allowance to the active fund": "Ajouter de l'argent au fonds actif",
    "Review and complete payment": "Vérifier et payer",
    "Allowance amount": "Montant du soutien",
    "Banxito fee": "Frais Banxito",
    "Total charge": "Total à payer",
    "Recipient estimate": "Estimation pour le proche",
    "Allowance FX snapshot": "Taux de change du soutien",
    "Complete allowance purchase": "Terminer le soutien",
    "Ready for payment": "Prêt pour le paiement",
    "Saved payment method": "Moyen de paiement enregistré",
    "No saved card yet": "Aucune carte enregistrée",
    "Add card": "Ajouter une carte",
    "Update card": "Mettre à jour la carte",
    "Remove saved card": "Supprimer la carte",
    "Save card securely": "Enregistrer la carte en sécurité",
    "Health": "Santé",
    "Healthcare": "Santé",
    "Education": "Éducation",
    "Essentials": "Essentiels",
    "Home": "Accueil",
    "Specialized Care": "Soins spécialisés",
    "Auto top-off on": "Recharge automatique activée",
    "Auto top-off paused": "Recharge automatique en pause",
    "Auto top-off off": "Recharge automatique désactivée",
    "Auto top-off not set": "Recharge automatique non configurée",
    "Adds": "Ajoute",
    "when this category drops below": "lorsque cette catégorie passe sous",
    "Let Banxito remind or automatically increase this category allowance later when its remaining allowance gets low.": "Banxito peut vous rappeler ou recharger automatiquement cette catégorie lorsque son solde devient faible.",
    "Save a card first": "Enregistrez d'abord une carte",
    "Automatic top-off needs a saved payment method. Banxito stores only safe card metadata, not the full card number.": "La recharge automatique nécessite un moyen de paiement enregistré. Banxito conserve uniquement des informations sécurisées de carte, jamais le numéro complet.",
    "Add saved card under Me": "Ajouter une carte dans Moi",
    "Uses": "Utilise",
    "Banxito stores only safe card metadata.": "Banxito conserve uniquement des informations sécurisées de carte.",
    "Hide settings": "Masquer les réglages",
    "Edit top-off": "Modifier la recharge",
    "Set auto top-off": "Configurer la recharge automatique",
    "Pause": "Mettre en pause",
    "Turn back on": "Réactiver",
    "When": "Quand",
    "drops below": "passe sous",
    "local": "locale",
    "Add this much (USD)": "Ajouter ce montant (USD)",
    "Monthly safety limit (USD)": "Limite mensuelle de sécurité (USD)",
    "Max allowance increases per month": "Nombre max de recharges par mois",
    "Wait at least this many days between allowance increases": "Attendre au moins ce nombre de jours entre les recharges",
    "Saving top-off...": "Enregistrement de la recharge...",
    "Enable automatic top-off": "Activer la recharge automatique",
    "Add a saved card under Me before turning this on.": "Ajoutez une carte enregistrée dans Moi avant d'activer cette option.",
    "The monthly safety limit must be at least as much as one top-off.": "La limite mensuelle doit être au moins égale à une recharge.",
    "Banxito checks this category automatically and uses your saved card only when the remaining allowance drops below your limit.": "Banxito vérifie cette catégorie automatiquement et utilise votre carte enregistrée seulement lorsque le solde passe sous votre limite."
  },
  fil: {
    "Language": "Wika",
    "Banxito Sponsor": "Sponsor ng Banxito",
    "Sponsor home": "Home ng sponsor",
    "Support overview": "Buod ng suporta",
    "See every recipient, service allowance, and fulfillment event in one place before you purchase the next care category.": "Tingnan ang bawat recipient, wallet, at bayad sa iisang lugar bago pondohan ang susunod na kategorya.",
    "Recipients": "Mga recipient",
    "Purchased allowances": "Kabuuang naipadala",
    "Pending purchases": "Naghihintay na bayad",
    "Active allowances": "Aktibong wallets",
    "Purchase allowance": "Pondohan ang recipient",
    "Add recipient": "Magdagdag ng recipient",
    "Linked family members": "Nakakonektang pamilya",
    "Recipient snapshot": "Buod ng recipient",
    "Current allowances": "Kasalukuyang pondo",
    "Past allowances": "Nakaraang pondo",
    "Add allowance": "Magdagdag ng pera",
    "Purchase new allowance": "Magsimula ng bagong suporta",
    "Reassign current allowance": "Baguhin ang hati ng kasalukuyang pondo",
    "Add allowance to the active fund": "Magdagdag ng pera sa aktibong pondo",
    "Review and complete payment": "Suriin at tapusin ang bayad",
    "Allowance amount": "Halaga ng suporta",
    "Banxito fee": "Bayad sa Banxito",
    "Total charge": "Kabuuang singil",
    "Recipient estimate": "Tantya para sa recipient",
    "Allowance FX snapshot": "Palitan sa oras ng suporta",
    "Complete allowance purchase": "Tapusin ang suporta",
    "Ready for payment": "Handa nang bayaran",
    "Saved payment method": "Saved payment method",
    "No saved card yet": "Wala pang saved card",
    "Add card": "Magdagdag ng card",
    "Update card": "I-update ang card",
    "Remove saved card": "Tanggalin ang saved card",
    "Save card securely": "I-save ang card nang secure",
    "Health": "Kalusugan",
    "Healthcare": "Kalusugan",
    "Education": "Edukasyon",
    "Essentials": "Pangunahing kailangan",
    "Home": "Home",
    "Specialized Care": "Espesyal na pangangalaga",
    "Auto top-off on": "Naka-on ang auto top-off",
    "Auto top-off paused": "Naka-pause ang auto top-off",
    "Auto top-off off": "Naka-off ang auto top-off",
    "Auto top-off not set": "Hindi pa naka-set ang auto top-off",
    "Adds": "Magdadagdag ng",
    "when this category drops below": "kapag bumaba ang category na ito sa",
    "Let Banxito remind or automatically increase this category allowance later when its remaining allowance gets low.": "Puwedeng magpaalala o awtomatikong mag-refill ang Banxito kapag mababa na ang balanse ng category na ito.",
    "Save a card first": "Mag-save muna ng card",
    "Automatic top-off needs a saved payment method. Banxito stores only safe card metadata, not the full card number.": "Kailangan ng saved payment method para sa automatic top-off. Safe card metadata lang ang iniimbak ng Banxito, hindi ang buong card number.",
    "Add saved card under Me": "Magdagdag ng saved card sa Ako",
    "Uses": "Gagamitin ang",
    "Banxito stores only safe card metadata.": "Safe card metadata lang ang iniimbak ng Banxito.",
    "Hide settings": "Itago ang settings",
    "Edit top-off": "I-edit ang top-off",
    "Set auto top-off": "I-set ang auto top-off",
    "Pause": "I-pause",
    "Turn back on": "I-on muli",
    "When": "Kapag ang",
    "drops below": "ay bumaba sa",
    "local": "lokal",
    "Add this much (USD)": "Magdagdag ng ganito kalaki (USD)",
    "Monthly safety limit (USD)": "Buwanang safety limit (USD)",
    "Max allowance increases per month": "Pinakamaraming top-off bawat buwan",
    "Wait at least this many days between allowance increases": "Maghintay ng ganitong karaming araw sa pagitan ng top-offs",
    "Saving top-off...": "Sine-save ang top-off...",
    "Enable automatic top-off": "I-enable ang automatic top-off",
    "Add a saved card under Me before turning this on.": "Magdagdag muna ng saved card sa Ako bago ito i-on.",
    "The monthly safety limit must be at least as much as one top-off.": "Dapat hindi bababa sa isang top-off ang buwanang safety limit.",
    "Banxito checks this category automatically and uses your saved card only when the remaining allowance drops below your limit.": "Awtomatikong chine-check ng Banxito ang category na ito at gagamitin lang ang saved card kapag bumaba ang balanse sa limit mo."
  },
  ar: {
    "Language": "اللغة",
    "Banxito Sponsor": "راعي Banxito",
    "Sponsor home": "الرئيسية للراعي",
    "Support overview": "ملخص الدعم",
    "See every recipient, service allowance, and fulfillment event in one place before you purchase the next care category.": "شاهد كل مستفيد ومحفظة ودفعة في مكان واحد قبل تمويل فئة الخدمة التالية.",
    "Recipients": "المستفيدون",
    "Purchased allowances": "إجمالي التمويل",
    "Pending purchases": "مدفوعات قيد الانتظار",
    "Active allowances": "محافظ نشطة",
    "Purchase allowance": "تمويل مستفيد",
    "Add recipient": "إضافة مستفيد",
    "Linked family members": "أفراد العائلة المرتبطون",
    "Recipient snapshot": "ملخص المستفيدين",
    "Current allowances": "الأموال الحالية",
    "Past allowances": "الأموال السابقة",
    "Add allowance": "إضافة أموال",
    "Purchase new allowance": "بدء دعم جديد",
    "Reassign current allowance": "إعادة توزيع الأموال الحالية",
    "Add allowance to the active fund": "إضافة أموال إلى الدعم النشط",
    "Review and complete payment": "مراجعة وإكمال الدفع",
    "Allowance amount": "مبلغ الدعم",
    "Banxito fee": "رسوم Banxito",
    "Total charge": "إجمالي المبلغ",
    "Recipient estimate": "المبلغ التقريبي للمستفيد",
    "Allowance FX snapshot": "سعر الصرف عند التمويل",
    "Complete allowance purchase": "إكمال الدعم",
    "Ready for payment": "جاهز للدفع",
    "Saved payment method": "طريقة الدفع المحفوظة",
    "No saved card yet": "لا توجد بطاقة محفوظة بعد",
    "Add card": "إضافة بطاقة",
    "Update card": "تحديث البطاقة",
    "Remove saved card": "إزالة البطاقة",
    "Save card securely": "حفظ البطاقة بأمان",
    "Health": "الصحة",
    "Healthcare": "الرعاية الصحية",
    "Education": "التعليم",
    "Essentials": "الاحتياجات الأساسية",
    "Home": "الرئيسية",
    "Specialized Care": "رعاية متخصصة",
    "Auto top-off on": "إعادة التعبئة التلقائية مفعلة",
    "Auto top-off paused": "إعادة التعبئة التلقائية متوقفة مؤقتاً",
    "Auto top-off off": "إعادة التعبئة التلقائية متوقفة",
    "Auto top-off not set": "لم يتم ضبط إعادة التعبئة التلقائية",
    "Adds": "يضيف",
    "when this category drops below": "عندما تنخفض هذه الفئة عن",
    "Let Banxito remind or automatically increase this category allowance later when its remaining allowance gets low.": "يمكن لـ Banxito تذكيرك أو إعادة تعبئة هذه الفئة تلقائياً عندما ينخفض رصيدها.",
    "Save a card first": "احفظ بطاقة أولاً",
    "Automatic top-off needs a saved payment method. Banxito stores only safe card metadata, not the full card number.": "تحتاج إعادة التعبئة التلقائية إلى طريقة دفع محفوظة. يحفظ Banxito بيانات آمنة فقط عن البطاقة، وليس رقم البطاقة الكامل.",
    "Add saved card under Me": "أضف بطاقة محفوظة من صفحة أنا",
    "Uses": "يستخدم",
    "Banxito stores only safe card metadata.": "يحفظ Banxito بيانات آمنة فقط عن البطاقة.",
    "Hide settings": "إخفاء الإعدادات",
    "Edit top-off": "تعديل إعادة التعبئة",
    "Set auto top-off": "ضبط إعادة التعبئة التلقائية",
    "Pause": "إيقاف مؤقت",
    "Turn back on": "تشغيل مرة أخرى",
    "When": "عندما",
    "drops below": "ينخفض عن",
    "local": "محلي",
    "Add this much (USD)": "إضافة هذا المبلغ (USD)",
    "Monthly safety limit (USD)": "حد الأمان الشهري (USD)",
    "Max allowance increases per month": "الحد الأقصى لإعادة التعبئة شهرياً",
    "Wait at least this many days between allowance increases": "انتظر هذا العدد من الأيام على الأقل بين عمليات التعبئة",
    "Saving top-off...": "جارٍ حفظ إعادة التعبئة...",
    "Enable automatic top-off": "تفعيل إعادة التعبئة التلقائية",
    "Add a saved card under Me before turning this on.": "أضف بطاقة محفوظة من صفحة أنا قبل تفعيل ذلك.",
    "The monthly safety limit must be at least as much as one top-off.": "يجب أن يكون حد الأمان الشهري مساوياً على الأقل لمبلغ تعبئة واحد.",
    "Banxito checks this category automatically and uses your saved card only when the remaining allowance drops below your limit.": "يتحقق Banxito من هذه الفئة تلقائياً ويستخدم بطاقتك المحفوظة فقط عندما ينخفض الرصيد عن الحد الذي حددته."
  },
  hi: {
    "Language": "भाषा",
    "Banxito Sponsor": "Banxito प्रायोजक",
    "Sponsor home": "प्रायोजक होम",
    "Support overview": "सहायता सारांश",
    "See every recipient, service allowance, and fulfillment event in one place before you purchase the next care category.": "अगली सेवा श्रेणी में सहायता भेजने से पहले हर प्राप्तकर्ता, वॉलेट और भुगतान को एक जगह देखें.",
    "Recipients": "प्राप्तकर्ता",
    "Purchased allowances": "कुल सहायता",
    "Pending purchases": "लंबित भुगतान",
    "Active allowances": "सक्रिय वॉलेट",
    "Purchase allowance": "प्राप्तकर्ता को सहायता दें",
    "Add recipient": "प्राप्तकर्ता जोड़ें",
    "Linked family members": "जुड़े परिवार सदस्य",
    "Recipient snapshot": "प्राप्तकर्ता सारांश",
    "Current allowances": "वर्तमान फंड",
    "Past allowances": "पिछले फंड",
    "Add allowance": "और पैसा जोड़ें",
    "Purchase new allowance": "नई सहायता शुरू करें",
    "Reassign current allowance": "वर्तमान फंड फिर से बांटें",
    "Add allowance to the active fund": "सक्रिय फंड में और पैसा जोड़ें",
    "Review and complete payment": "समीक्षा करें और भुगतान पूरा करें",
    "Allowance amount": "सहायता राशि",
    "Banxito fee": "Banxito शुल्क",
    "Total charge": "कुल शुल्क",
    "Recipient estimate": "प्राप्तकर्ता अनुमान",
    "Allowance FX snapshot": "फंडिंग विनिमय दर",
    "Complete allowance purchase": "सहायता पूरी करें",
    "Ready for payment": "भुगतान के लिए तैयार",
    "Saved payment method": "सहेजी गई भुगतान विधि",
    "No saved card yet": "अभी कोई कार्ड सहेजा नहीं गया",
    "Add card": "कार्ड जोड़ें",
    "Update card": "कार्ड अपडेट करें",
    "Remove saved card": "सहेजा कार्ड हटाएँ",
    "Save card securely": "कार्ड सुरक्षित रूप से सहेजें",
    "Health": "स्वास्थ्य",
    "Healthcare": "स्वास्थ्य सेवा",
    "Education": "शिक्षा",
    "Essentials": "ज़रूरी चीज़ें",
    "Home": "होम",
    "Specialized Care": "विशेष देखभाल",
    "Auto top-off on": "ऑटो टॉप-ऑफ चालू",
    "Auto top-off paused": "ऑटो टॉप-ऑफ रुका हुआ",
    "Auto top-off off": "ऑटो टॉप-ऑफ बंद",
    "Auto top-off not set": "ऑटो टॉप-ऑफ सेट नहीं है",
    "Adds": "जोड़ता है",
    "when this category drops below": "जब यह श्रेणी इससे नीचे जाती है",
    "Let Banxito remind or automatically increase this category allowance later when its remaining allowance gets low.": "जब इस श्रेणी का बैलेंस कम हो, Banxito आपको याद दिला सकता है या अपने आप फिर से भर सकता है.",
    "Save a card first": "पहले कार्ड सहेजें",
    "Automatic top-off needs a saved payment method. Banxito stores only safe card metadata, not the full card number.": "ऑटो टॉप-ऑफ के लिए सहेजी गई भुगतान विधि चाहिए। Banxito केवल सुरक्षित कार्ड जानकारी रखता है, पूरा कार्ड नंबर नहीं।",
    "Add saved card under Me": "Me में सहेजा कार्ड जोड़ें",
    "Uses": "उपयोग करता है",
    "Banxito stores only safe card metadata.": "Banxito केवल सुरक्षित कार्ड जानकारी रखता है.",
    "Hide settings": "सेटिंग छुपाएँ",
    "Edit top-off": "टॉप-ऑफ संपादित करें",
    "Set auto top-off": "ऑटो टॉप-ऑफ सेट करें",
    "Pause": "रोकें",
    "Turn back on": "फिर चालू करें",
    "When": "जब",
    "drops below": "इससे नीचे जाए",
    "local": "स्थानीय",
    "Add this much (USD)": "इतना जोड़ें (USD)",
    "Monthly safety limit (USD)": "मासिक सुरक्षा सीमा (USD)",
    "Max allowance increases per month": "हर महीने अधिकतम टॉप-ऑफ",
    "Wait at least this many days between allowance increases": "टॉप-ऑफ के बीच कम से कम इतने दिन प्रतीक्षा करें",
    "Saving top-off...": "टॉप-ऑफ सहेजा जा रहा है...",
    "Enable automatic top-off": "ऑटो टॉप-ऑफ चालू करें",
    "Add a saved card under Me before turning this on.": "इसे चालू करने से पहले Me में सहेजा कार्ड जोड़ें.",
    "The monthly safety limit must be at least as much as one top-off.": "मासिक सुरक्षा सीमा कम से कम एक टॉप-ऑफ जितनी होनी चाहिए.",
    "Banxito checks this category automatically and uses your saved card only when the remaining allowance drops below your limit.": "Banxito इस श्रेणी को अपने आप जांचता है और बैलेंस आपकी सीमा से नीचे आने पर ही सहेजे कार्ड का उपयोग करता है."
  },
  vi: {
    "Language": "Ngôn ngữ",
    "Banxito Sponsor": "Người hỗ trợ Banxito",
    "Sponsor home": "Trang chủ người hỗ trợ",
    "Support overview": "Tổng quan hỗ trợ",
    "See every recipient, service allowance, and fulfillment event in one place before you purchase the next care category.": "Xem mọi người nhận, ví và khoản thanh toán ở một nơi trước khi hỗ trợ hạng mục tiếp theo.",
    "Recipients": "Người nhận",
    "Purchased allowances": "Tổng đã hỗ trợ",
    "Pending purchases": "Thanh toán đang chờ",
    "Active allowances": "Ví đang hoạt động",
    "Purchase allowance": "Hỗ trợ người nhận",
    "Add recipient": "Thêm người nhận",
    "Linked family members": "Người thân đã liên kết",
    "Recipient snapshot": "Tóm tắt người nhận",
    "Current allowances": "Quỹ hiện tại",
    "Past allowances": "Quỹ đã dùng",
    "Add allowance": "Thêm tiền",
    "Purchase new allowance": "Tạo khoản hỗ trợ mới",
    "Reassign current allowance": "Phân bổ lại quỹ hiện tại",
    "Add allowance to the active fund": "Thêm tiền vào quỹ đang hoạt động",
    "Review and complete payment": "Xem lại và hoàn tất thanh toán",
    "Allowance amount": "Số tiền hỗ trợ",
    "Banxito fee": "Phí Banxito",
    "Total charge": "Tổng phí",
    "Recipient estimate": "Ước tính cho người nhận",
    "Allowance FX snapshot": "Tỷ giá lúc hỗ trợ",
    "Complete allowance purchase": "Hoàn tất hỗ trợ",
    "Ready for payment": "Sẵn sàng thanh toán",
    "Saved payment method": "Phương thức thanh toán đã lưu",
    "No saved card yet": "Chưa có thẻ đã lưu",
    "Add card": "Thêm thẻ",
    "Update card": "Cập nhật thẻ",
    "Remove saved card": "Xóa thẻ đã lưu",
    "Save card securely": "Lưu thẻ an toàn",
    "Health": "Sức khỏe",
    "Healthcare": "Y tế",
    "Education": "Giáo dục",
    "Essentials": "Thiết yếu",
    "Home": "Trang chủ",
    "Specialized Care": "Chăm sóc đặc biệt",
    "Auto top-off on": "Tự động nạp bổ sung đang bật",
    "Auto top-off paused": "Tự động nạp bổ sung tạm dừng",
    "Auto top-off off": "Tự động nạp bổ sung đã tắt",
    "Auto top-off not set": "Chưa cài tự động nạp bổ sung",
    "Adds": "Thêm",
    "when this category drops below": "khi hạng mục này thấp hơn",
    "Let Banxito remind or automatically increase this category allowance later when its remaining allowance gets low.": "Banxito có thể nhắc hoặc tự động nạp thêm khi số dư hạng mục này thấp.",
    "Save a card first": "Lưu thẻ trước",
    "Automatic top-off needs a saved payment method. Banxito stores only safe card metadata, not the full card number.": "Tự động nạp bổ sung cần phương thức thanh toán đã lưu. Banxito chỉ lưu thông tin thẻ an toàn, không lưu toàn bộ số thẻ.",
    "Add saved card under Me": "Thêm thẻ đã lưu trong mục Tôi",
    "Uses": "Dùng",
    "Banxito stores only safe card metadata.": "Banxito chỉ lưu thông tin thẻ an toàn.",
    "Hide settings": "Ẩn cài đặt",
    "Edit top-off": "Sửa nạp bổ sung",
    "Set auto top-off": "Cài tự động nạp bổ sung",
    "Pause": "Tạm dừng",
    "Turn back on": "Bật lại",
    "When": "Khi",
    "drops below": "thấp hơn",
    "local": "địa phương",
    "Add this much (USD)": "Thêm số tiền này (USD)",
    "Monthly safety limit (USD)": "Giới hạn an toàn hàng tháng (USD)",
    "Max allowance increases per month": "Số lần nạp tối đa mỗi tháng",
    "Wait at least this many days between allowance increases": "Chờ ít nhất số ngày này giữa các lần nạp",
    "Saving top-off...": "Đang lưu nạp bổ sung...",
    "Enable automatic top-off": "Bật tự động nạp bổ sung",
    "Add a saved card under Me before turning this on.": "Thêm thẻ đã lưu trong mục Tôi trước khi bật tính năng này.",
    "The monthly safety limit must be at least as much as one top-off.": "Giới hạn an toàn hàng tháng phải ít nhất bằng một lần nạp.",
    "Banxito checks this category automatically and uses your saved card only when the remaining allowance drops below your limit.": "Banxito tự động kiểm tra hạng mục này và chỉ dùng thẻ đã lưu khi số dư thấp hơn giới hạn của bạn."
  },
  pa: {
    "Language": "ਭਾਸ਼ਾ",
    "Banxito Sponsor": "Banxito ਸਪਾਂਸਰ",
    "Sponsor home": "ਸਪਾਂਸਰ ਹੋਮ",
    "Support overview": "ਸਹਾਇਤਾ ਸੰਖੇਪ",
    "See every recipient, service allowance, and fulfillment event in one place before you purchase the next care category.": "ਅਗਲੀ ਸੇਵਾ ਸ਼੍ਰੇਣੀ ਲਈ ਫੰਡ ਕਰਨ ਤੋਂ ਪਹਿਲਾਂ ਹਰ ਪ੍ਰਾਪਤਕਰਤਾ, ਵਾਲਿਟ ਅਤੇ ਭੁਗਤਾਨ ਇੱਕ ਥਾਂ ਵੇਖੋ।",
    "Recipients": "ਪ੍ਰਾਪਤਕਰਤਾ",
    "Purchased allowances": "ਕੁੱਲ ਸਹਾਇਤਾ",
    "Pending purchases": "ਬਕਾਇਆ ਭੁਗਤਾਨ",
    "Active allowances": "ਸਰਗਰਮ ਵਾਲਿਟ",
    "Purchase allowance": "ਪ੍ਰਾਪਤਕਰਤਾ ਨੂੰ ਸਹਾਇਤਾ ਦਿਓ",
    "Add recipient": "ਪ੍ਰਾਪਤਕਰਤਾ ਸ਼ਾਮਲ ਕਰੋ",
    "Linked family members": "ਜੁੜੇ ਪਰਿਵਾਰਕ ਮੈਂਬਰ",
    "Recipient snapshot": "ਪ੍ਰਾਪਤਕਰਤਾ ਸੰਖੇਪ",
    "Current allowances": "ਮੌਜੂਦਾ ਫੰਡ",
    "Past allowances": "ਪੁਰਾਣੇ ਫੰਡ",
    "Add allowance": "ਹੋਰ ਪੈਸੇ ਜੋੜੋ",
    "Purchase new allowance": "ਨਵੀਂ ਸਹਾਇਤਾ ਸ਼ੁਰੂ ਕਰੋ",
    "Reassign current allowance": "ਮੌਜੂਦਾ ਫੰਡ ਮੁੜ ਵੰਡੋ",
    "Add allowance to the active fund": "ਸਰਗਰਮ ਫੰਡ ਵਿੱਚ ਹੋਰ ਪੈਸੇ ਜੋੜੋ",
    "Review and complete payment": "ਸਮੀਖਿਆ ਕਰੋ ਅਤੇ ਭੁਗਤਾਨ ਪੂਰਾ ਕਰੋ",
    "Allowance amount": "ਸਹਾਇਤਾ ਰਕਮ",
    "Banxito fee": "Banxito ਫੀਸ",
    "Total charge": "ਕੁੱਲ ਚਾਰਜ",
    "Recipient estimate": "ਪ੍ਰਾਪਤਕਰਤਾ ਅਨੁਮਾਨ",
    "Allowance FX snapshot": "ਫੰਡਿੰਗ ਸਮੇਂ ਦੀ ਐਕਸਚੇਂਜ ਦਰ",
    "Complete allowance purchase": "ਸਹਾਇਤਾ ਪੂਰੀ ਕਰੋ",
    "Ready for payment": "ਭੁਗਤਾਨ ਲਈ ਤਿਆਰ",
    "Saved payment method": "ਸੇਵ ਕੀਤਾ ਭੁਗਤਾਨ ਤਰੀਕਾ",
    "No saved card yet": "ਹਾਲੇ ਕੋਈ ਸੇਵ ਕਾਰਡ ਨਹੀਂ",
    "Add card": "ਕਾਰਡ ਜੋੜੋ",
    "Update card": "ਕਾਰਡ ਅਪਡੇਟ ਕਰੋ",
    "Remove saved card": "ਸੇਵ ਕਾਰਡ ਹਟਾਓ",
    "Save card securely": "ਕਾਰਡ ਸੁਰੱਖਿਅਤ ਸੇਵ ਕਰੋ",
    "Health": "ਸਿਹਤ",
    "Healthcare": "ਸਿਹਤ ਸੇਵਾ",
    "Education": "ਸਿੱਖਿਆ",
    "Essentials": "ਜ਼ਰੂਰੀ ਚੀਜ਼ਾਂ",
    "Home": "ਘਰ",
    "Specialized Care": "ਖਾਸ ਦੇਖਭਾਲ",
    "Auto top-off on": "ਆਟੋ ਟਾਪ-ਆਫ਼ ਚਾਲੂ",
    "Auto top-off paused": "ਆਟੋ ਟਾਪ-ਆਫ਼ ਰੋਕਿਆ ਹੋਇਆ",
    "Auto top-off off": "ਆਟੋ ਟਾਪ-ਆਫ਼ ਬੰਦ",
    "Auto top-off not set": "ਆਟੋ ਟਾਪ-ਆਫ਼ ਸੈੱਟ ਨਹੀਂ",
    "Adds": "ਜੋੜਦਾ ਹੈ",
    "when this category drops below": "ਜਦੋਂ ਇਹ ਸ਼੍ਰੇਣੀ ਇਸ ਤੋਂ ਘੱਟ ਹੋਵੇ",
    "Let Banxito remind or automatically increase this category allowance later when its remaining allowance gets low.": "ਜਦੋਂ ਇਸ ਸ਼੍ਰੇਣੀ ਦਾ ਬੈਲੈਂਸ ਘੱਟ ਹੋਵੇ, Banxito ਤੁਹਾਨੂੰ ਯਾਦ ਦਿਵਾ ਸਕਦਾ ਹੈ ਜਾਂ ਆਪੇ ਭਰ ਸਕਦਾ ਹੈ।",
    "Save a card first": "ਪਹਿਲਾਂ ਕਾਰਡ ਸੇਵ ਕਰੋ",
    "Automatic top-off needs a saved payment method. Banxito stores only safe card metadata, not the full card number.": "ਆਟੋ ਟਾਪ-ਆਫ਼ ਲਈ ਸੇਵ ਕੀਤਾ ਭੁਗਤਾਨ ਤਰੀਕਾ ਚਾਹੀਦਾ ਹੈ। Banxito ਸਿਰਫ਼ ਸੁਰੱਖਿਅਤ ਕਾਰਡ ਜਾਣਕਾਰੀ ਰੱਖਦਾ ਹੈ, ਪੂਰਾ ਕਾਰਡ ਨੰਬਰ ਨਹੀਂ।",
    "Add saved card under Me": "ਮੈਂ ਵਿੱਚ ਸੇਵ ਕਾਰਡ ਸ਼ਾਮਲ ਕਰੋ",
    "Uses": "ਵਰਤਦਾ ਹੈ",
    "Banxito stores only safe card metadata.": "Banxito ਸਿਰਫ਼ ਸੁਰੱਖਿਅਤ ਕਾਰਡ ਜਾਣਕਾਰੀ ਰੱਖਦਾ ਹੈ।",
    "Hide settings": "ਸੈਟਿੰਗਾਂ ਲੁਕਾਓ",
    "Edit top-off": "ਟਾਪ-ਆਫ਼ ਸੋਧੋ",
    "Set auto top-off": "ਆਟੋ ਟਾਪ-ਆਫ਼ ਸੈੱਟ ਕਰੋ",
    "Pause": "ਰੋਕੋ",
    "Turn back on": "ਮੁੜ ਚਾਲੂ ਕਰੋ",
    "When": "ਜਦੋਂ",
    "drops below": "ਇਸ ਤੋਂ ਘੱਟ ਹੋਵੇ",
    "local": "ਸਥਾਨਕ",
    "Add this much (USD)": "ਇਹ ਰਕਮ ਜੋੜੋ (USD)",
    "Monthly safety limit (USD)": "ਮਹੀਨਾਵਾਰ ਸੁਰੱਖਿਆ ਸੀਮਾ (USD)",
    "Max allowance increases per month": "ਹਰ ਮਹੀਨੇ ਵੱਧ ਤੋਂ ਵੱਧ ਟਾਪ-ਆਫ਼",
    "Wait at least this many days between allowance increases": "ਟਾਪ-ਆਫ਼ਾਂ ਵਿਚਕਾਰ ਘੱਟੋ-ਘੱਟ ਇਹਨੇ ਦਿਨ ਰੁਕੋ",
    "Saving top-off...": "ਟਾਪ-ਆਫ਼ ਸੇਵ ਹੋ ਰਿਹਾ ਹੈ...",
    "Enable automatic top-off": "ਆਟੋ ਟਾਪ-ਆਫ਼ ਚਾਲੂ ਕਰੋ",
    "Add a saved card under Me before turning this on.": "ਇਸਨੂੰ ਚਾਲੂ ਕਰਨ ਤੋਂ ਪਹਿਲਾਂ ਮੈਂ ਵਿੱਚ ਸੇਵ ਕਾਰਡ ਸ਼ਾਮਲ ਕਰੋ।",
    "The monthly safety limit must be at least as much as one top-off.": "ਮਹੀਨਾਵਾਰ ਸੁਰੱਖਿਆ ਸੀਮਾ ਘੱਟੋ-ਘੱਟ ਇੱਕ ਟਾਪ-ਆਫ਼ ਦੇ ਬਰਾਬਰ ਹੋਣੀ ਚਾਹੀਦੀ ਹੈ।",
    "Banxito checks this category automatically and uses your saved card only when the remaining allowance drops below your limit.": "Banxito ਇਸ ਸ਼੍ਰੇਣੀ ਨੂੰ ਆਪੇ ਜਾਂਚਦਾ ਹੈ ਅਤੇ ਬੈਲੈਂਸ ਤੁਹਾਡੀ ਸੀਮਾ ਤੋਂ ਘੱਟ ਹੋਣ ਤੇ ਹੀ ਸੇਵ ਕਾਰਡ ਵਰਤਦਾ ਹੈ।"
  },
  sw: {
    "Language": "Lugha",
    "Banxito Sponsor": "Mfadhili wa Banxito",
    "Sponsor home": "Nyumbani kwa mfadhili",
    "Support overview": "Muhtasari wa msaada",
    "See every recipient, service allowance, and fulfillment event in one place before you purchase the next care category.": "Tazama wapokeaji, pochi, na malipo yote mahali pamoja kabla ya kufadhili huduma inayofuata.",
    "Recipients": "Wapokeaji",
    "Purchased allowances": "Jumla iliyowekwa",
    "Pending purchases": "Malipo yanayosubiri",
    "Active allowances": "Pochi zinazoendelea",
    "Purchase allowance": "Mfadhili mpokeaji",
    "Add recipient": "Ongeza mpokeaji",
    "Linked family members": "Wanafamilia waliounganishwa",
    "Recipient snapshot": "Muhtasari wa mpokeaji",
    "Current allowances": "Fedha za sasa",
    "Past allowances": "Fedha zilizotumika",
    "Add allowance": "Ongeza pesa",
    "Purchase new allowance": "Anza msaada mpya",
    "Reassign current allowance": "Gawa upya fedha za sasa",
    "Add allowance to the active fund": "Ongeza pesa kwenye mfuko unaotumika",
    "Review and complete payment": "Kagua na ukamilishe malipo",
    "Allowance amount": "Kiasi cha msaada",
    "Banxito fee": "Ada ya Banxito",
    "Total charge": "Jumla ya malipo",
    "Recipient estimate": "Makadirio ya mpokeaji",
    "Allowance FX snapshot": "Kiwango cha ubadilishaji wakati wa msaada",
    "Complete allowance purchase": "Kamilisha msaada",
    "Ready for payment": "Tayari kwa malipo",
    "Saved payment method": "Njia ya malipo iliyohifadhiwa",
    "No saved card yet": "Bado hakuna kadi iliyohifadhiwa",
    "Add card": "Ongeza kadi",
    "Update card": "Sasisha kadi",
    "Remove saved card": "Ondoa kadi iliyohifadhiwa",
    "Save card securely": "Hifadhi kadi kwa usalama",
    "Health": "Afya",
    "Healthcare": "Huduma ya afya",
    "Education": "Elimu",
    "Essentials": "Mahitaji muhimu",
    "Home": "Mwanzo",
    "Specialized Care": "Huduma maalum",
    "Auto top-off on": "Kuongeza kiotomatiki kumewashwa",
    "Auto top-off paused": "Kuongeza kiotomatiki kumesitishwa",
    "Auto top-off off": "Kuongeza kiotomatiki kumezimwa",
    "Auto top-off not set": "Kuongeza kiotomatiki hakijawekwa",
    "Adds": "Huongeza",
    "when this category drops below": "wakati kundi hili linashuka chini ya",
    "Let Banxito remind or automatically increase this category allowance later when its remaining allowance gets low.": "Banxito inaweza kukukumbusha au kujaza kundi hili kiotomatiki salio likiwa chini.",
    "Save a card first": "Hifadhi kadi kwanza",
    "Automatic top-off needs a saved payment method. Banxito stores only safe card metadata, not the full card number.": "Kuongeza kiotomatiki kunahitaji njia ya malipo iliyohifadhiwa. Banxito huhifadhi tu taarifa salama za kadi, si nambari kamili ya kadi.",
    "Add saved card under Me": "Ongeza kadi iliyohifadhiwa kwenye Mimi",
    "Uses": "Hutumia",
    "Banxito stores only safe card metadata.": "Banxito huhifadhi tu taarifa salama za kadi.",
    "Hide settings": "Ficha mipangilio",
    "Edit top-off": "Hariri kuongeza",
    "Set auto top-off": "Weka kuongeza kiotomatiki",
    "Pause": "Sitisha",
    "Turn back on": "Washa tena",
    "When": "Wakati",
    "drops below": "linashuka chini ya",
    "local": "ya eneo",
    "Add this much (USD)": "Ongeza kiasi hiki (USD)",
    "Monthly safety limit (USD)": "Kikomo cha usalama kwa mwezi (USD)",
    "Max allowance increases per month": "Idadi ya juu ya kuongeza kwa mwezi",
    "Wait at least this many days between allowance increases": "Subiri angalau siku hizi kati ya nyongeza",
    "Saving top-off...": "Inahifadhi kuongeza...",
    "Enable automatic top-off": "Washa kuongeza kiotomatiki",
    "Add a saved card under Me before turning this on.": "Ongeza kadi iliyohifadhiwa kwenye Mimi kabla ya kuwasha hili.",
    "The monthly safety limit must be at least as much as one top-off.": "Kikomo cha mwezi lazima kiwe angalau sawa na kuongeza mara moja.",
    "Banxito checks this category automatically and uses your saved card only when the remaining allowance drops below your limit.": "Banxito hukagua kundi hili kiotomatiki na hutumia kadi yako iliyohifadhiwa tu salio likishuka chini ya kikomo chako."
  }
};

for (const [languageCode, translations] of Object.entries(sponsorFinalExperienceTranslations)) {
  sponsorTranslations[languageCode] = {
    ...sponsorTranslations[languageCode],
    ...translations
  };
}

const sponsorCareAssuranceTranslations = {
  es: {
    "Care assurance dashboard": "Panel de tranquilidad familiar",
    "See who is okay, what care was delivered, and what needs attention next.": "Ve quién está bien, qué apoyo se entregó y qué necesita atención después.",
    "Impact so far": "Impacto hasta ahora",
    "Provider visits": "Visitas con proveedores",
    "Provider-confirmed care events.": "Servicios confirmados por proveedores.",
    "Receipts verified": "Recibos verificados",
    "Proof uploaded after care.": "Comprobantes cargados después del servicio.",
    "Needs attention": "Necesita atención",
    "Balances, receipts, or payments Banxito should watch.": "Saldos, recibos o pagos que Banxito debe vigilar.",
    "Care timeline": "Cronología de cuidado",
    "No care timeline yet": "Aún no hay cronología de cuidado",
    "Once allowance purchases or provider care are recorded, Banxito will show the care story here.": "Cuando haya fondos o servicios registrados, Banxito mostrará aquí la historia de cuidado.",
    "Support added": "Apoyo agregado",
    "Support available": "Apoyo disponible",
    "Care delivered": "Cuidado entregado",
    "Receipt uploaded": "Recibo cargado",
    "Receipt pending": "Recibo pendiente",
    "Confidence signals": "Señales de confianza",
    "Approved provider": "Proveedor aprobado",
    "Receipt verified": "Recibo verificado",
    "Within sponsor rules": "Dentro de las reglas del patrocinador",
    "Care intent": "Intención del apoyo",
    "Tell Banxito what this support is meant to do.": "Dile a Banxito para qué es este apoyo.",
    "Example: Mom's monthly medicine": "Ejemplo: medicina mensual de mamá",
    "Save intent": "Guardar intención",
    "Intent saved": "Intención guardada",
    "Care note": "Nota de cariño",
    "Draft a gentle note for later. No full chat yet.": "Escribe una nota amable para después. Todavía no es un chat completo.",
    "Glad this helped. Love you.": "Me alegra que esto haya ayudado. Te quiero.",
    "Save note draft": "Guardar borrador",
    "Note saved": "Nota guardada",
    "Reassurance": "Tranquilidad",
    "Your support is ready for care.": "Tu apoyo está listo para cuidar.",
    "Review activity": "Revisar actividad",
    "Fund more care": "Financiar más cuidado",
    "Active category allowances": "Saldos activos por categoría",
    "Ready to help": "Listo para ayudar",
    "Add a care intent": "Agregar intención de apoyo",
    "Next allowance step": "Siguiente paso de financiación",
    "Add purpose while purchasing": "Agrega el propósito al financiar",
    "When you fund, Banxito will ask what the support is for and let you draft a gentle note for the recipient.": "Cuando financies, Banxito preguntará para qué es el apoyo y te permitirá redactar una nota amable para el familiar.",
    "Go to purchase": "Ir a financiar",
    "What is this support for?": "¿Para qué es este apoyo?",
    "Mom's monthly medicine": "Medicina mensual de mamá",
    "Tell Banxito the purpose, like monthly medicine or school supplies.": "Dile a Banxito el propósito, como medicina mensual o útiles escolares.",
    "Care note for recipient": "Nota de cariño para el familiar",
    "Draft a gentle note that will be presented to the recipient.": "Redacta una nota amable que se mostrará al familiar."
  },
  fr: {
    "Care assurance dashboard": "Tableau de suivi du soutien",
    "See who is okay, what care was delivered, and what needs attention next.": "Voyez qui va bien, quel soutien a été fourni et ce qui demande votre attention.",
    "Impact so far": "Impact jusqu'ici",
    "Provider visits": "Visites chez les prestataires",
    "Provider-confirmed care events.": "Services confirmés par les prestataires.",
    "Receipts verified": "Reçus vérifiés",
    "Proof uploaded after care.": "Justificatifs téléversés après le service.",
    "Needs attention": "À surveiller",
    "Balances, receipts, or payments Banxito should watch.": "Soldes, reçus ou paiements que Banxito doit surveiller.",
    "Care timeline": "Fil du soutien",
    "No care timeline yet": "Aucun fil de soutien pour l'instant",
    "Once allowance purchases or provider care are recorded, Banxito will show the care story here.": "Dès qu'un financement ou un service est enregistré, Banxito affichera l'histoire du soutien ici.",
    "Support added": "Soutien ajouté",
    "Support available": "Soutien disponible",
    "Care delivered": "Soutien fourni",
    "Receipt uploaded": "Reçu téléversé",
    "Receipt pending": "Reçu en attente",
    "Confidence signals": "Signaux de confiance",
    "Approved provider": "Prestataire approuvé",
    "Receipt verified": "Reçu vérifié",
    "Within sponsor rules": "Conforme aux règles du sponsor",
    "Care intent": "Intention du soutien",
    "Tell Banxito what this support is meant to do.": "Indiquez à Banxito l'objectif de ce soutien.",
    "Example: Mom's monthly medicine": "Exemple : médicaments mensuels de maman",
    "Save intent": "Enregistrer l'intention",
    "Intent saved": "Intention enregistrée",
    "Care note": "Message attentionné",
    "Draft a gentle note for later. No full chat yet.": "Préparez un court message pour plus tard. Pas encore de conversation complète.",
    "Glad this helped. Love you.": "Content que cela ait aidé. Je t'aime.",
    "Save note draft": "Enregistrer le brouillon",
    "Note saved": "Note enregistrée",
    "Reassurance": "Réassurance",
    "Your support is ready for care.": "Votre soutien est prêt à être utilisé.",
    "Review activity": "Voir l'activité",
    "Fund more care": "Ajouter du soutien",
    "Active category allowances": "Soldes actifs par catégorie",
    "Ready to help": "Prêt à aider",
    "Add a care intent": "Ajouter une intention",
    "Next allowance step": "Prochaine étape de financement",
    "Add purpose while purchasing": "Ajoutez l'objectif pendant le financement",
    "When you fund, Banxito will ask what the support is for and let you draft a gentle note for the recipient.": "Lorsque vous financez, Banxito vous demandera à quoi sert ce soutien et vous permettra de rédiger un message attentionné pour le bénéficiaire.",
    "Go to purchase": "Aller au financement",
    "What is this support for?": "À quoi sert ce soutien ?",
    "Mom's monthly medicine": "Médicaments mensuels de maman",
    "Tell Banxito the purpose, like monthly medicine or school supplies.": "Indiquez l'objectif à Banxito, comme les médicaments mensuels ou les fournitures scolaires.",
    "Care note for recipient": "Message attentionné pour le bénéficiaire",
    "Draft a gentle note that will be presented to the recipient.": "Rédigez un message attentionné qui sera présenté au bénéficiaire."
  },
  fil: {
    "Care assurance dashboard": "Care assurance dashboard",
    "See who is okay, what care was delivered, and what needs attention next.": "Tingnan kung sino ang okay, anong tulong ang naibigay, at ano ang kailangang bantayan.",
    "Impact so far": "Epekto sa ngayon",
    "Provider visits": "Mga provider visit",
    "Provider-confirmed care events.": "Mga serbisyong kinumpirma ng provider.",
    "Receipts verified": "Na-verify na resibo",
    "Proof uploaded after care.": "Proof na na-upload pagkatapos ng serbisyo.",
    "Needs attention": "Kailangang pansinin",
    "Balances, receipts, or payments Banxito should watch.": "Mga balance, resibo, o payment na dapat bantayan ng Banxito.",
    "Care timeline": "Timeline ng pag-aalaga",
    "No care timeline yet": "Wala pang care timeline",
    "Once allowance purchases or provider care are recorded, Banxito will show the care story here.": "Kapag may funding o provider care na naitala, ipapakita ng Banxito ang kuwento rito.",
    "Support added": "Nagdagdag ng suporta",
    "Support available": "Available ang suporta",
    "Care delivered": "Naibigay ang care",
    "Receipt uploaded": "Na-upload ang resibo",
    "Receipt pending": "Naghihintay ng resibo",
    "Confidence signals": "Mga tanda ng kumpiyansa",
    "Approved provider": "Aprubadong provider",
    "Receipt verified": "Na-verify ang resibo",
    "Within sponsor rules": "Pasok sa sponsor rules",
    "Care intent": "Layunin ng suporta",
    "Tell Banxito what this support is meant to do.": "Sabihin sa Banxito kung para saan ang suportang ito.",
    "Example: Mom's monthly medicine": "Halimbawa: buwanang gamot ni Nanay",
    "Save intent": "I-save ang layunin",
    "Intent saved": "Na-save ang layunin",
    "Care note": "Care note",
    "Draft a gentle note for later. No full chat yet.": "Gumawa ng maikling mensahe para mamaya. Wala pang full chat.",
    "Glad this helped. Love you.": "Masaya akong nakatulong ito. Mahal kita.",
    "Save note draft": "I-save ang draft",
    "Note saved": "Na-save ang note",
    "Reassurance": "Kapanatagan",
    "Your support is ready for care.": "Handa na ang suporta mo para sa care.",
    "Review activity": "Tingnan ang activity",
    "Fund more care": "Magdagdag ng suporta",
    "Active category allowances": "Active category allowances",
    "Ready to help": "Handang tumulong",
    "Add a care intent": "Magdagdag ng layunin",
    "Next allowance step": "Susunod na hakbang sa pagpopondo",
    "Add purpose while purchasing": "Ilagay ang layunin habang nagpopondo",
    "When you fund, Banxito will ask what the support is for and let you draft a gentle note for the recipient.": "Kapag nagpopondo ka, tatanungin ng Banxito kung para saan ang suporta at papayagan kang gumawa ng mahinahong mensahe para sa tatanggap.",
    "Go to purchase": "Pumunta sa pagpopondo",
    "What is this support for?": "Para saan ang suportang ito?",
    "Mom's monthly medicine": "Buwanang gamot ni Nanay",
    "Tell Banxito the purpose, like monthly medicine or school supplies.": "Sabihin sa Banxito ang layunin, tulad ng buwanang gamot o gamit sa paaralan.",
    "Care note for recipient": "Mensahe para sa tatanggap",
    "Draft a gentle note that will be presented to the recipient.": "Gumawa ng mahinahong mensahe na ipapakita sa tatanggap."
  },
  ar: {
    "Care assurance dashboard": "لوحة الاطمئنان على الرعاية",
    "See who is okay, what care was delivered, and what needs attention next.": "اعرف من حاله جيد، وما الرعاية التي تم تقديمها، وما الذي يحتاج إلى متابعة.",
    "Impact so far": "الأثر حتى الآن",
    "Provider visits": "زيارات مقدمي الخدمة",
    "Provider-confirmed care events.": "خدمات رعاية أكدها مقدم الخدمة.",
    "Receipts verified": "إيصالات تم التحقق منها",
    "Proof uploaded after care.": "تم رفع الإثبات بعد تقديم الخدمة.",
    "Needs attention": "يحتاج إلى انتباه",
    "Balances, receipts, or payments Banxito should watch.": "أرصدة أو إيصالات أو مدفوعات يجب على Banxito متابعتها.",
    "Care timeline": "سجل الرعاية",
    "No care timeline yet": "لا يوجد سجل رعاية بعد",
    "Once allowance purchases or provider care are recorded, Banxito will show the care story here.": "عند تسجيل تمويل أو خدمة من مقدم رعاية، سيعرض Banxito قصة الرعاية هنا.",
    "Support added": "تمت إضافة دعم",
    "Support available": "الدعم متاح",
    "Care delivered": "تم تقديم الرعاية",
    "Receipt uploaded": "تم رفع الإيصال",
    "Receipt pending": "الإيصال قيد الانتظار",
    "Confidence signals": "مؤشرات الثقة",
    "Approved provider": "مقدم خدمة معتمد",
    "Receipt verified": "تم التحقق من الإيصال",
    "Within sponsor rules": "ضمن قواعد الراعي",
    "Care intent": "هدف الدعم",
    "Tell Banxito what this support is meant to do.": "أخبر Banxito بالهدف من هذا الدعم.",
    "Example: Mom's monthly medicine": "مثال: دواء الأم الشهري",
    "Save intent": "حفظ الهدف",
    "Intent saved": "تم حفظ الهدف",
    "Care note": "رسالة دعم",
    "Draft a gentle note for later. No full chat yet.": "اكتب رسالة لطيفة لوقت لاحق. لا توجد محادثة كاملة الآن.",
    "Glad this helped. Love you.": "سعيد أن هذا ساعدك. أحبك.",
    "Save note draft": "حفظ مسودة الرسالة",
    "Note saved": "تم حفظ الرسالة",
    "Reassurance": "طمأنة",
    "Your support is ready for care.": "دعمك جاهز للاستخدام في الرعاية.",
    "Review activity": "مراجعة النشاط",
    "Fund more care": "إضافة دعم للرعاية",
    "Active category allowances": "أرصدة الفئات النشطة",
    "Ready to help": "جاهز للمساعدة",
    "Add a care intent": "إضافة هدف للدعم",
    "Next allowance step": "خطوة التمويل التالية",
    "Add purpose while purchasing": "أضف الغرض أثناء التمويل",
    "When you fund, Banxito will ask what the support is for and let you draft a gentle note for the recipient.": "عند التمويل، سيسألك Banxito عن الغرض من الدعم ويسمح لك بكتابة رسالة لطيفة للمستفيد.",
    "Go to purchase": "اذهب إلى التمويل",
    "What is this support for?": "ما الغرض من هذا الدعم؟",
    "Mom's monthly medicine": "دواء أمي الشهري",
    "Tell Banxito the purpose, like monthly medicine or school supplies.": "أخبر Banxito بالغرض، مثل الدواء الشهري أو المستلزمات المدرسية.",
    "Care note for recipient": "رسالة دعم للمستفيد",
    "Draft a gentle note that will be presented to the recipient.": "اكتب رسالة لطيفة ستُعرض على المستفيد."
  },
  hi: {
    "Care assurance dashboard": "देखभाल भरोसा डैशबोर्ड",
    "See who is okay, what care was delivered, and what needs attention next.": "देखें कौन ठीक है, कौन सी देखभाल मिली, और आगे किस पर ध्यान देना है.",
    "Impact so far": "अब तक का असर",
    "Provider visits": "प्रदाता विजिट",
    "Provider-confirmed care events.": "प्रदाता द्वारा पुष्टि की गई सेवाएं.",
    "Receipts verified": "रसीदें सत्यापित",
    "Proof uploaded after care.": "सेवा के बाद प्रमाण अपलोड किया गया.",
    "Needs attention": "ध्यान देने योग्य",
    "Balances, receipts, or payments Banxito should watch.": "बैलेंस, रसीदें, या भुगतान जिन पर Banxito को नज़र रखनी चाहिए.",
    "Care timeline": "देखभाल टाइमलाइन",
    "No care timeline yet": "अभी कोई देखभाल टाइमलाइन नहीं",
    "Once allowance purchases or provider care are recorded, Banxito will show the care story here.": "फंडिंग या प्रदाता सेवा दर्ज होने पर Banxito यहां देखभाल की कहानी दिखाएगा.",
    "Support added": "सहायता जोड़ी गई",
    "Support available": "सहायता उपलब्ध",
    "Care delivered": "देखभाल मिली",
    "Receipt uploaded": "रसीद अपलोड हुई",
    "Receipt pending": "रसीद लंबित",
    "Confidence signals": "भरोसे के संकेत",
    "Approved provider": "स्वीकृत प्रदाता",
    "Receipt verified": "रसीद सत्यापित",
    "Within sponsor rules": "स्पॉन्सर नियमों के भीतर",
    "Care intent": "सहायता का उद्देश्य",
    "Tell Banxito what this support is meant to do.": "Banxito को बताएं कि यह सहायता किस लिए है.",
    "Example: Mom's monthly medicine": "उदाहरण: मां की मासिक दवा",
    "Save intent": "उद्देश्य सेव करें",
    "Intent saved": "उद्देश्य सेव हुआ",
    "Care note": "स्नेह संदेश",
    "Draft a gentle note for later. No full chat yet.": "बाद के लिए छोटा संदेश लिखें. अभी पूरा चैट नहीं.",
    "Glad this helped. Love you.": "खुशी है इससे मदद मिली. प्यार.",
    "Save note draft": "ड्राफ्ट सेव करें",
    "Note saved": "संदेश सेव हुआ",
    "Reassurance": "आश्वासन",
    "Your support is ready for care.": "आपकी सहायता देखभाल के लिए तैयार है.",
    "Review activity": "गतिविधि देखें",
    "Fund more care": "और सहायता भेजें",
    "Active category allowances": "सक्रिय श्रेणी बैलेंस",
    "Ready to help": "मदद के लिए तैयार",
    "Add a care intent": "सहायता उद्देश्य जोड़ें",
    "Next allowance step": "अगला फंडिंग चरण",
    "Add purpose while purchasing": "फंडिंग करते समय उद्देश्य जोड़ें",
    "When you fund, Banxito will ask what the support is for and let you draft a gentle note for the recipient.": "फंडिंग करते समय Banxito पूछेगा कि सहायता किस लिए है और आपको प्राप्तकर्ता के लिए एक स्नेहपूर्ण संदेश लिखने देगा।",
    "Go to purchase": "फंडिंग पर जाएं",
    "What is this support for?": "यह सहायता किस लिए है?",
    "Mom's monthly medicine": "मां की मासिक दवा",
    "Tell Banxito the purpose, like monthly medicine or school supplies.": "Banxito को उद्देश्य बताएं, जैसे मासिक दवा या स्कूल की सामग्री।",
    "Care note for recipient": "प्राप्तकर्ता के लिए स्नेह संदेश",
    "Draft a gentle note that will be presented to the recipient.": "एक स्नेहपूर्ण संदेश लिखें जो प्राप्तकर्ता को दिखाया जाएगा।"
  },
  vi: {
    "Care assurance dashboard": "Bảng bảo đảm chăm sóc",
    "See who is okay, what care was delivered, and what needs attention next.": "Xem ai đang ổn, dịch vụ nào đã được cung cấp và điều gì cần chú ý tiếp theo.",
    "Impact so far": "Tác động đến nay",
    "Provider visits": "Lượt dùng tại nhà cung cấp",
    "Provider-confirmed care events.": "Dịch vụ được nhà cung cấp xác nhận.",
    "Receipts verified": "Biên nhận đã xác minh",
    "Proof uploaded after care.": "Bằng chứng được tải lên sau dịch vụ.",
    "Needs attention": "Cần chú ý",
    "Balances, receipts, or payments Banxito should watch.": "Số dư, biên nhận hoặc thanh toán Banxito nên theo dõi.",
    "Care timeline": "Dòng thời gian chăm sóc",
    "No care timeline yet": "Chưa có dòng thời gian chăm sóc",
    "Once allowance purchases or provider care are recorded, Banxito will show the care story here.": "Khi có khoản nạp hoặc dịch vụ được ghi nhận, Banxito sẽ hiển thị câu chuyện chăm sóc tại đây.",
    "Support added": "Đã thêm hỗ trợ",
    "Support available": "Hỗ trợ sẵn sàng",
    "Care delivered": "Đã cung cấp chăm sóc",
    "Receipt uploaded": "Đã tải biên nhận",
    "Receipt pending": "Đang chờ biên nhận",
    "Confidence signals": "Tín hiệu tin cậy",
    "Approved provider": "Nhà cung cấp đã duyệt",
    "Receipt verified": "Biên nhận đã xác minh",
    "Within sponsor rules": "Đúng quy tắc của người tài trợ",
    "Care intent": "Mục đích hỗ trợ",
    "Tell Banxito what this support is meant to do.": "Cho Banxito biết khoản hỗ trợ này dùng để làm gì.",
    "Example: Mom's monthly medicine": "Ví dụ: thuốc hằng tháng của mẹ",
    "Save intent": "Lưu mục đích",
    "Intent saved": "Đã lưu mục đích",
    "Care note": "Lời nhắn chăm sóc",
    "Draft a gentle note for later. No full chat yet.": "Soạn lời nhắn nhẹ nhàng để gửi sau. Chưa phải trò chuyện đầy đủ.",
    "Glad this helped. Love you.": "Mừng là điều này giúp được. Thương nhiều.",
    "Save note draft": "Lưu nháp",
    "Note saved": "Đã lưu lời nhắn",
    "Reassurance": "Sự yên tâm",
    "Your support is ready for care.": "Khoản hỗ trợ của bạn đã sẵn sàng.",
    "Review activity": "Xem hoạt động",
    "Fund more care": "Thêm hỗ trợ",
    "Active category allowances": "Số dư danh mục đang hoạt động",
    "Ready to help": "Sẵn sàng hỗ trợ",
    "Add a care intent": "Thêm mục đích hỗ trợ",
    "Next allowance step": "Bước nạp tiền tiếp theo",
    "Add purpose while purchasing": "Thêm mục đích khi nạp tiền",
    "When you fund, Banxito will ask what the support is for and let you draft a gentle note for the recipient.": "Khi bạn nạp tiền, Banxito sẽ hỏi khoản hỗ trợ này dùng để làm gì và cho phép bạn soạn lời nhắn nhẹ nhàng cho người nhận.",
    "Go to purchase": "Đi đến nạp tiền",
    "What is this support for?": "Khoản hỗ trợ này dùng để làm gì?",
    "Mom's monthly medicine": "Thuốc hằng tháng của mẹ",
    "Tell Banxito the purpose, like monthly medicine or school supplies.": "Cho Banxito biết mục đích, như thuốc hằng tháng hoặc đồ dùng học tập.",
    "Care note for recipient": "Lời nhắn chăm sóc cho người nhận",
    "Draft a gentle note that will be presented to the recipient.": "Soạn lời nhắn nhẹ nhàng sẽ được hiển thị cho người nhận."
  },
  pa: {
    "Care assurance dashboard": "ਦੇਖਭਾਲ ਭਰੋਸਾ ਡੈਸ਼ਬੋਰਡ",
    "See who is okay, what care was delivered, and what needs attention next.": "ਦੇਖੋ ਕੌਣ ਠੀਕ ਹੈ, ਕਿਹੜੀ ਦੇਖਭਾਲ ਮਿਲੀ, ਅਤੇ ਅੱਗੇ ਕਿਸ ਗੱਲ 'ਤੇ ਧਿਆਨ ਦੇਣਾ ਹੈ।",
    "Impact so far": "ਹੁਣ ਤੱਕ ਅਸਰ",
    "Provider visits": "ਪ੍ਰਦਾਤਾ ਮੁਲਾਕਾਤਾਂ",
    "Provider-confirmed care events.": "ਪ੍ਰਦਾਤਾ ਵੱਲੋਂ ਪੁਸ਼ਟੀ ਕੀਤੀਆਂ ਸੇਵਾਵਾਂ।",
    "Receipts verified": "ਰਸੀਦਾਂ ਜਾਂਚੀਆਂ ਗਈਆਂ",
    "Proof uploaded after care.": "ਸੇਵਾ ਤੋਂ ਬਾਅਦ ਸਬੂਤ ਅਪਲੋਡ ਕੀਤਾ ਗਿਆ।",
    "Needs attention": "ਧਿਆਨ ਦੀ ਲੋੜ",
    "Balances, receipts, or payments Banxito should watch.": "ਬੈਲੈਂਸ, ਰਸੀਦਾਂ ਜਾਂ ਭੁਗਤਾਨ ਜਿਨ੍ਹਾਂ 'ਤੇ Banxito ਨੂੰ ਨਜ਼ਰ ਰੱਖਣੀ ਚਾਹੀਦੀ ਹੈ।",
    "Care timeline": "ਦੇਖਭਾਲ ਟਾਈਮਲਾਈਨ",
    "No care timeline yet": "ਅਜੇ ਕੋਈ ਦੇਖਭਾਲ ਟਾਈਮਲਾਈਨ ਨਹੀਂ",
    "Once allowance purchases or provider care are recorded, Banxito will show the care story here.": "ਫੰਡਿੰਗ ਜਾਂ ਪ੍ਰਦਾਤਾ ਸੇਵਾ ਦਰਜ ਹੋਣ 'ਤੇ Banxito ਇੱਥੇ ਦੇਖਭਾਲ ਦੀ ਕਹਾਣੀ ਦਿਖਾਏਗਾ।",
    "Support added": "ਸਹਾਇਤਾ ਜੋੜੀ ਗਈ",
    "Support available": "ਸਹਾਇਤਾ ਉਪਲਬਧ",
    "Care delivered": "ਦੇਖਭਾਲ ਦਿੱਤੀ ਗਈ",
    "Receipt uploaded": "ਰਸੀਦ ਅਪਲੋਡ ਹੋਈ",
    "Receipt pending": "ਰਸੀਦ ਬਾਕੀ ਹੈ",
    "Confidence signals": "ਭਰੋਸੇ ਦੇ ਸੰਕੇਤ",
    "Approved provider": "ਮਨਜ਼ੂਰਸ਼ੁਦਾ ਪ੍ਰਦਾਤਾ",
    "Receipt verified": "ਰਸੀਦ ਜਾਂਚੀ ਗਈ",
    "Within sponsor rules": "ਸਪਾਂਸਰ ਨਿਯਮਾਂ ਦੇ ਅੰਦਰ",
    "Care intent": "ਸਹਾਇਤਾ ਦਾ ਮਕਸਦ",
    "Tell Banxito what this support is meant to do.": "Banxito ਨੂੰ ਦੱਸੋ ਇਹ ਸਹਾਇਤਾ ਕਿਸ ਲਈ ਹੈ।",
    "Example: Mom's monthly medicine": "ਉਦਾਹਰਨ: ਮਾਂ ਦੀ ਮਹੀਨਾਵਾਰ ਦਵਾਈ",
    "Save intent": "ਮਕਸਦ ਸੰਭਾਲੋ",
    "Intent saved": "ਮਕਸਦ ਸੰਭਾਲਿਆ ਗਿਆ",
    "Care note": "ਪਿਆਰ ਭਰੀ ਨੋਟ",
    "Draft a gentle note for later. No full chat yet.": "ਬਾਅਦ ਲਈ ਪਿਆਰ ਭਰੀ ਨੋਟ ਲਿਖੋ। ਅਜੇ ਪੂਰੀ ਚੈਟ ਨਹੀਂ।",
    "Glad this helped. Love you.": "ਖੁਸ਼ੀ ਹੈ ਕਿ ਇਸ ਨਾਲ ਮਦਦ ਹੋਈ। ਪਿਆਰ।",
    "Save note draft": "ਨੋਟ ਡਰਾਫਟ ਸੰਭਾਲੋ",
    "Note saved": "ਨੋਟ ਸੰਭਾਲੀ ਗਈ",
    "Reassurance": "ਭਰੋਸਾ",
    "Your support is ready for care.": "ਤੁਹਾਡੀ ਸਹਾਇਤਾ ਦੇਖਭਾਲ ਲਈ ਤਿਆਰ ਹੈ।",
    "Review activity": "ਸਰਗਰਮੀ ਵੇਖੋ",
    "Fund more care": "ਹੋਰ ਸਹਾਇਤਾ ਭੇਜੋ",
    "Active category allowances": "ਸਰਗਰਮ ਸ਼੍ਰੇਣੀ ਬੈਲੈਂਸ",
    "Ready to help": "ਮਦਦ ਲਈ ਤਿਆਰ",
    "Add a care intent": "ਸਹਾਇਤਾ ਮਕਸਦ ਜੋੜੋ",
    "Next allowance step": "ਅਗਲਾ ਫੰਡਿੰਗ ਕਦਮ",
    "Add purpose while purchasing": "ਫੰਡਿੰਗ ਕਰਦੇ ਸਮੇਂ ਮਕਸਦ ਜੋੜੋ",
    "When you fund, Banxito will ask what the support is for and let you draft a gentle note for the recipient.": "ਜਦੋਂ ਤੁਸੀਂ ਫੰਡ ਕਰੋਗੇ, Banxito ਪੁੱਛੇਗਾ ਕਿ ਇਹ ਸਹਾਇਤਾ ਕਿਸ ਲਈ ਹੈ ਅਤੇ ਤੁਹਾਨੂੰ ਪ੍ਰਾਪਤਕਰਤਾ ਲਈ ਪਿਆਰ ਭਰੀ ਨੋਟ ਲਿਖਣ ਦੇਵੇਗਾ।",
    "Go to purchase": "ਫੰਡਿੰਗ 'ਤੇ ਜਾਓ",
    "What is this support for?": "ਇਹ ਸਹਾਇਤਾ ਕਿਸ ਲਈ ਹੈ?",
    "Mom's monthly medicine": "ਮਾਂ ਦੀ ਮਹੀਨਾਵਾਰ ਦਵਾਈ",
    "Tell Banxito the purpose, like monthly medicine or school supplies.": "Banxito ਨੂੰ ਮਕਸਦ ਦੱਸੋ, ਜਿਵੇਂ ਮਹੀਨਾਵਾਰ ਦਵਾਈ ਜਾਂ ਸਕੂਲ ਸਮਾਨ।",
    "Care note for recipient": "ਪ੍ਰਾਪਤਕਰਤਾ ਲਈ ਪਿਆਰ ਭਰੀ ਨੋਟ",
    "Draft a gentle note that will be presented to the recipient.": "ਇੱਕ ਪਿਆਰ ਭਰੀ ਨੋਟ ਲਿਖੋ ਜੋ ਪ੍ਰਾਪਤਕਰਤਾ ਨੂੰ ਦਿਖਾਈ ਜਾਵੇਗੀ।"
  },
  sw: {
    "Care assurance dashboard": "Dashibodi ya uhakika wa huduma",
    "See who is okay, what care was delivered, and what needs attention next.": "Ona nani yuko sawa, huduma gani imetolewa, na nini kinahitaji uangalizi.",
    "Impact so far": "Matokeo hadi sasa",
    "Provider visits": "Ziara kwa watoa huduma",
    "Provider-confirmed care events.": "Huduma zilizothibitishwa na mtoa huduma.",
    "Receipts verified": "Risiti zimethibitishwa",
    "Proof uploaded after care.": "Uthibitisho ulipakiwa baada ya huduma.",
    "Needs attention": "Inahitaji uangalizi",
    "Balances, receipts, or payments Banxito should watch.": "Salio, risiti, au malipo ambayo Banxito inapaswa kufuatilia.",
    "Care timeline": "Mtiririko wa huduma",
    "No care timeline yet": "Bado hakuna mtiririko wa huduma",
    "Once allowance purchases or provider care are recorded, Banxito will show the care story here.": "Ufadhili au huduma ikirekodiwa, Banxito itaonyesha hadithi ya huduma hapa.",
    "Support added": "Msaada umeongezwa",
    "Support available": "Msaada upo tayari",
    "Care delivered": "Huduma imetolewa",
    "Receipt uploaded": "Risiti imepakiwa",
    "Receipt pending": "Risiti inasubiri",
    "Confidence signals": "Ishara za kuaminika",
    "Approved provider": "Mtoa huduma aliyeidhinishwa",
    "Receipt verified": "Risiti imethibitishwa",
    "Within sponsor rules": "Ndani ya sheria za mfadhili",
    "Care intent": "Lengo la msaada",
    "Tell Banxito what this support is meant to do.": "Mwambie Banxito msaada huu unakusudiwa kufanya nini.",
    "Example: Mom's monthly medicine": "Mfano: dawa za mama za kila mwezi",
    "Save intent": "Hifadhi lengo",
    "Intent saved": "Lengo limehifadhiwa",
    "Care note": "Ujumbe wa upendo",
    "Draft a gentle note for later. No full chat yet.": "Andika ujumbe mfupi wa upendo kwa baadaye. Si gumzo kamili bado.",
    "Glad this helped. Love you.": "Nafurahi hii imesaidia. Nakupenda.",
    "Save note draft": "Hifadhi rasimu",
    "Note saved": "Ujumbe umehifadhiwa",
    "Reassurance": "Utulivu",
    "Your support is ready for care.": "Msaada wako uko tayari kwa huduma.",
    "Review activity": "Angalia shughuli",
    "Fund more care": "Ongeza msaada",
    "Active category allowances": "Salio hai kwa kategoria",
    "Ready to help": "Tayari kusaidia",
    "Add a care intent": "Ongeza lengo la msaada",
    "Next allowance step": "Hatua inayofuata ya ufadhili",
    "Add purpose while purchasing": "Ongeza kusudi wakati wa kufadhili",
    "When you fund, Banxito will ask what the support is for and let you draft a gentle note for the recipient.": "Unapofadhili, Banxito itauliza msaada huu ni wa nini na kukuruhusu kuandika ujumbe mpole kwa mpokeaji.",
    "Go to purchase": "Nenda kwenye ufadhili",
    "What is this support for?": "Msaada huu ni wa nini?",
    "Mom's monthly medicine": "Dawa za mama za kila mwezi",
    "Tell Banxito the purpose, like monthly medicine or school supplies.": "Mwambie Banxito kusudi, kama dawa za kila mwezi au vifaa vya shule.",
    "Care note for recipient": "Ujumbe wa upendo kwa mpokeaji",
    "Draft a gentle note that will be presented to the recipient.": "Andika ujumbe mpole ambao utaonyeshwa kwa mpokeaji."
  }
};

for (const [languageCode, translations] of Object.entries(sponsorCareAssuranceTranslations)) {
  sponsorTranslations[languageCode] = {
    ...sponsorTranslations[languageCode],
    ...translations
  };
}

const sponsorFundingContextTranslations = {
  es: {
    "Optional": "Opcional",
    "Add a purpose or note": "Agregar propósito o nota",
    "Add this only if it helps. You can continue without it.": "Agrega esto solo si ayuda. Puedes continuar sin hacerlo.",
    "Hide purpose and note": "Ocultar propósito y nota",
    "Edit purpose or note": "Editar propósito o nota",
    "Add": "Agregar",
    "Purpose added": "Propósito agregado",
    "Note added": "Nota agregada"
  },
  fr: {
    "Optional": "Facultatif",
    "Add a purpose or note": "Ajouter un objectif ou un message",
    "Add this only if it helps. You can continue without it.": "Ajoutez-le seulement si cela aide. Vous pouvez continuer sans le faire.",
    "Hide purpose and note": "Masquer l'objectif et le message",
    "Edit purpose or note": "Modifier l'objectif ou le message",
    "Add": "Ajouter",
    "Purpose added": "Objectif ajouté",
    "Note added": "Message ajouté"
  },
  fil: {
    "Optional": "Opsyonal",
    "Add a purpose or note": "Magdagdag ng layunin o mensahe",
    "Add this only if it helps. You can continue without it.": "Idagdag lang ito kung makakatulong. Maaari kang magpatuloy kahit wala ito.",
    "Hide purpose and note": "Itago ang layunin at mensahe",
    "Edit purpose or note": "I-edit ang layunin o mensahe",
    "Add": "Idagdag",
    "Purpose added": "Nagdagdag ng layunin",
    "Note added": "Nagdagdag ng mensahe"
  },
  ar: {
    "Optional": "اختياري",
    "Add a purpose or note": "أضف غرضًا أو رسالة",
    "Add this only if it helps. You can continue without it.": "أضف هذا فقط إذا كان مفيدًا. يمكنك المتابعة بدونه.",
    "Hide purpose and note": "إخفاء الغرض والرسالة",
    "Edit purpose or note": "تعديل الغرض أو الرسالة",
    "Add": "إضافة",
    "Purpose added": "تمت إضافة الغرض",
    "Note added": "تمت إضافة الرسالة"
  },
  hi: {
    "Optional": "वैकल्पिक",
    "Add a purpose or note": "उद्देश्य या संदेश जोड़ें",
    "Add this only if it helps. You can continue without it.": "इसे तभी जोड़ें जब यह मददगार हो। आप इसके बिना भी आगे बढ़ सकते हैं।",
    "Hide purpose and note": "उद्देश्य और संदेश छिपाएँ",
    "Edit purpose or note": "उद्देश्य या संदेश संपादित करें",
    "Add": "जोड़ें",
    "Purpose added": "उद्देश्य जोड़ा गया",
    "Note added": "संदेश जोड़ा गया"
  },
  vi: {
    "Optional": "Tùy chọn",
    "Add a purpose or note": "Thêm mục đích hoặc lời nhắn",
    "Add this only if it helps. You can continue without it.": "Chỉ thêm nếu điều này hữu ích. Bạn có thể tiếp tục mà không cần thêm.",
    "Hide purpose and note": "Ẩn mục đích và lời nhắn",
    "Edit purpose or note": "Sửa mục đích hoặc lời nhắn",
    "Add": "Thêm",
    "Purpose added": "Đã thêm mục đích",
    "Note added": "Đã thêm lời nhắn"
  },
  pa: {
    "Optional": "ਵਿਕਲਪਿਕ",
    "Add a purpose or note": "ਮਕਸਦ ਜਾਂ ਸੁਨੇਹਾ ਜੋੜੋ",
    "Add this only if it helps. You can continue without it.": "ਇਹ ਸਿਰਫ਼ ਤਾਂ ਹੀ ਜੋੜੋ ਜੇ ਇਹ ਮਦਦ ਕਰਦਾ ਹੈ। ਤੁਸੀਂ ਇਸ ਤੋਂ ਬਿਨਾਂ ਵੀ ਜਾਰੀ ਰੱਖ ਸਕਦੇ ਹੋ।",
    "Hide purpose and note": "ਮਕਸਦ ਅਤੇ ਸੁਨੇਹਾ ਲੁਕਾਓ",
    "Edit purpose or note": "ਮਕਸਦ ਜਾਂ ਸੁਨੇਹਾ ਸੋਧੋ",
    "Add": "ਜੋੜੋ",
    "Purpose added": "ਮਕਸਦ ਜੋੜਿਆ ਗਿਆ",
    "Note added": "ਸੁਨੇਹਾ ਜੋੜਿਆ ਗਿਆ"
  },
  sw: {
    "Optional": "Hiari",
    "Add a purpose or note": "Ongeza kusudi au ujumbe",
    "Add this only if it helps. You can continue without it.": "Ongeza hii tu ikiwa inasaidia. Unaweza kuendelea bila hiyo.",
    "Hide purpose and note": "Ficha kusudi na ujumbe",
    "Edit purpose or note": "Hariri kusudi au ujumbe",
    "Add": "Ongeza",
    "Purpose added": "Kusudi limeongezwa",
    "Note added": "Ujumbe umeongezwa"
  }
};

for (const [languageCode, translations] of Object.entries(sponsorFundingContextTranslations)) {
  sponsorTranslations[languageCode] = {
    ...sponsorTranslations[languageCode],
    ...translations
  };
}

const i18nReviewStatus = {
  ...stage2ReviewStatus,
  app: "sponsor"
};

const I18nContext = React.createContext({
  language: "en",
  setLanguage: () => undefined,
  t: (text) => text,
  format: (text, values) => formatTranslatedMessage(text, values)
});

function formatTranslatedMessage(text, values) {
  return text.replace(/\{(\w+)\}/g, (_, key) => String(values[key] ?? `{${key}}`));
}

function translateSponsorStatusMessage(message, t, format) {
  const categorySplitMatch = message.match(/^Updated the active category split in (.+)\.$/);
  if (categorySplitMatch) {
    return format("Updated the active category split in {currency}.", { currency: categorySplitMatch[1] });
  }

  return t(message);
}

function readLanguage() {
  const saved = window.localStorage.getItem(storageKeys.language);
  if (saved && languageOptions.some((option) => option.value === saved)) {
    return saved;
  }

  const browserLanguage = window.navigator.language.toLowerCase();
  if (browserLanguage.startsWith("es")) return "es";
  if (browserLanguage.startsWith("fr")) return "fr";
  if (browserLanguage.startsWith("fil") || browserLanguage.startsWith("tl")) return "fil";
  if (browserLanguage.startsWith("ar")) return "ar";
  if (browserLanguage.startsWith("hi")) return "hi";
  if (browserLanguage.startsWith("vi")) return "vi";
  if (browserLanguage.startsWith("pa")) return "pa";
  if (browserLanguage.startsWith("sw")) return "sw";
  return "en";
}

function useI18n() {
  return React.useContext(I18nContext);
}

const pool = new CognitoUserPool({
  UserPoolId: config.userPoolId,
  ClientId: config.userPoolClientId
});

const stripePromise = config.stripePublishableKey
  ? loadStripe(config.stripePublishableKey)
  : null;

const stripeElementAppearance = {
  theme: "night",
  variables: {
    colorPrimary: "#f18d32",
    colorBackground: "#101925",
    colorText: "#f7efe4",
    borderRadius: "16px"
  }
};

const fallbackCategoryOptions = [
  { code: "health", label: "Healthcare" },
  { code: "education", label: "Education" },
  { code: "essentials", label: "Essentials" },
  { code: "home", label: "Home" },
  { code: "specialized_care", label: "Specialized Care" }
];

const relationshipOptions = [
  "Brother",
  "Child",
  "Cousin",
  "Father",
  "Friend",
  "Grandparent",
  "Mother",
  "Sister",
  "Spouse"
];

const fallbackRecipientCountryOptions = [
  { code: "US", label: "United States" },
  { code: "BD", label: "Bangladesh" },
  { code: "DO", label: "Dominican Republic" },
  { code: "GH", label: "Ghana" },
  { code: "GT", label: "Guatemala" },
  { code: "IN", label: "India" },
  { code: "KE", label: "Kenya" },
  { code: "MX", label: "Mexico" },
  { code: "NG", label: "Nigeria" },
  { code: "PK", label: "Pakistan" },
  { code: "PH", label: "Philippines" }
];

const localeOptionsByCountry = {
  US: [{ value: "en-US", label: "English" }],
  BD: [
    { value: "bn-BD", label: "Bengali" },
    { value: "en-BD", label: "English" }
  ],
  DO: [{ value: "es-DO", label: "Spanish" }],
  GH: [{ value: "en-GH", label: "English" }],
  GT: [{ value: "es-GT", label: "Spanish" }],
  IN: [
    { value: "en-IN", label: "English" },
    { value: "hi-IN", label: "Hindi" }
  ],
  KE: [
    { value: "en-KE", label: "English" },
    { value: "sw-KE", label: "Swahili" }
  ],
  MX: [
    { value: "es-MX", label: "Spanish" },
    { value: "en-MX", label: "English" }
  ],
  NG: [{ value: "en-NG", label: "English" }],
  PK: [
    { value: "en-PK", label: "English" },
    { value: "ur-PK", label: "Urdu" }
  ],
  PH: [
    { value: "en-PH", label: "English" },
    { value: "fil-PH", label: "Filipino" }
  ]
};

const recipientCurrencyByCountry = {
  US: "USD",
  BD: "BDT",
  DO: "DOP",
  GH: "GHS",
  GT: "GTQ",
  IN: "INR",
  KE: "KES",
  MX: "MXN",
  NG: "NGN",
  PK: "PKR",
  PH: "PHP"
};

const localeCodeByLanguage = {
  arabic: "ar",
  bengali: "bn",
  english: "en",
  filipino: "fil",
  french: "fr",
  hindi: "hi",
  punjabi: "pa",
  spanish: "es",
  swahili: "sw",
  tagalog: "fil",
  urdu: "ur",
  vietnamese: "vi"
};

const corridorEstimateConfig = {
  MX: {
    recipientCurrency: "MXN",
    sponsorCurrency: "USD",
    approximateFxRate: 17.3025,
    label: "Mexico corridor"
  }
};

const pricingBands = [
  { maxUsd: 99.99, feeRate: 0.09, label: "Up to $99" },
  { maxUsd: 249.99, feeRate: 0.08, label: "$100-$249" },
  { maxUsd: 499.99, feeRate: 0.07, label: "$250-$499" },
  { maxUsd: Number.POSITIVE_INFINITY, feeRate: 0.06, label: "$500+" }
];

function splitCorridorLanguages(languages) {
  return String(languages ?? "")
    .split(",")
    .map((language) => language.trim())
    .filter(Boolean);
}

function localeValueForLanguage(language, countryCode) {
  const normalizedLanguage = String(language ?? "").trim().toLowerCase();
  const languageCode = localeCodeByLanguage[normalizedLanguage] ?? normalizedLanguage.slice(0, 2);
  const normalizedCountry = String(countryCode ?? "US").trim().toUpperCase();
  return `${languageCode || "en"}-${normalizedCountry}`;
}

function buildLocaleOptionsForCorridor(corridor) {
  const languages = splitCorridorLanguages(corridor?.languages);
  if (languages.length > 0) {
    return languages.map((language) => ({
      value: localeValueForLanguage(language, corridor.countryCode ?? corridor.code),
      label: language
    }));
  }

  return localeOptionsByCountry[corridor?.code] ?? localeOptionsByCountry[corridor?.countryCode] ?? [];
}

function buildRecipientCountryOptions(corridors) {
  if (!corridors.length) {
    return fallbackRecipientCountryOptions;
  }

  return corridors.map((corridor) => ({
    code: corridor.code,
    label: `${corridor.name} (${corridor.code})`
  }));
}

function getDefaultRecipientCountryCode(corridors) {
  const options = buildRecipientCountryOptions(corridors);
  return options.find((country) => country.code === "US")?.code ?? options[0]?.code ?? "US";
}

function getDefaultLocaleForCountry(countryCode, corridors = []) {
  const corridor = corridors.find((item) => item.code === countryCode);
  return buildLocaleOptionsForCorridor(corridor)[0]?.value ?? localeOptionsByCountry[countryCode]?.[0]?.value ?? "en-US";
}

function getLocaleOptionsForCountry(countryCode, corridors = []) {
  const corridor = corridors.find((item) => item.code === countryCode);
  const corridorOptions = buildLocaleOptionsForCorridor(corridor);
  return corridorOptions.length ? corridorOptions : localeOptionsByCountry[countryCode] ?? localeOptionsByCountry.US;
}

function getCountryLabel(countryCode, corridors = []) {
  return (
    corridors.find((country) => country.code === countryCode)?.name ??
    fallbackRecipientCountryOptions.find((country) => country.code === countryCode)?.label ??
    countryCode
  );
}

function getRecipientCurrency(countryCode, corridors = []) {
  return corridors.find((country) => country.code === countryCode)?.currency ?? recipientCurrencyByCountry[countryCode] ?? "USD";
}

function getRecipientLanguageLabel(locale, countryCode, corridors = []) {
  const normalizedCountryCode = String(countryCode ?? "").trim().toUpperCase();
  const normalizedLocale = String(locale ?? "").trim();
  if (normalizedCountryCode === "US") {
    return "English";
  }

  const option = getLocaleOptionsForCountry(normalizedCountryCode, corridors).find(
    (localeOption) => localeOption.value === normalizedLocale
  );
  return option?.label ?? normalizedLocale;
}

function getCategoryOptionsForCountry(countryCode, categoriesByCorridor = {}) {
  if (!Object.prototype.hasOwnProperty.call(categoriesByCorridor, countryCode)) {
    return fallbackCategoryOptions;
  }

  const corridorCategories = categoriesByCorridor[countryCode] ?? [];
  return corridorCategories.map((category) => ({
    code: category.categoryCode,
    label: category.displayName || category.categoryName || normalizeLabel(category.categoryCode)
  }));
}

function getPricingBand(amountUsd) {
  return (
    pricingBands.find((band) => amountUsd <= band.maxUsd) ??
    pricingBands[pricingBands.length - 1]
  );
}

function calculateFundingQuote({ amountUsd, recipientCountryCode }) {
  const normalizedAmountUsd = Number(amountUsd ?? 0);
  const fundingAmountMinor = Number.isFinite(normalizedAmountUsd)
    ? Math.max(0, Math.round(normalizedAmountUsd * 100))
    : 0;
  const pricingBand = getPricingBand(normalizedAmountUsd);
  const sponsorFeeMinor = Math.round(fundingAmountMinor * pricingBand.feeRate);
  const totalChargeMinor = fundingAmountMinor + sponsorFeeMinor;
  const corridor = corridorEstimateConfig[recipientCountryCode];
  const recipientCurrency = getRecipientCurrency(recipientCountryCode);
  const isSameCurrencyFunding = recipientCurrency === "USD";
  const recipientEstimatedAmountMinor = corridor
    ? Math.round((fundingAmountMinor / 100) * corridor.approximateFxRate * 100)
    : isSameCurrencyFunding
      ? fundingAmountMinor
      : null;

  return {
    amountUsd: normalizedAmountUsd,
    fundingAmountMinor,
    sponsorFeeMinor,
    totalChargeMinor,
    feeRate: pricingBand.feeRate,
    pricingBandLabel: pricingBand.label,
    recipientCurrency,
    recipientEstimatedAmountMinor,
    recipientEstimatedCurrency: corridor?.recipientCurrency ?? recipientCurrency,
    fxRateLocked: corridor ? corridor.approximateFxRate.toFixed(4) : isSameCurrencyFunding ? "1.0000" : null,
    corridorLabel: corridor?.label ?? null
  };
}

function splitFullName(fullName) {
  const parts = String(fullName ?? "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  return {
    firstName: parts[0] ?? "",
    lastName: parts.slice(1).join(" ")
  };
}

function joinFullName(firstName, lastName) {
  return `${firstName ?? ""} ${lastName ?? ""}`.trim();
}

function decodeJwtClaims(token) {
  try {
    const [, payload] = token.split(".");
    if (!payload) {
      return {};
    }

    const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "=");
    return JSON.parse(window.atob(padded));
  } catch {
    return {};
  }
}

function readSession() {
  const idToken = window.localStorage.getItem(storageKeys.idToken);
  const email = window.localStorage.getItem(storageKeys.email);
  const fullName = window.localStorage.getItem(storageKeys.fullName);
  if (!idToken) {
    return null;
  }

  const claims = decodeJwtClaims(idToken);
  const expiresAt = Number(claims.exp ?? 0);
  if (expiresAt && expiresAt * 1000 <= Date.now()) {
    return null;
  }

  return { idToken, email: email ?? "", fullName: fullName ?? "" };
}

function persistSession(session) {
  window.localStorage.setItem(storageKeys.idToken, session.idToken);
  window.localStorage.setItem(storageKeys.email, session.email ?? "");
  window.localStorage.setItem(storageKeys.fullName, session.fullName ?? "");
}

function clearSession() {
  window.localStorage.removeItem(storageKeys.idToken);
  window.localStorage.removeItem(storageKeys.email);
  window.localStorage.removeItem(storageKeys.fullName);
}

async function apiRequest(path, { method = "GET", token, body } = {}) {
  const response = await fetch(`${config.apiBaseUrl}${path}`, {
    method,
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(body ? { "content-type": "application/json" } : {})
    },
    body: body ? JSON.stringify(body) : undefined
  });

  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(payload.error ?? `Request failed with ${response.status}`);
    error.status = response.status;
    throw error;
  }
  return payload;
}

function signIn(email, password) {
  return new Promise((resolve, reject) => {
    const user = new CognitoUser({
      Username: email,
      Pool: pool
    });

    const authDetails = new AuthenticationDetails({
      Username: email,
      Password: password
    });

    user.authenticateUser(authDetails, {
      onSuccess: (result) => {
        const idToken = result.getIdToken().getJwtToken();
        const claims = decodeJwtClaims(idToken);
        resolve({
          idToken,
          email: claims.email ?? email,
          fullName: claims.name ?? claims["custom:full_name"] ?? ""
        });
      },
      onFailure: reject,
      newPasswordRequired: () =>
        reject(new Error("Temporary-password challenge not supported in the sponsor app yet."))
    });
  });
}

function signUp({ email, password, firstName, lastName }) {
  return new Promise((resolve, reject) => {
    const fullName = joinFullName(firstName, lastName);
    const attributes = [
      new CognitoUserAttribute({ Name: "email", Value: email }),
      new CognitoUserAttribute({ Name: "name", Value: fullName })
    ];

    pool.signUp(email, password, attributes, [], (error, result) => {
      if (error) {
        reject(error);
        return;
      }

      resolve(result?.user.getUsername() ?? email);
    });
  });
}

function confirmSignUp(email, code) {
  return new Promise((resolve, reject) => {
    const user = new CognitoUser({
      Username: email,
      Pool: pool
    });

    user.confirmRegistration(code, true, (error, result) => {
      if (error) {
        reject(error);
        return;
      }
      resolve(result);
    });
  });
}

function requestPasswordReset(email) {
  const normalizedEmail = String(email ?? "").trim().toLowerCase();

  return new Promise((resolve, reject) => {
    const user = new CognitoUser({
      Username: normalizedEmail,
      Pool: pool
    });

    let settled = false;
    const finalize = (payload) => {
      if (settled) {
        return;
      }
      settled = true;
      resolve(payload);
    };

    user.forgotPassword({
      onSuccess: () =>
        finalize({
          deliveryMedium: "email",
          destination: normalizedEmail
        }),
      onFailure: reject,
      inputVerificationCode: (data) =>
        finalize({
          deliveryMedium:
            data?.CodeDeliveryDetails?.DeliveryMedium?.toLowerCase?.() ??
            data?.DeliveryMedium?.toLowerCase?.() ??
            "email",
          destination:
            data?.CodeDeliveryDetails?.Destination ??
            data?.Destination ??
            normalizedEmail
        })
    });
  });
}

function confirmPasswordReset(email, code, password) {
  const normalizedEmail = String(email ?? "").trim().toLowerCase();

  return new Promise((resolve, reject) => {
    const user = new CognitoUser({
      Username: normalizedEmail,
      Pool: pool
    });

    user.confirmPassword(code, password, {
      onSuccess: () => resolve(),
      onFailure: reject
    });
  });
}

function changeCurrentPassword(currentPassword, nextPassword) {
  return new Promise((resolve, reject) => {
    const currentUser = pool.getCurrentUser();
    if (!currentUser) {
      reject(new Error("You need to sign in again before changing your password."));
      return;
    }

    currentUser.getSession((sessionError) => {
      if (sessionError) {
        reject(sessionError);
        return;
      }

      currentUser.changePassword(currentPassword, nextPassword, (error, result) => {
        if (error) {
          reject(error);
          return;
        }
        resolve(result);
      });
    });
  });
}

function buildSessionFromToken(idToken, fallbackEmail = "", fallbackFullName = "") {
  const claims = decodeJwtClaims(idToken);
  return {
    idToken,
    email: claims.email ?? fallbackEmail,
    fullName: claims.name ?? claims["custom:full_name"] ?? fallbackFullName
  };
}

function restoreCognitoSession() {
  return new Promise((resolve) => {
    const currentUser = pool.getCurrentUser();
    if (!currentUser) {
      resolve(null);
      return;
    }

    currentUser.getSession((error, sessionResult) => {
      if (error || !sessionResult) {
        currentUser.signOut();
        resolve(null);
        return;
      }

      const idToken = sessionResult.getIdToken().getJwtToken();
      const cachedEmail = window.localStorage.getItem(storageKeys.email) ?? currentUser.getUsername();
      const cachedFullName = window.localStorage.getItem(storageKeys.fullName) ?? "";

      resolve(buildSessionFromToken(idToken, cachedEmail, cachedFullName));
    });
  });
}

function formatMoney(amountMinor, currency = "USD") {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency
  }).format(Number(amountMinor ?? 0) / 100);
}

function formatPaymentMethodLabel(paymentMethod) {
  if (!paymentMethod) {
    return "No saved card";
  }

  const brand = paymentMethod.brand ? `${paymentMethod.brand}` : "Card";
  const last4 = paymentMethod.last4 ? ` ending in ${paymentMethod.last4}` : "";
  return `${brand}${last4}`;
}

function parseUsdToMinor(value) {
  const normalized = String(value ?? "").replace(/[^0-9.]/g, "");
  if (!normalized) {
    return 0;
  }

  const numeric = Number(normalized);
  if (!Number.isFinite(numeric) || numeric <= 0) {
    return 0;
  }

  return Math.round(numeric * 100);
}

function formatMinorAsUsdInput(amountMinor) {
  const numeric = Number(amountMinor ?? 0);
  if (!Number.isFinite(numeric) || numeric <= 0) {
    return "";
  }

  return (numeric / 100).toFixed(2);
}

function buildDefaultAllocationDraft(categoryCode = "health") {
  return [{ categoryCode, amountUsd: "" }];
}

function buildEmptyPaymentState() {
  return {
    fundingTransactionId: "",
    paymentIntentId: "",
    clientSecret: "",
    fundingAmountMinor: 0,
    sponsorFeeMinor: 0,
    totalChargeMinor: 0,
    recipientEstimatedAmountMinor: null,
    recipientEstimatedCurrency: "",
    fxRateLocked: "",
    recipientId: ""
  };
}

function distributeMinorByWeights(items, totalMinor) {
  const safeTotalMinor = Math.max(Number(totalMinor ?? 0), 0);
  if (!items.length || safeTotalMinor <= 0) {
    return items.map(() => 0);
  }

  const weights = items.map((item) => Math.max(Number(item.weight ?? 0), 0));
  const totalWeight = weights.reduce((sum, weight) => sum + weight, 0);

  if (totalWeight <= 0) {
    const evenBase = Math.floor(safeTotalMinor / items.length);
    let remainder = safeTotalMinor - evenBase * items.length;
    return items.map((_, index) => {
      const extra = remainder > 0 ? 1 : 0;
      remainder -= extra;
      return evenBase + extra;
    });
  }

  const raw = weights.map((weight) => (safeTotalMinor * weight) / totalWeight);
  const roundedDown = raw.map((value) => Math.floor(value));
  let remainder = safeTotalMinor - roundedDown.reduce((sum, value) => sum + value, 0);

  const ranked = raw
    .map((value, index) => ({
      index,
      fraction: value - Math.floor(value)
    }))
    .sort((left, right) => right.fraction - left.fraction);

  for (const item of ranked) {
    if (remainder <= 0) {
      break;
    }
    roundedDown[item.index] += 1;
    remainder -= 1;
  }

  return roundedDown;
}

function buildAllocationDraftFromAllowances(wallets, totalMinor) {
  const sourceAllowances = (wallets ?? []).filter((wallet) => Number(wallet.balanceMinor ?? 0) > 0);
  if (!sourceAllowances.length) {
    return buildDefaultAllocationDraft();
  }

  if (Number(totalMinor ?? 0) <= 0) {
    return sourceAllowances.map((wallet) => ({
      categoryCode: wallet.categoryCode,
      amountUsd: ""
    }));
  }

  const allocations = distributeMinorByWeights(
    sourceAllowances.map((wallet) => ({
      weight: wallet.balanceMinor
    })),
    totalMinor
  );

  return sourceAllowances.map((wallet, index) => ({
    categoryCode: wallet.categoryCode,
    amountUsd: formatMinorAsUsdInput(allocations[index])
  }));
}

function buildReallocationDraftFromAllowances(wallets) {
  return (wallets ?? [])
    .filter((wallet) => Number(wallet.balanceMinor ?? 0) > 0)
    .map((wallet) => ({
      categoryCode: wallet.categoryCode,
      amountLocal: (Number(wallet.balanceMinor ?? 0) / 100).toFixed(2)
    }));
}

function normalizeRecipientGroupKey(recipient) {
  const fullName = String(recipient?.fullName ?? "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();
  const countryCode = String(recipient?.countryCode ?? "").trim().toUpperCase();
  const relationshipLabel = String(recipient?.relationshipLabel ?? "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();

  return `${fullName}::${countryCode}::${relationshipLabel}`;
}

function getTransactionTimestamp(transaction) {
  return new Date(transaction?.fundedAt ?? transaction?.createdAt ?? 0).getTime();
}

function buildRecipientGroups(recipients, fundingServiceActivity) {
  const groups = new Map();

  for (const recipient of recipients ?? []) {
    const key = normalizeRecipientGroupKey(recipient);
    const existing = groups.get(key) ?? [];
    existing.push(recipient);
    groups.set(key, existing);
  }

  return Array.from(groups.values())
    .map((groupRecipients) => {
      const recipientIds = groupRecipients.map((recipient) => recipient.recipientId);
      const fundingHistory = (fundingServiceActivity ?? [])
        .filter((transaction) => recipientIds.includes(transaction.recipientId))
        .sort((left, right) => getTransactionTimestamp(right) - getTransactionTimestamp(left));

      const recipientScores = groupRecipients.map((recipient) => {
        const walletBalanceMinor = (recipient.wallets ?? []).reduce(
          (sum, wallet) => sum + Number(wallet.balanceMinor ?? 0),
          0
        );
        const successfulServiceActivity = fundingHistory.filter(
          (transaction) => transaction.recipientId === recipient.recipientId && transaction.status === "succeeded"
        );
        const latestSuccessfulFundingAt =
          successfulServiceActivity.length > 0 ? getTransactionTimestamp(successfulServiceActivity[0]) : 0;

        return {
          recipient,
          walletBalanceMinor,
          successfulFundingCount: successfulServiceActivity.length,
          latestSuccessfulFundingAt
        };
      });

      recipientScores.sort((left, right) => {
        if (right.walletBalanceMinor !== left.walletBalanceMinor) {
          return right.walletBalanceMinor - left.walletBalanceMinor;
        }
        if (right.successfulFundingCount !== left.successfulFundingCount) {
          return right.successfulFundingCount - left.successfulFundingCount;
        }
        if (right.latestSuccessfulFundingAt !== left.latestSuccessfulFundingAt) {
          return right.latestSuccessfulFundingAt - left.latestSuccessfulFundingAt;
        }

        return new Date(right.recipient.createdAt ?? 0).getTime() - new Date(left.recipient.createdAt ?? 0).getTime();
      });

      const primaryRecipient = recipientScores[0]?.recipient ?? groupRecipients[0];
      const walletMap = new Map();

      for (const recipient of groupRecipients) {
        for (const wallet of recipient.wallets ?? []) {
          const walletKey = wallet.categoryCode;
          const existingWallet = walletMap.get(walletKey);

          if (existingWallet) {
            existingWallet.balanceMinor += Number(wallet.balanceMinor ?? 0);
            existingWallet.reservedMinor += Number(wallet.reservedMinor ?? 0);
          } else {
            walletMap.set(walletKey, {
              ...wallet,
              balanceMinor: Number(wallet.balanceMinor ?? 0),
              reservedMinor: Number(wallet.reservedMinor ?? 0)
            });
          }
        }
      }

      const wallets = Array.from(walletMap.values());
      const latestSuccessfulFunding =
        fundingHistory.find((transaction) => transaction.status === "succeeded") ?? null;

      return {
        ...primaryRecipient,
        primaryRecipientId: primaryRecipient.recipientId,
        recipientIds,
        duplicateCount: groupRecipients.length,
        wallets,
        fundingHistory,
        latestSuccessfulFunding,
        currentBalanceMinor: wallets.reduce(
          (sum, wallet) => sum + Number(wallet.balanceMinor ?? 0),
          0
        )
      };
    })
    .sort(
      (left, right) =>
        new Date(right.createdAt ?? 0).getTime() - new Date(left.createdAt ?? 0).getTime()
    );
}

function getPasswordChecks(password) {
  return [
    { label: "At least 8 characters", ok: password.length >= 8 },
    { label: "1 uppercase letter", ok: /[A-Z]/.test(password) },
    { label: "1 lowercase letter", ok: /[a-z]/.test(password) },
    { label: "1 number", ok: /\d/.test(password) }
  ];
}

function getFriendlyStatusLabel(status) {
  const labels = {
    pending_bind: "Waiting for recipient setup",
    requires_payment_method: "Ready for payment",
    requires_confirmation: "Ready to continue",
    requires_action: "Action needed",
    processing: "Payment in progress",
    succeeded: "Purchased",
    canceled: "Canceled",
    active: "Active",
    submitted: "Submitted",
    requested: "Requested",
    authorized: "Approved",
    completed: "Completed",
    pending: "Pending"
  };

  return labels[status] ?? normalizeLabel(status);
}

function normalizeLabel(value) {
  return String(value ?? "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

function shouldHideTransactionByDefault(transaction) {
  return ["requires_payment_method", "created"].includes(transaction?.status);
}

function App() {
  const [session, setSession] = useState(() => readSession());
  const [summary, setSummary] = useState(null);
  const [activeTab, setActiveTab] = useState("home");
  const [language, setLanguage] = useState(() => readLanguage());
  const [status, setStatus] = useState({ type: "idle", message: "" });
  const [authNotice, setAuthNotice] = useState("");
  const [restoringSession, setRestoringSession] = useState(true);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [corridors, setCorridors] = useState([]);
  const [categoriesByCorridor, setCategoriesByCorridor] = useState({});
  const [recentInvite, setRecentInvite] = useState(null);
  const [recipientForm, setRecipientForm] = useState({
    firstName: "",
    lastName: "",
    relationshipLabel: "Spouse",
    countryCode: "US",
    locale: getDefaultLocaleForCountry("US")
  });
  const [fundingDraft, setFundingDraft] = useState({
    recipientId: "",
    amountUsd: "40.00",
    supportPurpose: "",
    recipientNote: ""
  });
  const [paymentState, setPaymentState] = useState(buildEmptyPaymentState);
  const [allocationDraft, setAllocationDraft] = useState([
    { categoryCode: "health", amountUsd: "" }
  ]);

  const recipients = summary?.recipients ?? [];
  const fundingServiceActivity = summary?.fundingServiceActivity ?? [];
  const recipientCountryOptions = useMemo(() => buildRecipientCountryOptions(corridors), [corridors]);
  const defaultRecipientCountryCode = useMemo(
    () => getDefaultRecipientCountryCode(corridors),
    [corridors]
  );
  const i18nValue = useMemo(
    () => ({
      language,
      setLanguage,
      t: (text) => sponsorTranslations[language]?.[text] ?? text,
      format: (text, values) =>
        formatTranslatedMessage(sponsorTranslations[language]?.[text] ?? text, values)
    }),
    [language]
  );
  const { t, format } = i18nValue;

  const totalPurchasedMinor = useMemo(
    () =>
      fundingServiceActivity
        .filter((item) => item.status === "succeeded")
        .reduce((sum, item) => sum + Number(item.amountMinor ?? 0), 0),
    [fundingServiceActivity]
  );

  const pendingServiceActivity = fundingServiceActivity.filter(
    (item) => item.status !== "succeeded"
  );

  function applyRecipientInviteToSummary(recipientId, invite) {
    setSummary((current) => {
      if (!current?.recipients) {
        return current;
      }

      return {
        ...current,
        recipients: current.recipients.map((recipient) =>
          recipient.recipientId === recipientId
            ? {
                ...recipient,
                invitationCode: invite.invitationCode,
                invitationExpiresAt: invite.invitationExpiresAt,
                invitationStatus: invite.invitationStatus ?? "active"
              }
            : recipient
        )
      };
    });
  }

  function isUnauthorizedError(error) {
    return error?.status === 401;
  }

  function clearSponsorSessionState() {
    clearSession();
    setSession(null);
    setSummary(null);
    setRecentInvite(null);
    setPaymentState(buildEmptyPaymentState());
    setActiveTab("home");
    setStatus({ type: "idle", message: "" });
  }

  function handleResetFundingIntent() {
    setPaymentState(buildEmptyPaymentState());
  }

  async function restorePersistentSession() {
    const restoredSession = await restoreCognitoSession();
    if (restoredSession?.idToken) {
      persistSession(restoredSession);
      setSession(restoredSession);
      setAuthNotice("");
      return restoredSession;
    }

    clearSponsorSessionState();
    return null;
  }

  async function handleUnauthorizedSession() {
    const restoredSession = await restorePersistentSession();
    if (restoredSession?.idToken) {
      return restoredSession;
    }

    setAuthNotice("Your Banxito session expired or is no longer valid. Please sign in again.");
    return null;
  }

  async function runWithSessionRetry(operation) {
    const activeSession = session?.idToken ? session : await restorePersistentSession();
    if (!activeSession?.idToken) {
      const error = new Error("Please sign in to continue.");
      error.status = 401;
      throw error;
    }

    try {
      return await operation(activeSession);
    } catch (error) {
      if (!isUnauthorizedError(error)) {
        throw error;
      }

      const refreshedSession = await handleUnauthorizedSession();
      if (!refreshedSession?.idToken || refreshedSession.idToken === activeSession.idToken) {
        const authError = new Error(
          "Your Banxito session expired or is no longer valid. Please sign in again."
        );
        authError.status = 401;
        throw authError;
      }

      return operation(refreshedSession);
    }
  }

  async function loadSummary(currentSession = session) {
    setLoadingSummary(true);
    try {
      const response = await runWithSessionRetry((activeSession) =>
        apiRequest("/sponsors/me/summary", {
          token: activeSession.idToken
        })
      );
      setSummary(response);
      setStatus((current) => (current.type === "error" ? { type: "idle", message: "" } : current));
      if (!fundingDraft.recipientId && response.recipients?.[0]?.recipientId) {
        setFundingDraft((draft) => ({
          ...draft,
          recipientId: response.recipients[0].recipientId
        }));
      }
    } catch (error) {
      if (isUnauthorizedError(error)) {
        return;
      }

      if (error.message === "Sponsor profile not found for authenticated user.") {
        setSummary(null);
        setStatus({ type: "idle", message: "" });
      } else {
        setStatus({ type: "error", message: error.message });
      }
    } finally {
      setLoadingSummary(false);
    }
  }

  async function loadCorridorConfig() {
    try {
      const response = await apiRequest("/corridors");
      const activeCorridors = response.corridors ?? [];
      setCorridors(activeCorridors);

      const categoryResponses = await Promise.all(
        activeCorridors.map(async (corridor) => {
          const categoryResponse = await apiRequest(`/corridors/${corridor.code}/categories`);
          return [corridor.code, categoryResponse.categories ?? []];
        })
      );

      setCategoriesByCorridor(Object.fromEntries(categoryResponses));
    } catch (error) {
      setCorridors([]);
      setCategoriesByCorridor({});
    }
  }

  useEffect(() => {
    let isMounted = true;

    async function bootstrapSession() {
      try {
        await restorePersistentSession();
      } finally {
        if (isMounted) {
          setRestoringSession(false);
        }
      }
    }

    void bootstrapSession();
    void loadCorridorConfig();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    window.localStorage.setItem(storageKeys.language, language);
    document.documentElement.lang = language;
    document.documentElement.dir = rtlLanguages.has(language) ? "rtl" : "ltr";
    document.documentElement.dataset.banxitoI18nBundle = i18nReviewStatus.activeBundle;
    document.documentElement.dataset.banxitoI18nReview = i18nReviewStatus.reviewLabel;
    document.documentElement.dataset.banxitoI18nProductionReady = String(
      i18nReviewStatus.productionReady
    );
  }, [language]);

  useEffect(() => {
    if (restoringSession) {
      return;
    }

    if (session?.idToken) {
      void loadSummary(session);
    }
  }, [restoringSession, session]);

  useEffect(() => {
    if (!recipientCountryOptions.length) {
      return;
    }

    setRecipientForm((current) => {
      const selectedCountryCode = recipientCountryOptions.some(
        (country) => country.code === current.countryCode
      )
        ? current.countryCode
        : defaultRecipientCountryCode;
      const localeOptions = getLocaleOptionsForCountry(selectedCountryCode, corridors);
      const selectedLocale = localeOptions.some((locale) => locale.value === current.locale)
        ? current.locale
        : getDefaultLocaleForCountry(selectedCountryCode, corridors);

      if (selectedCountryCode === current.countryCode && selectedLocale === current.locale) {
        return current;
      }

      return {
        ...current,
        countryCode: selectedCountryCode,
        locale: selectedLocale
      };
    });
  }, [corridors, defaultRecipientCountryCode, recipientCountryOptions]);

  function handleAuthenticated(nextSession) {
    persistSession(nextSession);
    setSession(nextSession);
    setAuthNotice("");
    setStatus({ type: "success", message: "Signed in to Banxito Sponsor." });
  }

  function handleLogout() {
    pool.getCurrentUser()?.signOut();
    clearSponsorSessionState();
  }

  async function handleSponsorRegister(form) {
    try {
      const response = await runWithSessionRetry((activeSession) =>
        apiRequest("/sponsors/register", {
          method: "POST",
          token: activeSession.idToken,
          body: {
            email: form.email,
            firstName: form.firstName,
            lastName: form.lastName,
            phone: form.phone,
            locale: "en-US"
          }
        })
      );

      setStatus({
        type: "success",
        message: `Sponsor profile ready for ${response.fullName}.`
      });
      await loadSummary();
    } catch (error) {
      if (isUnauthorizedError(error)) {
        return;
      }
      setStatus({ type: "error", message: error.message });
    }
  }

  async function handleAddRecipient(event) {
    event.preventDefault();
    try {
      const fullName = joinFullName(recipientForm.firstName, recipientForm.lastName);
      if (!fullName) {
        throw new Error("Recipient first name and/or last name is required.");
      }

      const response = await runWithSessionRetry((activeSession) =>
        apiRequest("/sponsors/recipients", {
          method: "POST",
          token: activeSession.idToken,
          body: {
            ...recipientForm,
            fullName
          }
        })
      );
      setStatus({
        type: "success",
        message: `Added ${response.fullName}. Share the code below so they can finish setup.`
      });
      setRecentInvite({
        recipientId: response.recipientId,
        fullName: response.fullName,
        invitationCode: response.invitationCode,
        invitationExpiresAt: response.invitationExpiresAt,
        invitationStatus: response.invitationStatus
      });
      applyRecipientInviteToSummary(response.recipientId, {
        invitationCode: response.invitationCode,
        invitationExpiresAt: response.invitationExpiresAt,
        invitationStatus: response.invitationStatus
      });
      setRecipientForm({
        firstName: "",
        lastName: "",
        relationshipLabel: "Spouse",
        countryCode: defaultRecipientCountryCode,
        locale: getDefaultLocaleForCountry(defaultRecipientCountryCode, corridors)
      });
      if (!fundingDraft.recipientId) {
        setFundingDraft((draft) => ({ ...draft, recipientId: response.recipientId }));
      }
      await loadSummary();
      setActiveTab("home");
    } catch (error) {
      if (isUnauthorizedError(error)) {
        return;
      }
      setStatus({ type: "error", message: error.message });
    }
  }

  async function handleRefreshInvite(recipientId, recipientName) {
    try {
      const response = await runWithSessionRetry((activeSession) =>
        apiRequest(`/sponsors/recipients/${recipientId}/invite`, {
          method: "POST",
          token: activeSession.idToken,
          body: {
            recipientId
          }
        })
      );

      setRecentInvite({
        recipientId,
        fullName: recipientName,
        invitationCode: response.invitationCode,
        invitationExpiresAt: response.expiresAt,
        invitationStatus: response.status
      });
      applyRecipientInviteToSummary(recipientId, {
        invitationCode: response.invitationCode,
        invitationExpiresAt: response.expiresAt,
        invitationStatus: response.status
      });
      setStatus({
        type: "success",
        message: `New setup code ready for ${recipientName}.`
      });
      await loadSummary();
    } catch (error) {
      if (isUnauthorizedError(error)) {
        return;
      }
      setStatus({ type: "error", message: error.message });
    }
  }

  async function handleCopyInvite(code) {
    try {
      await window.navigator.clipboard.writeText(code);
      setStatus({
        type: "success",
        message: `Setup code copied: ${code}`
      });
    } catch {
      setStatus({
        type: "error",
        message: "Copy was not available. You can still share the code manually."
      });
    }
  }

  async function handleCreateFundingIntent(event) {
    event.preventDefault();
    try {
      const selectedRecipient = recipients.find(
        (recipient) => recipient.recipientId === fundingDraft.recipientId
      );
      const quote = calculateFundingQuote({
        amountUsd: fundingDraft.amountUsd,
        recipientCountryCode: selectedRecipient?.countryCode
      });

      if (!fundingDraft.recipientId || quote.fundingAmountMinor <= 0) {
        throw new Error("Select a recipient and enter a valid amount.");
      }

      const response = await runWithSessionRetry((activeSession) =>
        apiRequest("/funding/payment-intents", {
          method: "POST",
          token: activeSession.idToken,
          body: {
            recipientId: fundingDraft.recipientId,
            fundingAmountMinor: quote.fundingAmountMinor,
            sponsorFeeMinor: quote.sponsorFeeMinor,
            totalChargeMinor: quote.totalChargeMinor,
            currency: "USD",
            recipientEstimatedAmountMinor: quote.recipientEstimatedAmountMinor,
            recipientEstimatedCurrency: quote.recipientEstimatedCurrency,
            fxRateLocked: quote.fxRateLocked
          }
        })
      );

      setPaymentState({
        fundingTransactionId: response.fundingTransactionId,
        paymentIntentId: response.paymentIntentId,
        clientSecret: response.clientSecret,
        fundingAmountMinor: Number(response.fundingAmountMinor ?? 0),
        sponsorFeeMinor: Number(response.sponsorFeeMinor ?? 0),
        totalChargeMinor: Number(response.totalChargeMinor ?? 0),
        recipientEstimatedAmountMinor:
          response.recipientEstimatedAmountMinor == null
            ? null
            : Number(response.recipientEstimatedAmountMinor),
        recipientEstimatedCurrency: response.recipientEstimatedCurrency ?? "",
        fxRateLocked: response.fxRateLocked ?? "",
        recipientId: response.recipientId
      });
      setStatus({
        type: "success",
        message: "Pricing locked. Review the summary and continue funding."
      });
      setActiveTab("fund");
      await loadSummary();
    } catch (error) {
      if (isUnauthorizedError(error)) {
        return;
      }
      setStatus({ type: "error", message: error.message });
    }
  }

  async function handleFundingConfirmed() {
    try {
      const response = await runWithSessionRetry((activeSession) =>
        apiRequest("/funding/confirm", {
          method: "POST",
          token: activeSession.idToken,
          body: {
            fundingTransactionId: paymentState.fundingTransactionId,
            paymentIntentId: paymentState.paymentIntentId
          }
        })
      );

      setStatus({
        type: "success",
        message:
          response.status === "succeeded"
            ? "Allowance purchase complete. Updating the recipient balances now."
            : `Funding updated: ${getFriendlyStatusLabel(response.status)}`
      });
      if (response.status === "succeeded") {
        const allocations = allocationDraft
          .filter((item) => item.amountUsd)
          .map((item) => ({
            categoryCode: item.categoryCode,
            amountMinor: parseUsdToMinor(item.amountUsd)
          }))
          .filter((item) => item.amountMinor > 0);
        const totalAmountMinor = allocations.reduce(
          (sum, item) => sum + Number(item.amountMinor ?? 0),
          0
        );

        if (allocations.length > 0 && totalAmountMinor > 0) {
          await runWithSessionRetry((activeSession) =>
            apiRequest("/wallet-allocations", {
              method: "POST",
              token: activeSession.idToken,
              body: {
                fundingTransactionId: paymentState.fundingTransactionId,
                recipientId: paymentState.recipientId,
                totalAmountMinor,
                allocations
              }
            })
          );
        }

        setPaymentState((current) => ({
          ...current,
          clientSecret: ""
        }));
        setAllocationDraft(buildDefaultAllocationDraft());
        setStatus({
          type: "success",
          message: "Allowance purchase complete. Recipient balances were updated with your planned category split."
        });
      }
      await loadSummary();
      return response.status === "succeeded";
    } catch (error) {
      if (isUnauthorizedError(error)) {
        return false;
      }
      setStatus({ type: "error", message: error.message });
      return false;
    }
  }

  async function handleAllocateFunds(event, allocationTarget = {}) {
    event.preventDefault();
    try {
      const allocations = allocationDraft
        .filter((item) => item.amountUsd)
        .map((item) => ({
          categoryCode: item.categoryCode,
          amountMinor: parseUsdToMinor(item.amountUsd)
        }));

      const totalAmountMinor = allocations.reduce(
        (sum, item) => sum + Number(item.amountMinor ?? 0),
        0
      );

      await runWithSessionRetry((activeSession) =>
        apiRequest("/wallet-allocations", {
          method: "POST",
          token: activeSession.idToken,
          body: {
            fundingTransactionId:
              allocationTarget.fundingTransactionId ?? paymentState.fundingTransactionId,
            recipientId: allocationTarget.recipientId ?? paymentState.recipientId,
            totalAmountMinor,
            allocations
          }
        })
      );

      setStatus({
        type: "success",
        message: "Funds allocated across Banxito wallets."
      });
      await loadSummary();
    } catch (error) {
      if (isUnauthorizedError(error)) {
        return;
      }
      setStatus({ type: "error", message: error.message });
    }
  }

  async function handleReallocateCurrentFunding({ recipientId, targetAllocations, currency }) {
    try {
      await runWithSessionRetry((activeSession) =>
        apiRequest("/wallet-reallocations", {
          method: "POST",
          token: activeSession.idToken,
          body: {
            recipientId,
            targetAllocations: targetAllocations
              .filter((item) => item.amountLocal)
              .map((item) => ({
                categoryCode: item.categoryCode,
                amountMinor: parseUsdToMinor(item.amountLocal)
              }))
              .filter((item) => item.amountMinor > 0)
          }
        })
      );

      setStatus({
        type: "success",
        message: `Updated the active category split in ${currency}.`
      });
      await loadSummary();
      return true;
    } catch (error) {
      if (isUnauthorizedError(error)) {
        return false;
      }
      setStatus({ type: "error", message: error.message });
      return false;
    }
  }

  async function handleSaveWalletTopOffRule({ walletId, rule }) {
    try {
      const response = await runWithSessionRetry((activeSession) =>
        apiRequest(`/sponsors/me/wallets/${walletId}/top-off-rule`, {
          method: "POST",
          token: activeSession.idToken,
          body: rule
        })
      );

      setStatus({
        type: "success",
        message:
          response.topOffRule?.status === "active"
            ? "Automatic top-off is enabled for this category."
            : "Automatic top-off was updated."
      });
      await loadSummary();
      return true;
    } catch (error) {
      if (isUnauthorizedError(error)) {
        return false;
      }
      setStatus({ type: "error", message: error.message });
      return false;
    }
  }

  async function handleUpdateSponsorProfile(form) {
    try {
      const response = await runWithSessionRetry((activeSession) =>
        apiRequest("/sponsors/me", {
          method: "POST",
          token: activeSession.idToken,
          body: {
            firstName: form.firstName,
            lastName: form.lastName,
            phone: form.phone,
            locale: summary?.sponsor?.locale ?? "en-US"
          }
        })
      );

      setStatus({
        type: "success",
        message: `Updated sponsor profile for ${response.fullName}.`
      });
      await loadSummary();
    } catch (error) {
      if (isUnauthorizedError(error)) {
        return;
      }
      setStatus({ type: "error", message: error.message });
    }
  }

  async function handleChangePassword(form) {
    try {
      if (form.nextPassword !== form.confirmPassword) {
        throw new Error("New passwords do not match.");
      }

      await changeCurrentPassword(form.currentPassword, form.nextPassword);
      setStatus({
        type: "success",
        message: "Password updated successfully."
      });
      return true;
    } catch (error) {
      setStatus({ type: "error", message: error.message });
      return false;
    }
  }

  if (restoringSession) {
    return (
      <I18nContext.Provider value={i18nValue}>
        <div className="app-shell auth-shell">
          <LanguageSelect />
          <SessionRestoreCard />
        </div>
      </I18nContext.Provider>
    );
  }

  if (!session?.idToken) {
    return (
      <I18nContext.Provider value={i18nValue}>
        <div className="app-shell auth-shell">
          <LanguageSelect />
          <AuthCard onAuthenticated={handleAuthenticated} notice={authNotice} />
        </div>
      </I18nContext.Provider>
    );
  }

  const latestFund = fundingServiceActivity.find(
    (item) => item.fundingTransactionId === paymentState.fundingTransactionId
  );

  return (
    <I18nContext.Provider value={i18nValue}>
      <div className="app-shell">
        <div className="ambient-orb ambient-orb-a" />
        <div className="ambient-orb ambient-orb-b" />
        <header className="compact-header">
          <div className="brand-lockup">
            <img className="brand-logo" src={logoSrc} alt="Banxito" />
            <div>
            <p className="eyebrow">{t("Banxito Sponsor")}</p>
            <h1>{summary?.sponsor?.fullName ?? session.email}</h1>
            </div>
          </div>
          <div className="header-actions">
            <LanguageSelect compact />
            <button className="ghost-button" onClick={handleLogout}>
              {t("Sign out")}
            </button>
          </div>
        </header>

      <main className="scroll-panel">
        {status.message ? (
          <div className={`status-banner status-${status.type}`}>
            {translateSponsorStatusMessage(status.message, t, format)}
          </div>
        ) : null}

        {!summary?.sponsor ? (
          <SetupSponsorCard
            email={session.email}
            fullName={session.fullName}
            onSubmit={handleSponsorRegister}
            loading={loadingSummary}
          />
        ) : (
          <>
            <DashboardTab
              visible={activeTab === "home"}
              summary={summary}
              loading={loadingSummary}
              totalPurchasedMinor={totalPurchasedMinor}
              pendingServiceActivity={pendingServiceActivity}
              onGoFund={() => setActiveTab("fund")}
              onGoRecipients={() => setActiveTab("recipients")}
              corridors={corridors}
              recentInvite={recentInvite}
              onRefreshInvite={handleRefreshInvite}
              onCopyInvite={handleCopyInvite}
            />

            <RecipientsTab
              visible={activeTab === "recipients"}
              recipientForm={recipientForm}
              setRecipientForm={setRecipientForm}
              corridors={corridors}
              recipientCountryOptions={recipientCountryOptions}
              onSubmit={handleAddRecipient}
            />

            <FundTabV2
              visible={activeTab === "fund"}
              recipients={recipients}
              fundingServiceActivity={fundingServiceActivity}
              fundingDraft={fundingDraft}
              setFundingDraft={setFundingDraft}
              onResetFundingIntent={handleResetFundingIntent}
              onCreateIntent={handleCreateFundingIntent}
              paymentState={paymentState}
              latestFund={latestFund}
              onFundingConfirmed={handleFundingConfirmed}
              allocationDraft={allocationDraft}
              setAllocationDraft={setAllocationDraft}
              corridors={corridors}
              categoriesByCorridor={categoriesByCorridor}
              onAllocateFunds={handleAllocateFunds}
              onReallocateCurrentFunding={handleReallocateCurrentFunding}
              defaultPaymentMethod={summary?.defaultPaymentMethod ?? null}
              onSaveWalletTopOffRule={handleSaveWalletTopOffRule}
              onGoPaymentSettings={() => setActiveTab("me")}
            />

            <EnhancedActivityTab
              visible={activeTab === "activity"}
              fundingServiceActivity={fundingServiceActivity}
              recipients={recipients}
              careEvents={summary?.careEvents ?? []}
              categoryUsage={summary?.categoryUsage ?? []}
            />

            <AccountTab
              visible={activeTab === "me"}
              summary={summary}
              session={session}
              onUpdateProfile={handleUpdateSponsorProfile}
              onChangePassword={handleChangePassword}
              runWithSessionRetry={runWithSessionRetry}
              onPaymentMethodSaved={() => loadSummary(session)}
            />
          </>
        )}
      </main>

      <nav className="mobile-bottom-nav">
        {[
          ["home", "Home"],
          ["fund", "Fund"],
          ["activity", "Activity"],
          ["me", "Me"]
        ].map(([key, label]) => (
          <button
            key={key}
            className={activeTab === key ? "nav-item active" : "nav-item"}
            onClick={() => setActiveTab(key)}
          >
            {t(label)}
          </button>
        ))}
      </nav>
      </div>
    </I18nContext.Provider>
  );
}

function AuthCard({ onAuthenticated, notice }) {
  const { t } = useI18n();
  const [mode, setMode] = useState("signin");
  const [authStep, setAuthStep] = useState("auth");
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
    code: "",
    recoveryEmail: "",
    resetCode: "",
    resetPassword: "",
    resetConfirmPassword: ""
  });
  const [status, setStatus] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [showResetConfirmPassword, setShowResetConfirmPassword] = useState(false);
  const passwordChecks = getPasswordChecks(form.password);
  const passwordIsStrong = passwordChecks.every((check) => check.ok);
  const resetPasswordChecks = getPasswordChecks(form.resetPassword);
  const resetPasswordIsStrong = resetPasswordChecks.every((check) => check.ok);

  function resetToSignIn(nextStatus = "") {
    setMode("signin");
    setAuthStep("auth");
    setStatus(nextStatus);
    setForm((current) => ({
      ...current,
      password: "",
      confirmPassword: "",
      code: "",
      resetCode: "",
      resetPassword: "",
      resetConfirmPassword: ""
    }));
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setSubmitting(true);
    setStatus("");

    try {
      if (mode === "signup") {
        if (!passwordIsStrong) {
          throw new Error("Choose a stronger password before continuing.");
        }
        if (form.password !== form.confirmPassword) {
          throw new Error("Passwords do not match.");
        }
        window.localStorage.setItem(
          storageKeys.pendingSignupProfile,
          JSON.stringify({
            firstName: form.firstName,
            lastName: form.lastName,
            email: form.email
          })
        );
        await signUp({
          email: form.email,
          password: form.password,
          firstName: form.firstName,
          lastName: form.lastName
        });
        setAuthStep("confirm");
        setStatus("Check your email for the verification code, then confirm your account.");
      } else {
        const session = await signIn(form.email, form.password);
        onAuthenticated(session);
      }
    } catch (error) {
      setStatus(error.message);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleConfirm(event) {
    event.preventDefault();
    setSubmitting(true);
    setStatus("");
    try {
      await confirmSignUp(form.email, form.code);
      const session = await signIn(form.email, form.password);
      onAuthenticated(session);
    } catch (error) {
      setStatus(error.message);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleResetRequest(event) {
    event.preventDefault();
    setSubmitting(true);
    setStatus("");

    try {
      const recoveryEmail = form.recoveryEmail.trim().toLowerCase() || form.email.trim().toLowerCase();
      if (!recoveryEmail) {
        throw new Error("Enter the email address for the Banxito sponsor account.");
      }

      const recovery = await requestPasswordReset(recoveryEmail);
      setForm((current) => ({ ...current, recoveryEmail }));
      setAuthStep("recoverConfirm");
      setStatus(
        recovery.destination
          ? `We sent a password reset code to ${recovery.destination}.`
          : "We sent a password reset code to your email address."
      );
    } catch (error) {
      setStatus(error.message);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleResetConfirm(event) {
    event.preventDefault();
    setSubmitting(true);
    setStatus("");

    try {
      const recoveryEmail = form.recoveryEmail.trim().toLowerCase() || form.email.trim().toLowerCase();
      if (!recoveryEmail) {
        throw new Error("Enter the email address for the Banxito sponsor account.");
      }
      if (!resetPasswordIsStrong) {
        throw new Error("Choose a stronger password before continuing.");
      }
      if (form.resetPassword !== form.resetConfirmPassword) {
        throw new Error("Passwords do not match.");
      }

      await confirmPasswordReset(recoveryEmail, form.resetCode, form.resetPassword);
      setForm((current) => ({
        ...current,
        email: recoveryEmail,
        password: "",
        resetCode: "",
        resetPassword: "",
        resetConfirmPassword: ""
      }));
      resetToSignIn("Password updated. Sign in with your new password.");
    } catch (error) {
      setStatus(error.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section className="auth-card">
      <img className="hero-logo" src={logoSrc} alt="Banxito" />
      <p className="eyebrow">{t("Banxito Sponsor MVP")}</p>
      <h1>{t("Sponsor care services for family")}</h1>
      <p className="lede">
        {t("Sign in, create a recipient, purchase restricted service allowances, and track every authorization and fulfillment event from one mobile-first dashboard.")}
      </p>

      {notice ? <div className="status-banner status-error">{notice}</div> : null}

      <div className="mode-switch">
        <button
          className={mode === "signin" ? "mode-pill active" : "mode-pill"}
          onClick={() => {
            resetToSignIn("");
          }}
        >
          {t("Sign in")}
        </button>
        <button
          className={mode === "signup" ? "mode-pill active" : "mode-pill"}
          onClick={() => {
            setMode("signup");
            setAuthStep("auth");
            setStatus("");
          }}
        >
          {t("Create account")}
        </button>
      </div>

      {authStep === "confirm" ? (
        <form className="stack-form" onSubmit={handleConfirm}>
          <Field
            label="Verification code"
            value={form.code}
            onChange={(value) => setForm((current) => ({ ...current, code: value }))}
          />
          <button className="primary-button" disabled={submitting}>
            {submitting ? t("Confirming...") : t("Confirm account")}
          </button>
        </form>
      ) : authStep === "recoverRequest" ? (
        <form className="stack-form" onSubmit={handleResetRequest}>
          <Field
            label="Sponsor email"
            type="email"
            value={form.recoveryEmail}
            onChange={(value) =>
              setForm((current) => ({ ...current, recoveryEmail: value.toLowerCase() }))
            }
            helper="Banxito will email a password reset code to this address."
          />
          <button className="primary-button" disabled={submitting}>
            {submitting ? t("Sending...") : t("Send reset code")}
          </button>
          <button
            className="ghost-button"
            type="button"
            onClick={() => resetToSignIn("")}
          >
            {t("Back to sign in")}
          </button>
        </form>
      ) : authStep === "recoverConfirm" ? (
        <form className="stack-form" onSubmit={handleResetConfirm}>
          <Field
            label="Verification code"
            value={form.resetCode}
            onChange={(value) => setForm((current) => ({ ...current, resetCode: value }))}
            helper="Enter the reset code from your email."
          />
          <Field
            label="New password"
            type={showResetPassword ? "text" : "password"}
            value={form.resetPassword}
            onChange={(value) => setForm((current) => ({ ...current, resetPassword: value }))}
            trailingAction={{
              label: showResetPassword ? t("Hide") : t("Show"),
              onClick: () => setShowResetPassword((current) => !current)
            }}
          />
          <PasswordChecklist checks={resetPasswordChecks} />
          <Field
            label="Confirm new password"
            type={showResetConfirmPassword ? "text" : "password"}
            value={form.resetConfirmPassword}
            onChange={(value) =>
              setForm((current) => ({ ...current, resetConfirmPassword: value }))
            }
            trailingAction={{
              label: showResetConfirmPassword ? t("Hide") : t("Show"),
              onClick: () => setShowResetConfirmPassword((current) => !current)
            }}
          />
          <button className="primary-button" disabled={submitting}>
            {submitting ? t("Updating...") : t("Update password")}
          </button>
          <button
            className="ghost-button"
            type="button"
            onClick={() => setAuthStep("recoverRequest")}
          >
            {t("Send code again")}
          </button>
        </form>
      ) : (
        <form className="stack-form" onSubmit={handleSubmit}>
          {mode === "signup" ? (
            <div className="two-col">
              <Field
                label="First name"
                value={form.firstName}
                onChange={(value) =>
                  setForm((current) => ({ ...current, firstName: value }))
                }
              />
              <Field
                label="Last name"
                value={form.lastName}
                onChange={(value) =>
                  setForm((current) => ({ ...current, lastName: value }))
                }
              />
            </div>
          ) : null}
          <Field
            label="Email"
            type="email"
            value={form.email}
            onChange={(value) => setForm((current) => ({ ...current, email: value }))}
          />
          <Field
            label="Password"
            type={showPassword ? "text" : "password"}
            value={form.password}
            onChange={(value) => setForm((current) => ({ ...current, password: value }))}
            helper={mode === "signup" ? null : "Tap Show if you want to verify what you typed."}
            trailingAction={{
              label: showPassword ? t("Hide") : t("Show"),
              onClick: () => setShowPassword((current) => !current)
            }}
          />
          {mode === "signup" ? <PasswordChecklist checks={passwordChecks} /> : null}
          {mode === "signup" ? (
            <Field
              label="Confirm password"
              type={showConfirmPassword ? "text" : "password"}
              value={form.confirmPassword}
              onChange={(value) =>
                setForm((current) => ({ ...current, confirmPassword: value }))
              }
              trailingAction={{
                label: showConfirmPassword ? t("Hide") : t("Show"),
                onClick: () => setShowConfirmPassword((current) => !current)
              }}
            />
          ) : null}
          <button className="primary-button" disabled={submitting}>
            {submitting
              ? t("Working...")
              : mode === "signup"
                ? t("Create sponsor account")
                : t("Sign in")}
          </button>
          {mode === "signin" ? (
            <button
              className="ghost-button"
              type="button"
              onClick={() => {
                setAuthStep("recoverRequest");
                setStatus("");
                setForm((current) => ({
                  ...current,
                  recoveryEmail: current.recoveryEmail || current.email
                }));
              }}
            >
              {t("Forgot password?")}
            </button>
          ) : null}
        </form>
      )}

      {status ? <p className="helper-text">{status}</p> : null}
    </section>
  );
}

function SessionRestoreCard() {
  const { t } = useI18n();
  return (
    <section className="auth-card">
      <img className="hero-logo" src={logoSrc} alt="Banxito" />
      <p className="eyebrow">{t("Restoring session")}</p>
      <h1>{t("Getting your sponsor workspace ready")}</h1>
      <p className="lede">
        {t("Banxito is checking your saved Cognito session and refreshing it silently when possible.")}
      </p>
      <div className="session-loader" aria-hidden="true" />
      <p className="helper-text muted">{t("This usually takes a second or two.")}</p>
    </section>
  );
}

function SetupSponsorCard({ email, fullName, onSubmit, loading }) {
  const { t } = useI18n();
  const pendingProfile = useMemo(() => {
    try {
      const raw = window.localStorage.getItem(storageKeys.pendingSignupProfile);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, []);

  const splitPendingName = splitFullName(pendingProfile?.firstName || pendingProfile?.lastName
    ? joinFullName(pendingProfile?.firstName, pendingProfile?.lastName)
    : pendingProfile?.fullName ?? fullName ?? "");

  const [form, setForm] = useState({
    email: pendingProfile?.email ?? email,
    firstName: pendingProfile?.firstName ?? splitPendingName.firstName,
    lastName: pendingProfile?.lastName ?? splitPendingName.lastName,
    phone: ""
  });

  useEffect(() => {
    const splitName = splitFullName(fullName || "");
    setForm((current) => ({
      ...current,
      email,
      firstName: current.firstName || pendingProfile?.firstName || splitName.firstName,
      lastName: current.lastName || pendingProfile?.lastName || splitName.lastName
    }));
  }, [email, fullName, pendingProfile]);

  return (
    <section className="surface-card">
      <div className="section-head">
        <div>
          <p className="eyebrow">{t("Finish setup")}</p>
          <h2>{t("Create your sponsor profile")}</h2>
        </div>
      </div>
      <form
        className="stack-form"
        onSubmit={(event) => {
          event.preventDefault();
          window.localStorage.removeItem(storageKeys.pendingSignupProfile);
          void onSubmit(form);
        }}
      >
        <div className="two-col">
          <Field
            label="First name"
            value={form.firstName}
            onChange={(value) => setForm((current) => ({ ...current, firstName: value }))}
          />
          <Field
            label="Last name"
            value={form.lastName}
            onChange={(value) => setForm((current) => ({ ...current, lastName: value }))}
          />
        </div>
        <Field label="Email" value={form.email} disabled />
        <Field
          label="Phone"
          value={form.phone}
          onChange={(value) => setForm((current) => ({ ...current, phone: value }))}
        />
        <button className="primary-button" disabled={loading}>
          {loading ? t("Saving...") : t("Activate sponsor profile")}
        </button>
      </form>
    </section>
  );
}

function DashboardTab({
  visible,
  summary,
  loading,
  totalPurchasedMinor,
  pendingServiceActivity,
  onGoFund,
  onGoRecipients,
  corridors,
  recentInvite,
  onRefreshInvite,
  onCopyInvite
}) {
  if (!visible) {
    return null;
  }

  const { t } = useI18n();
  const recipients = summary?.recipients ?? [];
  const activeAllowances = recipients.flatMap((recipient) => recipient.wallets ?? []);
  const fundingServiceActivity = summary?.fundingServiceActivity ?? [];
  const careEvents = summary?.careEvents ?? [];
  const [expandedInviteRecipientId, setExpandedInviteRecipientId] = useState("");

  useEffect(() => {
    if (recentInvite?.recipientId) {
      setExpandedInviteRecipientId(recentInvite.recipientId);
    }
  }, [recentInvite]);

  return (
    <div className="screen-stack">
      <section className="hero-card">
        <p className="eyebrow">{t("Sponsor home")}</p>
        <h2>{t("Support overview")}</h2>
        <p className="lede">
          {t("See every recipient, service allowance, and fulfillment event in one place before you purchase the next care category.")}
        </p>
        <div className="metrics-grid">
          <MetricCard label="Recipients" value={String(recipients.length)} />
          <MetricCard label="Purchased allowances" value={formatMoney(totalPurchasedMinor)} />
          <MetricCard label="Pending purchases" value={String(pendingServiceActivity.length)} />
          <MetricCard label="Active allowances" value={String(activeAllowances.length)} />
        </div>
        <div className="action-row">
          <button className="primary-button" onClick={onGoFund}>
            {t("Purchase allowance")}
          </button>
          <button className="ghost-button" onClick={onGoRecipients}>
            {t("Add recipient")}
          </button>
        </div>
      </section>

      <SponsorCareAssurancePanel
        recipients={recipients}
        activeAllowances={activeAllowances}
        fundingServiceActivity={fundingServiceActivity}
        careEvents={careEvents}
        onGoFund={onGoFund}
      />

      <section className="surface-card">
        <div className="section-head">
          <div>
            <p className="eyebrow">{t("Linked family members")}</p>
            <h2>{t("Recipient snapshot")}</h2>
          </div>
          <button className="ghost-button" type="button" onClick={onGoRecipients}>
            {t("Create setup code")}
          </button>
        </div>
        {loading ? <SkeletonCards count={2} /> : null}
        {!loading && recipients.length === 0 ? (
          <EmptyState
            title="No recipients yet"
            message="Create the first recipient to purchase healthcare, grocery, tuition, or home support allowances."
          />
        ) : null}
        {!loading
          ? recipients.map((recipient) => (
              <div className="entity-card" key={recipient.recipientId}>
                {(() => {
                  const pendingInvite =
                    recentInvite?.recipientId === recipient.recipientId
                      ? recentInvite
                      : null;
                  const visibleInvitationCode =
                    recipient.invitationCode ?? pendingInvite?.invitationCode ?? null;
                  const visibleInvitationExpiresAt =
                    recipient.invitationExpiresAt ?? pendingInvite?.invitationExpiresAt ?? null;

                  return (
                    <>
                <div className="entity-topline">
                  <strong>{recipient.fullName}</strong>
                  <span className={`badge badge-${recipient.status}`}>
                    {t(getFriendlyStatusLabel(recipient.status))}
                  </span>
                </div>
                <p className="meta-line">
                  {t(recipient.relationshipLabel || "Family member")} · {t(getCountryLabel(recipient.countryCode))} ·{" "}
                  {t(getRecipientLanguageLabel(recipient.locale, recipient.countryCode, corridors))}
                </p>
                {visibleInvitationCode ? (
                  <div className="invite-panel">
                    <div className="invite-panel-header">
                      <div>
                        <span className="meta-label">{t("Setup code")}</span>
                        <p className="helper-text muted">
                          {t("Share this code so the recipient can finish setting up their account.")}
                        </p>
                      </div>
                      <button
                        className="ghost-button invite-toggle"
                        type="button"
                        onClick={() =>
                          setExpandedInviteRecipientId((current) =>
                            current === recipient.recipientId ? "" : recipient.recipientId
                          )
                        }
                      >
                        {expandedInviteRecipientId === recipient.recipientId ? t("Hide code") : t("Show code")}
                      </button>
                    </div>
                    {expandedInviteRecipientId === recipient.recipientId ? (
                      <>
                        <div className="invite-code-row">
                          <strong className="invite-code">{visibleInvitationCode}</strong>
                        </div>
                        <p className="helper-text muted">
                          {t("Expires")} {new Date(visibleInvitationExpiresAt).toLocaleString()}.
                        </p>
                        <div className="action-row">
                          <button
                            className="ghost-button"
                            type="button"
                            onClick={() => onCopyInvite(visibleInvitationCode)}
                          >
                            {t("Copy code")}
                          </button>
                          <button
                            className="ghost-button"
                            type="button"
                            onClick={() => onRefreshInvite(recipient.recipientId, recipient.fullName)}
                          >
                            {t("New code")}
                          </button>
                        </div>
                      </>
                    ) : null}
                  </div>
                ) : (
                  <div className="invite-panel">
                    <p className="helper-text muted">
                      {recipient.status === "pending_bind"
                        ? t("This recipient still needs a setup code before they can connect their account.")
                        : t("Create a fresh setup code if this recipient needs to reconnect their account.")}
                    </p>
                    <div className="action-row">
                      <button
                        className="ghost-button"
                        type="button"
                        onClick={() => onRefreshInvite(recipient.recipientId, recipient.fullName)}
                      >
                        {t(recipient.status === "pending_bind" ? "Create code" : "New setup code")}
                      </button>
                    </div>
                  </div>
                )}
                <div className="wallet-grid">
                  {(recipient.wallets ?? []).length === 0 ? (
                    <p className="helper-text muted">
                      {t("No service allowances yet. The first successful purchase can be assigned by category here.")}
                    </p>
                  ) : (
                    recipient.wallets.map((wallet) => (
                      <div className="wallet-chip" key={wallet.recipientWalletId}>
                        <span>{t(wallet.categoryName)}</span>
                        <strong>{formatMoney(wallet.balanceMinor, wallet.currency)}</strong>
                      </div>
                    ))
                  )}
                </div>
                    </>
                  );
                })()}
              </div>
            ))
          : null}
      </section>
    </div>
  );
}

function SponsorCareAssurancePanel({
  recipients,
  activeAllowances,
  fundingServiceActivity,
  careEvents,
  onGoFund
}) {
  const { t } = useI18n();

  const successfulFunding = fundingServiceActivity
    .filter((transaction) => transaction.status === "succeeded")
    .slice()
    .sort(
      (left, right) =>
        new Date(right.fundedAt ?? right.createdAt).getTime() -
        new Date(left.fundedAt ?? left.createdAt).getTime()
    );
  const recipientMap = new Map(recipients.map((recipient) => [recipient.recipientId, recipient.fullName]));
  const walletRecords = recipients.flatMap((recipient) =>
    (recipient.wallets ?? []).map((wallet) => ({
      ...wallet,
      recipientName: recipient.fullName
    }))
  );
  const receiptCount = careEvents.filter((event) => event.receiptFileUrl).length;
  const pendingReceiptCount = careEvents.filter((event) => !event.receiptFileUrl).length;
  const pendingPaymentCount = fundingServiceActivity.filter((transaction) =>
    transaction.status !== "succeeded" && !shouldHideTransactionByDefault(transaction)
  ).length;
  const lowBalanceCount = walletRecords.filter((wallet) => Number(wallet.balanceMinor ?? 0) > 0 && Number(wallet.balanceMinor ?? 0) < 20000).length;
  const needsAttentionCount = pendingReceiptCount + pendingPaymentCount + lowBalanceCount;

  const timelineItems = [
    ...careEvents.map((event) => ({
      key: `care-${event.redemptionId}`,
      type: "care",
      title: t("Care delivered"),
      body: `${event.recipientName ?? t("Recipient")} - ${event.providerName ?? t("Approved provider")} - ${t(event.categoryName ?? "Support")}`,
      detail: event.receiptFileUrl ? t("Receipt uploaded") : t("Receipt pending"),
      amount: formatMoney(event.amountMinor, event.currency),
      timestamp: event.completedAt ?? event.claimSubmittedAt ?? event.authorizedAt ?? event.requestedAt
    })),
    ...successfulFunding.map((transaction) => ({
      key: `fund-${transaction.fundingTransactionId}`,
      type: "funding",
      title: t("Support added"),
      body: `${recipientMap.get(transaction.recipientId) ?? t("Recipient")} - ${t("Balance updated")}`,
      detail: t("Your support is ready for care."),
      amount: formatMoney(transaction.amountMinor, transaction.currency),
      timestamp: transaction.fundedAt ?? transaction.createdAt
    })),
    ...walletRecords
      .filter((wallet) => Number(wallet.balanceMinor ?? 0) > 0)
      .map((wallet) => ({
        key: `wallet-${wallet.recipientWalletId}`,
        type: "wallet",
        title: t("Support available"),
        body: `${wallet.recipientName} - ${t(wallet.categoryName)}`,
        detail: t("Ready to help"),
        amount: formatMoney(wallet.balanceMinor, wallet.currency),
        timestamp: null
      }))
  ]
    .sort((left, right) => {
      const leftTime = left.timestamp ? new Date(left.timestamp).getTime() : 0;
      const rightTime = right.timestamp ? new Date(right.timestamp).getTime() : 0;
      return rightTime - leftTime;
    })
    .slice(0, 5);

  return (
    <section className="surface-card care-assurance-card">
      <div className="section-head">
        <div>
          <p className="eyebrow">{t("Reassurance")}</p>
          <h2>{t("Care assurance dashboard")}</h2>
          <p className="section-copy">
            {t("See who is okay, what care was delivered, and what needs attention next.")}
          </p>
        </div>
        <button className="ghost-button" type="button" onClick={onGoFund}>
          {t("Fund more care")}
        </button>
      </div>

      <div className="care-impact-grid">
        <MetricCard label="Provider visits" value={String(careEvents.length)} />
        <MetricCard label="Receipts verified" value={String(receiptCount)} />
        <MetricCard label="Needs attention" value={String(needsAttentionCount)} />
        <MetricCard label="Active category allowances" value={String(activeAllowances.length)} />
      </div>

      <div className="care-assurance-layout">
        <div className="care-story-card">
          <div className="entity-topline">
            <div>
              <p className="eyebrow">{t("Care timeline")}</p>
              <h3>{t("Impact so far")}</h3>
            </div>
          </div>
          {timelineItems.length === 0 ? (
            <EmptyState
              title="No care timeline yet"
              message="Once allowance purchases or provider care are recorded, Banxito will show the care story here."
            />
          ) : (
            <div className="care-timeline">
              {timelineItems.map((item) => (
                <div className={`care-timeline-item care-timeline-${item.type}`} key={item.key}>
                  <span className="timeline-dot" />
                  <div>
                    <div className="timeline-heading">
                      <strong>{item.title}</strong>
                      <span>{item.amount}</span>
                    </div>
                    <p>{item.body}</p>
                    <small>
                      {item.detail}
                      {item.timestamp ? ` - ${new Date(item.timestamp).toLocaleString()}` : ""}
                    </small>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="care-controls-card">
          <div>
            <p className="eyebrow">{t("Confidence signals")}</p>
            <div className="confidence-grid">
              <span>{t("Approved provider")}</span>
              <span>{t("Receipt verified")}</span>
              <span>{t("Within sponsor rules")}</span>
            </div>
          </div>

          <div className="care-next-step">
            <p className="eyebrow">{t("Next allowance step")}</p>
            <h3>{t("Add purpose while purchasing")}</h3>
            <p>
              {t(
                "When you fund, Banxito will ask what the support is for and let you draft a gentle note for the recipient."
              )}
            </p>
            <button className="ghost-button" type="button" onClick={onGoFund}>
              {t("Go to purchase")}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

function RecipientsTab({
  visible,
  recipientForm,
  setRecipientForm,
  corridors,
  recipientCountryOptions,
  onSubmit
}) {
  if (!visible) {
    return null;
  }
  const { t } = useI18n();

  return (
    <div className="screen-stack">
      <section className="surface-card">
        <div className="section-head">
          <div>
            <p className="eyebrow">{t("Recipient setup")}</p>
            <h2>{t("Add a family member")}</h2>
          </div>
        </div>
        <form className="stack-form" onSubmit={onSubmit}>
          <div className="two-col">
            <Field
              label="First name"
              value={recipientForm.firstName}
              onChange={(value) =>
                setRecipientForm((current) => ({ ...current, firstName: value }))
              }
            />
            <Field
              label="Last name"
              value={recipientForm.lastName}
              onChange={(value) =>
                setRecipientForm((current) => ({ ...current, lastName: value }))
              }
            />
          </div>
          <SelectField
            label="Relationship"
            value={recipientForm.relationshipLabel}
            onChange={(value) =>
              setRecipientForm((current) => ({ ...current, relationshipLabel: value }))
            }
            options={relationshipOptions.map((option) => ({
              value: option,
              label: option
            }))}
          />
          <div className="two-col">
            <SelectField
              label="Country"
              value={recipientForm.countryCode}
              onChange={(value) =>
                setRecipientForm((current) => ({
                  ...current,
                  countryCode: value,
                  locale: getDefaultLocaleForCountry(value, corridors)
                }))
              }
              options={recipientCountryOptions.map((country) => ({
                value: country.code,
                label: country.label
              }))}
            />
            <SelectField
              label="Language"
              value={recipientForm.locale}
              onChange={(value) =>
                setRecipientForm((current) => ({ ...current, locale: value }))
              }
              options={getLocaleOptionsForCountry(recipientForm.countryCode, corridors).map((locale) => ({
                value: locale.value,
                label: locale.label
              }))}
            />
          </div>
          <button className="primary-button">{t("Save recipient")}</button>
        </form>
      </section>

      {false ? (
      <section className="surface-card">
        <div className="section-head">
          <div>
            <p className="eyebrow">Manage recipients</p>
            <h2>Current linked accounts</h2>
          </div>
        </div>
        {recipients.length === 0 ? (
          <EmptyState
            title="No family profiles yet"
            message="Once a recipient is added, they’ll appear here with their balance categories and status."
          />
        ) : (
          recipients.map((recipient) => (
            <div className="entity-card" key={recipient.recipientId}>
              <div className="entity-topline">
                <strong>{recipient.fullName}</strong>
                <span className={`badge badge-${recipient.status}`}>
                  {getFriendlyStatusLabel(recipient.status)}
                </span>
              </div>
              <p className="meta-line">
                {recipient.relationshipLabel || "Family member"} · {getCountryLabel(recipient.countryCode)} · {recipient.locale}
              </p>
              {recipient.invitationCode ? (
                <div className="invite-panel">
                  <div className="invite-panel-header">
                    <div>
                      <span className="meta-label">Setup code</span>
                      <p className="helper-text muted">
                        Share this code so the recipient can finish setting up their account.
                      </p>
                    </div>
                    <button
                      className="ghost-button invite-toggle"
                      type="button"
                      onClick={() =>
                        setExpandedInviteRecipientId((current) =>
                          current === recipient.recipientId ? "" : recipient.recipientId
                        )
                      }
                    >
                      {expandedInviteRecipientId === recipient.recipientId ? "Hide code" : "Show code"}
                    </button>
                  </div>
                  {expandedInviteRecipientId === recipient.recipientId ? (
                    <>
                      <div className="invite-code-row">
                        <strong className="invite-code">{recipient.invitationCode}</strong>
                      </div>
                      <p className="helper-text muted">
                        Expires {new Date(recipient.invitationExpiresAt).toLocaleString()}.
                      </p>
                      <div className="action-row">
                        <button
                          className="ghost-button"
                          type="button"
                          onClick={() => onCopyInvite(recipient.invitationCode)}
                        >
                          Copy code
                        </button>
                        <button
                          className="ghost-button"
                          type="button"
                          onClick={() => onRefreshInvite(recipient.recipientId, recipient.fullName)}
                        >
                          New code
                        </button>
                      </div>
                    </>
                  ) : null}
                </div>
              ) : recipient.status === "pending_bind" ? (
                <div className="invite-panel">
                  <p className="helper-text muted">
                    {t("This recipient still needs a setup code before they can connect their account.")}
                  </p>
                  <div className="action-row">
                    <button
                      className="ghost-button"
                      type="button"
                      onClick={() => onRefreshInvite(recipient.recipientId, recipient.fullName)}
                    >
                      {t("Create code")}
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          ))
        )}
      </section>
      ) : null}
    </div>
  );
}

function FundTab({
  visible,
  recipients,
  fundingDraft,
  setFundingDraft,
  onCreateIntent,
  paymentState,
  latestFund,
  onFundingConfirmed,
  allocationDraft,
  setAllocationDraft,
  onAllocateFunds
}) {
  if (!visible) {
    return null;
  }

  const selectedRecipient = recipients.find(
    (recipient) => recipient.recipientId === fundingDraft.recipientId
  );
  const [showFeeInfo, setShowFeeInfo] = useState(false);
  const quote = calculateFundingQuote({
    amountUsd: fundingDraft.amountUsd,
    recipientCountryCode: selectedRecipient?.countryCode
  });
  const showCheckout = Boolean(paymentState.clientSecret);
  const checkoutReady = Boolean(stripePromise && config.stripePublishableKey);
  const isSucceeded = latestFund?.status === "succeeded";
  const showFundingSuccess =
    Boolean(paymentState.fundingTransactionId) &&
    !showCheckout &&
    isSucceeded;
  const allocatableAmountMinor = isSucceeded
    ? Number(latestFund?.amountMinor ?? paymentState.fundingAmountMinor ?? 0)
    : 0;
  const allocatedDraftMinor = allocationDraft.reduce(
    (sum, row) => sum + parseUsdToMinor(row.amountUsd),
    0
  );
  const remainingAllocationMinor = allocatableAmountMinor - allocatedDraftMinor;
  const allocationMatchesFunding =
    allocatableAmountMinor > 0 && remainingAllocationMinor === 0;
  const quoteMatchesCheckout =
    paymentState.fundingAmountMinor > 0 &&
    paymentState.fundingAmountMinor === quote.fundingAmountMinor &&
    paymentState.recipientId === fundingDraft.recipientId;

  return (
    <div className="screen-stack">
      <section className="surface-card">
        <div className="section-head">
          <div>
            <p className="eyebrow">Purchase allowance</p>
            <h2>Pricing and FX calculator</h2>
          </div>
        </div>
        <form className="stack-form" onSubmit={onCreateIntent}>
          <SelectField
            label="Recipient"
            value={fundingDraft.recipientId}
            onChange={(value) =>
              setFundingDraft((current) => ({
                ...current,
                recipientId: value
              }))
            }
            placeholder="Choose recipient"
            options={recipients.map((recipient) => ({
              value: recipient.recipientId,
              label: `${recipient.fullName} (${getCountryLabel(recipient.countryCode)})`
            }))}
          />
          <Field
            label="Allowance amount in USD"
            value={fundingDraft.amountUsd}
            onChange={(value) =>
              setFundingDraft((current) => ({ ...current, amountUsd: value }))
            }
          />
          <div className="fund-quote-grid">
            <div className="quote-panel quote-panel-accent">
              <p className="quote-label">Recipient allowance</p>
              <strong>{formatMoney(quote.fundingAmountMinor, "USD")}</strong>
              <p className="helper-text muted">
                This is the service allowance Banxito will make available to the recipient.
              </p>
            </div>
            <div className="quote-panel">
              <div className="quote-label-row">
                <p className="quote-label">Banxito fee</p>
                <button
                  className="info-chip"
                  type="button"
                  onClick={() => setShowFeeInfo((current) => !current)}
                  aria-expanded={showFeeInfo}
                  aria-label="Show Banxito fee ranges"
                >
                  i
                </button>
              </div>
              <strong>{formatMoney(quote.sponsorFeeMinor, "USD")}</strong>
              <p className="helper-text muted">
                {Math.round(quote.feeRate * 100)}% includes payment processing,
                cross-border handling, and exchange-rate variation.
              </p>
            </div>
            <div className="quote-panel">
              <p className="quote-label">Total sponsor charge</p>
              <strong>{formatMoney(quote.totalChargeMinor, "USD")}</strong>
              <p className="helper-text muted">
                Your card is charged once for the service allowance plus the Banxito fee.
              </p>
            </div>
            <div className="quote-panel">
              <p className="quote-label">Recipient sees</p>
              <strong>
                {quote.recipientEstimatedAmountMinor == null
                  ? "Estimate pending"
                  : `Approx. ${formatMoney(
                      quote.recipientEstimatedAmountMinor,
                      quote.recipientEstimatedCurrency
                    )}`}
              </strong>
              <p className="helper-text muted">
                {quote.fxRateLocked
                  ? `Using approx. locked allowance conversion rate 1 USD = ${Number(
                      quote.fxRateLocked
                    ).toFixed(4)} ${quote.recipientEstimatedCurrency}.`
                  : "Local estimate will appear after this corridor is configured."}
              </p>
            </div>
          </div>
          {showFeeInfo ? (
            <div className="helper-block">
              <p>
                Banxito fee ranges:
              </p>
              <p>Up to $99: 9%</p>
              <p>$100-$249: 8%</p>
              <p>$250-$499: 7%</p>
              <p>$500+: 6%</p>
            </div>
          ) : null}
          {selectedRecipient ? (
            <div className="helper-block">
              <p>
                Recipient: <strong>{selectedRecipient.fullName}</strong>
              </p>
              <p>
                Country: <strong>{getCountryLabel(selectedRecipient.countryCode)}</strong>
                {" · "}
                Currency: <strong>{getRecipientCurrency(selectedRecipient.countryCode)}</strong>
              </p>
              <p>
                Pricing band: <strong>{quote.pricingBandLabel}</strong>
              </p>
            </div>
          ) : null}
          <button className="primary-button">Continue allowance purchase</button>
        </form>
      </section>

      {showCheckout ? (
        <section className="surface-card">
          <div className="section-head">
            <div>
              <p className="eyebrow">Confirmation summary</p>
              <h2>Review and complete payment</h2>
            </div>
          </div>
          <div className="checkout-recap">
            <DetailRow
              label="Allowance amount"
              value={formatMoney(paymentState.fundingAmountMinor, "USD")}
            />
            <DetailRow
              label="Banxito fee"
              value={formatMoney(paymentState.sponsorFeeMinor, "USD")}
            />
            <DetailRow
              label="Total charge"
              value={formatMoney(paymentState.totalChargeMinor, "USD")}
            />
            <DetailRow
              label="Recipient estimate"
              value={
                paymentState.recipientEstimatedAmountMinor == null
                  ? "Not available yet"
                  : `Approx. ${formatMoney(
                      paymentState.recipientEstimatedAmountMinor,
                      paymentState.recipientEstimatedCurrency || "USD"
                    )}`
              }
            />
            <DetailRow
              label="Allowance FX snapshot"
              value={
                paymentState.fxRateLocked
                  ? `1 USD = ${Number(paymentState.fxRateLocked).toFixed(4)} ${
                      paymentState.recipientEstimatedCurrency || "local currency"
                    }`
                  : "Not configured for this corridor"
              }
            />
          </div>
          {!checkoutReady ? (
            <EmptyState
              title="Payment setup missing"
              message="Add the Banxito payment public key to load the secure payment form."
            />
          ) : (
            <Elements
              stripe={stripePromise}
              options={{
                clientSecret: paymentState.clientSecret,
                appearance: stripeElementAppearance
              }}
            >
              <CheckoutCard
                fundingAmountMinor={paymentState.fundingAmountMinor}
                sponsorFeeMinor={paymentState.sponsorFeeMinor}
                totalChargeMinor={paymentState.totalChargeMinor}
                onFundingConfirmed={onFundingConfirmed}
              />
            </Elements>
          )}
          <div className="helper-block">
            <p>
              Status: <strong>{getFriendlyStatusLabel(latestFund?.status ?? "created")}</strong>
            </p>
            {!quoteMatchesCheckout ? (
              <p>
                Update detected. Re-lock pricing if you changed recipient or amount after
                generating this checkout.
              </p>
            ) : null}
          </div>
        </section>
      ) : null}

      {showFundingSuccess ? (
        <section className="surface-card">
          <div className="section-head">
            <div>
              <p className="eyebrow">Allowance purchase complete</p>
              <h2>Banxito has created this service allowance</h2>
            </div>
          </div>
          <div className="checkout-recap">
            <DetailRow
              label="Recipient"
              value={selectedRecipient?.fullName ?? "Recipient allowance purchased"}
            />
            <DetailRow
              label="Allowance amount"
              value={formatMoney(paymentState.fundingAmountMinor, "USD")}
            />
            <DetailRow
              label="Total charge"
              value={formatMoney(paymentState.totalChargeMinor, "USD")}
            />
            <DetailRow
              label="Recipient account"
              value={
                paymentState.recipientEstimatedAmountMinor == null
                  ? "Updated"
                  : `Approx. ${formatMoney(
                      paymentState.recipientEstimatedAmountMinor,
                      paymentState.recipientEstimatedCurrency || "USD"
                    )}`
              }
            />
          </div>
          <div className="helper-block">
            <p>
              Status: <strong>{getFriendlyStatusLabel(latestFund?.status ?? "succeeded")}</strong>
            </p>
            <p>
              The card payment is complete. Your next step is to assign the purchased allowance across
              healthcare, groceries, school, or other Banxito categories below.
            </p>
          </div>
          <div className="action-row">
            <button
              className="ghost-button"
              type="button"
              onClick={() =>
                setFundingDraft((current) => ({
                  ...current,
                  amountUsd: ""
                }))
              }
            >
              Purchase another allowance
            </button>
          </div>
        </section>
      ) : null}

      <section className="surface-card">
        <div className="section-head">
          <div>
            <p className="eyebrow">Assign allowances</p>
            <h2>Assign category allowances</h2>
          </div>
        </div>
        <div className="helper-block">
          <p>
            Amount ready to assign: <strong>{formatMoney(allocatableAmountMinor, "USD")}</strong>
          </p>
          <p>
            Assigned so far: <strong>{formatMoney(allocatedDraftMinor, "USD")}</strong>
            {" · "}
            Left to assign: <strong>{formatMoney(Math.max(remainingAllocationMinor, 0), "USD")}</strong>
          </p>
          <p>Enter normal dollar amounts like 30.00 and 10.00. Banxito converts them automatically.</p>
        </div>
        <form className="stack-form" onSubmit={onAllocateFunds}>
          {allocationDraft.map((row, index) => (
            <div className="two-col" key={`${row.categoryCode}-${index}`}>
              <SelectField
                label="Category"
                value={row.categoryCode}
                onChange={(value) =>
                  setAllocationDraft((current) =>
                    current.map((item, itemIndex) =>
                      itemIndex === index ? { ...item, categoryCode: value } : item
                    )
                  )
                }
                options={fallbackCategoryOptions.map((option) => ({
                  value: option.code,
                  label: option.label
                }))}
              />
              <Field
                label="Amount in USD"
                value={row.amountUsd}
                onChange={(value) =>
                  setAllocationDraft((current) =>
                    current.map((item, itemIndex) =>
                      itemIndex === index ? { ...item, amountUsd: value } : item
                    )
                  )
                }
                placeholder="30.00"
              />
            </div>
          ))}
          <button
            className="ghost-button"
            type="button"
            onClick={() =>
              setAllocationDraft((current) => [
                ...current,
                { categoryCode: "education", amountUsd: "" }
              ])
            }
          >
            Add category
          </button>
          {!allocationMatchesFunding && isSucceeded ? (
            <p className="helper-text">
              Your category amounts need to add up to{" "}
              <strong>{formatMoney(allocatableAmountMinor, "USD")}</strong> before you can continue.
            </p>
          ) : null}
          <button
            className="primary-button"
            disabled={!isSucceeded || !allocationMatchesFunding}
          >
            {isSucceeded ? "Assign purchased allowance" : "Payment must succeed first"}
          </button>
        </form>
      </section>
    </div>
  );
}

function WalletTopOffRuleCard({ wallet, defaultPaymentMethod, onSave, onGoPaymentSettings }) {
  const { t } = useI18n();
  const existingRule = wallet.topOffRule ?? null;
  const [expanded, setExpanded] = useState(Boolean(existingRule));
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    triggerBalance: formatMinorAsUsdInput(existingRule?.triggerBalanceMinor ?? 0),
    topOffAmount: formatMinorAsUsdInput(existingRule?.topOffAmountMinor ?? 0) || "25.00",
    monthlyCap: formatMinorAsUsdInput(existingRule?.monthlyCapMinor ?? 0) || "100.00",
    maxTopOffsPerMonth: String(existingRule?.maxTopOffsPerMonth ?? 2),
    cooldownDays: String(existingRule?.cooldownDays ?? 7)
  });

  useEffect(() => {
    setForm({
      triggerBalance: formatMinorAsUsdInput(existingRule?.triggerBalanceMinor ?? 0),
      topOffAmount: formatMinorAsUsdInput(existingRule?.topOffAmountMinor ?? 0) || "25.00",
      monthlyCap: formatMinorAsUsdInput(existingRule?.monthlyCapMinor ?? 0) || "100.00",
      maxTopOffsPerMonth: String(existingRule?.maxTopOffsPerMonth ?? 2),
      cooldownDays: String(existingRule?.cooldownDays ?? 7)
    });
  }, [existingRule?.id, existingRule?.updatedAt]);

  const topOffAmountMinor = parseUsdToMinor(form.topOffAmount);
  const monthlyCapMinor = parseUsdToMinor(form.monthlyCap);
  const hasSavedPaymentMethod = Boolean(defaultPaymentMethod?.paymentMethodId);
  const settingsAreValid = topOffAmountMinor > 0 && monthlyCapMinor >= topOffAmountMinor;
  const canSaveTopOff = hasSavedPaymentMethod && settingsAreValid;
  const isActive = existingRule?.status === "active";
  const statusLabel = existingRule
    ? isActive
      ? t("Auto top-off on")
      : existingRule.status === "paused"
        ? t("Auto top-off paused")
        : t("Auto top-off off")
    : t("Auto top-off not set");

  async function saveRule(nextStatus = "active") {
    if (!hasSavedPaymentMethod) {
      return;
    }

    setSaving(true);
    try {
      const success = await onSave({
        walletId: wallet.recipientWalletId,
        rule: {
          paymentMethodId: defaultPaymentMethod.paymentMethodId,
          status: nextStatus,
          triggerBalanceMinor: parseUsdToMinor(form.triggerBalance),
          topOffAmountMinor: parseUsdToMinor(form.topOffAmount),
          topOffCurrency: "USD",
          maxTopOffsPerMonth: Math.max(Number.parseInt(form.maxTopOffsPerMonth, 10) || 1, 1),
          monthlyCapMinor: parseUsdToMinor(form.monthlyCap),
          cooldownDays: Math.max(Number.parseInt(form.cooldownDays, 10) || 0, 0)
        }
      });
      if (success) {
        setExpanded(nextStatus === "active");
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="top-off-card">
      <div className="entity-topline">
        <div>
          <strong>{t(wallet.categoryName)}</strong>
          <p className="meta-line">
            {t("Remaining allowance")} {formatMoney(wallet.balanceMinor, wallet.currency || "USD")}
          </p>
        </div>
        <span className={isActive ? "badge badge-active" : "badge badge-pending"}>
          {statusLabel}
        </span>
      </div>
      {existingRule ? (
        <p className="meta-line">
          {t("Adds")} {formatMoney(existingRule.topOffAmountMinor, existingRule.topOffCurrency || "USD")}{" "}
          {t("when this category drops below")}{" "}
          {formatMoney(existingRule.triggerBalanceMinor, wallet.currency || "USD")}.
        </p>
      ) : (
        <p className="meta-line">
          {t("Let Banxito remind or automatically increase this category allowance later when its remaining allowance gets low.")}
        </p>
      )}
      {!hasSavedPaymentMethod ? (
        <div className="helper-block top-off-payment-warning">
          <strong>{t("Save a card first")}</strong>
          <p>
            {t("Automatic top-off needs a saved payment method. Banxito stores only safe card metadata, not the full card number.")}
          </p>
          <button className="ghost-button" type="button" onClick={onGoPaymentSettings}>
            {t("Add saved card under Me")}
          </button>
        </div>
      ) : (
        <p className="helper-text muted">
          {t("Uses")} {formatPaymentMethodLabel(defaultPaymentMethod)}. {t("Banxito stores only safe card metadata.")}
        </p>
      )}
      <div className="action-row compact-actions">
        <button
          className="ghost-button"
          type="button"
          onClick={() => setExpanded((current) => !current)}
        >
          {expanded ? t("Hide settings") : existingRule ? t("Edit top-off") : t("Set auto top-off")}
        </button>
        {existingRule ? (
          <button
            className="ghost-button"
            type="button"
            disabled={saving || !canSaveTopOff}
            onClick={() => saveRule(isActive ? "paused" : "active")}
        >
            {saving ? t("Saving...") : isActive ? t("Pause") : t("Turn back on")}
          </button>
        ) : null}
      </div>
      {expanded ? (
        <div className="top-off-settings">
          <div className="two-col">
            <Field
              label={`${t("When")} ${t(wallet.categoryName)} ${t("drops below")} (${wallet.currency || t("local")})`}
              value={form.triggerBalance}
              onChange={(value) => setForm((current) => ({ ...current, triggerBalance: value }))}
              placeholder="200.00"
            />
            <Field
              label={t("Add this much (USD)")}
              value={form.topOffAmount}
              onChange={(value) => setForm((current) => ({ ...current, topOffAmount: value }))}
              placeholder="25.00"
            />
          </div>
          <div className="two-col">
            <Field
              label={t("Monthly safety limit (USD)")}
              value={form.monthlyCap}
              onChange={(value) => setForm((current) => ({ ...current, monthlyCap: value }))}
              placeholder="100.00"
            />
            <Field
              label={t("Max allowance increases per month")}
              value={form.maxTopOffsPerMonth}
              onChange={(value) =>
                setForm((current) => ({ ...current, maxTopOffsPerMonth: value }))
              }
              placeholder="2"
            />
          </div>
          <Field
            label={t("Wait at least this many days between allowance increases")}
            value={form.cooldownDays}
            onChange={(value) => setForm((current) => ({ ...current, cooldownDays: value }))}
            placeholder="7"
          />
          <button
            className="primary-button"
            type="button"
            disabled={saving || !canSaveTopOff}
            onClick={() => saveRule("active")}
          >
            {saving ? t("Saving top-off...") : t("Enable automatic top-off")}
          </button>
          {!hasSavedPaymentMethod ? (
            <p className="helper-text muted">
              {t("Add a saved card under Me before turning this on.")}
            </p>
          ) : null}
          {!settingsAreValid ? (
            <p className="helper-text error-text">
              {t("The monthly safety limit must be at least as much as one top-off.")}
            </p>
          ) : null}
          <p className="helper-text muted">
            {t("Banxito checks this category automatically and uses your saved card only when the remaining allowance drops below your limit.")}
          </p>
        </div>
      ) : null}
    </div>
  );
}

function FundTabV2({
  visible,
  recipients,
  fundingServiceActivity,
  fundingDraft,
  setFundingDraft,
  onResetFundingIntent,
  onCreateIntent,
  paymentState,
  latestFund,
  onFundingConfirmed,
  allocationDraft,
  setAllocationDraft,
  corridors,
  categoriesByCorridor,
  onAllocateFunds,
  onReallocateCurrentFunding,
  defaultPaymentMethod,
  onSaveWalletTopOffRule,
  onGoPaymentSettings
}) {
  if (!visible) {
    return null;
  }

  const { t } = useI18n();
  const [showFeeInfo, setShowFeeInfo] = useState(false);
  const [fundViewTab, setFundViewTab] = useState("current");
  const [fundingMode, setFundingMode] = useState("new");
  const [splitMode, setSplitMode] = useState("adjust");
  const [showCareContext, setShowCareContext] = useState(false);
  const [reallocationDraft, setReallocationDraft] = useState([]);
  const [reallocating, setReallocating] = useState(false);
  const recipientGroups = useMemo(
    () => buildRecipientGroups(recipients, fundingServiceActivity),
    [recipients, fundingServiceActivity]
  );
  const selectedRecipient =
    recipientGroups.find(
      (recipient) =>
        recipient.primaryRecipientId === fundingDraft.recipientId ||
        recipient.recipientIds.includes(fundingDraft.recipientId)
    ) ?? null;
  const selectedRecipientCategoryOptions = getCategoryOptionsForCountry(
    selectedRecipient?.countryCode,
    categoriesByCorridor
  );
  const defaultCategoryCode = selectedRecipientCategoryOptions[0]?.code ?? "";
  const hasCategoryOptions = selectedRecipientCategoryOptions.length > 0;
  const selectedRecipientAllowances = selectedRecipient?.wallets ?? [];
  const selectedRecipientBalanceMinor = Number(selectedRecipient?.currentBalanceMinor ?? 0);
  const hasCurrentBalances = selectedRecipientBalanceMinor > 0;
  const recipientFundingHistory = (selectedRecipient?.fundingHistory ?? []).filter(
    (transaction) => !shouldHideTransactionByDefault(transaction)
  );
  const hiddenRecipientServiceActivity = (selectedRecipient?.fundingHistory ?? []).filter(
    (transaction) => shouldHideTransactionByDefault(transaction)
  );
  const successfulFundingHistory = recipientFundingHistory.filter(
    (transaction) => transaction.status === "succeeded"
  );
  const latestRecipientFunding = selectedRecipient?.latestSuccessfulFunding ?? null;
  const hasUnallocatedSuccessfulFunding =
    !hasCurrentBalances &&
    latestRecipientFunding?.status === "succeeded" &&
    Number(latestRecipientFunding.amountMinor ?? 0) > 0;
  const selectedRecipientCurrency =
    selectedRecipientAllowances[0]?.currency || getRecipientCurrency(selectedRecipient?.countryCode, corridors);
  const currentFundingServiceActivity = hasCurrentBalances || hasUnallocatedSuccessfulFunding
    ? successfulFundingHistory.slice(0, 1)
    : [];
  const pastFundingServiceActivity = hasCurrentBalances || hasUnallocatedSuccessfulFunding
    ? successfulFundingHistory.slice(1)
    : successfulFundingHistory;
  const quote = calculateFundingQuote({
    amountUsd: fundingDraft.amountUsd,
    recipientCountryCode: selectedRecipient?.countryCode
  });
  const showCheckout = Boolean(paymentState.clientSecret);
  const checkoutReady = Boolean(stripePromise && config.stripePublishableKey);
  const isSucceeded = latestFund?.status === "succeeded";
  const showFundingSuccess =
    Boolean(paymentState.fundingTransactionId) &&
    !showCheckout &&
    isSucceeded;
  const allocatedDraftMinor = allocationDraft.reduce(
    (sum, row) => sum + parseUsdToMinor(row.amountUsd),
    0
  );
  const unallocatedFundingAmountMinor = hasUnallocatedSuccessfulFunding
    ? Number(latestRecipientFunding.amountMinor ?? 0)
    : 0;
  const unallocatedFundingMatchesDraft =
    unallocatedFundingAmountMinor > 0 && allocatedDraftMinor === unallocatedFundingAmountMinor;
  const quoteMatchesCheckout =
    paymentState.fundingAmountMinor > 0 &&
    paymentState.fundingAmountMinor === quote.fundingAmountMinor &&
    paymentState.recipientId === fundingDraft.recipientId;
  const hasCareContext =
    Boolean(fundingDraft.supportPurpose?.trim()) || Boolean(fundingDraft.recipientNote?.trim());
  const reallocationDraftTotalMinor = reallocationDraft.reduce(
    (sum, row) => sum + parseUsdToMinor(row.amountLocal),
    0
  );
  const reallocationMatchesBalance =
    hasCurrentBalances &&
    reallocationDraft.length > 0 &&
    reallocationDraftTotalMinor === selectedRecipientBalanceMinor;

  useEffect(() => {
    if (
      selectedRecipient &&
      fundingDraft.recipientId &&
      fundingDraft.recipientId !== selectedRecipient.primaryRecipientId &&
      selectedRecipient.recipientIds.includes(fundingDraft.recipientId)
    ) {
      setFundingDraft((current) => ({
        ...current,
        recipientId: selectedRecipient.primaryRecipientId
      }));
    }
  }, [
    fundingDraft.recipientId,
    selectedRecipient?.primaryRecipientId,
    selectedRecipient?.recipientIds,
    setFundingDraft
  ]);

  useEffect(() => {
    if (!selectedRecipient) {
      setFundViewTab("current");
      setFundingMode("new");
      setSplitMode("adjust");
      setReallocationDraft([]);
      setAllocationDraft(buildDefaultAllocationDraft(defaultCategoryCode));
      return;
    }

    setFundViewTab("current");
    setReallocationDraft(buildReallocationDraftFromAllowances(selectedRecipientAllowances));
    if (hasUnallocatedSuccessfulFunding) {
      setFundingMode("allocate");
      setSplitMode("adjust");
      setAllocationDraft(buildDefaultAllocationDraft(defaultCategoryCode));
    } else if (hasCurrentBalances) {
      setFundingMode("reallocate");
      setSplitMode("keep");
      setAllocationDraft(
        buildAllocationDraftFromAllowances(selectedRecipientAllowances, quote.fundingAmountMinor)
      );
    } else {
      setFundingMode("new");
      setSplitMode("adjust");
      setAllocationDraft(buildDefaultAllocationDraft(defaultCategoryCode));
    }
  }, [
    selectedRecipient?.recipientId,
    selectedRecipient?.countryCode,
    defaultCategoryCode,
    hasUnallocatedSuccessfulFunding
  ]);

  useEffect(() => {
    if (!selectedRecipient) {
      return;
    }

    if (fundingMode === "topup" && splitMode === "keep" && hasCurrentBalances) {
      setAllocationDraft(
        buildAllocationDraftFromAllowances(selectedRecipientAllowances, quote.fundingAmountMinor)
      );
    }
  }, [
    fundingMode,
    splitMode,
    fundingDraft.amountUsd,
    selectedRecipient?.recipientId,
    hasCurrentBalances,
    quote.fundingAmountMinor
  ]);

  function updateFundingDraft(patch) {
    onResetFundingIntent();
    setFundingDraft((current) => ({
      ...current,
      ...patch
    }));
  }

  function chooseFundingMode(nextMode) {
    onResetFundingIntent();
    setFundViewTab("current");
    if (nextMode === "reallocate" && hasCurrentBalances) {
      setFundingMode("reallocate");
      setReallocationDraft(buildReallocationDraftFromAllowances(selectedRecipientAllowances));
      return;
    }

    if (nextMode === "topup" && hasCurrentBalances) {
      setFundingMode("topup");
      setSplitMode("keep");
      setAllocationDraft(
        buildAllocationDraftFromAllowances(selectedRecipientAllowances, quote.fundingAmountMinor)
      );
      return;
    }

    setFundingMode("new");
    setSplitMode("adjust");
    setAllocationDraft(buildDefaultAllocationDraft(defaultCategoryCode));
  }

  function chooseSplitMode(nextMode) {
    onResetFundingIntent();
    setSplitMode(nextMode);
    if (nextMode === "keep") {
      setAllocationDraft(
        buildAllocationDraftFromAllowances(selectedRecipientAllowances, quote.fundingAmountMinor)
      );
    }
  }

  async function handleReallocateSubmit(event) {
    event.preventDefault();
    if (!selectedRecipient) {
      return;
    }

    setReallocating(true);
    try {
      await onReallocateCurrentFunding({
        recipientId: selectedRecipient.primaryRecipientId,
        currency: selectedRecipientCurrency,
        targetAllocations: reallocationDraft
      });
    } finally {
      setReallocating(false);
    }
  }

  return (
    <div className="screen-stack">
      <section className="surface-card">
        <div className="section-head">
          <div>
              <p className="eyebrow">{t("Purchase allowance")}</p>
              <h2>{t("Choose a recipient and manage their service allowances")}</h2>
          </div>
        </div>
        <SelectField
          label="Recipient"
          value={fundingDraft.recipientId}
          onChange={(value) => updateFundingDraft({ recipientId: value })}
          placeholder={t("Choose recipient")}
          options={recipientGroups.map((recipient) => ({
            value: recipient.primaryRecipientId,
            label: `${recipient.fullName} · ${getCountryLabel(recipient.countryCode, corridors)}${
              recipient.relationshipLabel ? ` · ${recipient.relationshipLabel}` : ""
            }`
          }))}
        />
        {selectedRecipient ? (
          <div className="helper-block">
            <p>
                {t("Recipient")}: <strong>{selectedRecipient.fullName}</strong>
            </p>
            <p>
                {t("Country")}: <strong>{t(getCountryLabel(selectedRecipient.countryCode, corridors))}</strong>
              {" · "}
                {t("Currency")}: <strong>{selectedRecipientCurrency}</strong>
            </p>
            <p>
              {t("Current available allowance")}:{" "}
              <strong>{formatMoney(selectedRecipientBalanceMinor, selectedRecipientCurrency)}</strong>
            </p>
            {latestRecipientFunding ? (
              <p>
                {t("Last allowance purchase")}:{" "}
                <strong>{formatMoney(latestRecipientFunding.amountMinor, "USD")}</strong>
                {" · "}
                {latestRecipientFunding.fundedAt
                  ? new Date(latestRecipientFunding.fundedAt).toLocaleDateString()
                  : new Date(latestRecipientFunding.createdAt).toLocaleDateString()}
              </p>
            ) : (
              <p>{t("No service allowance purchase yet for this recipient.")}</p>
            )}
          </div>
        ) : (
          <EmptyState
            title="Choose a recipient first"
            message="Banxito will then show active allowances, past allowances, and the next purchase options for that family member."
          />
        )}
      </section>

      {selectedRecipient ? (
        <section className="surface-card">
          <div className="section-head">
            <div>
              <p className="eyebrow">{t("Allowance views")}</p>
              <h2>{t("Current and past allowances")}</h2>
            </div>
          </div>
          <div className="action-row">
            <button
              className={fundViewTab === "current" ? "mode-pill active" : "mode-pill"}
              type="button"
              onClick={() => setFundViewTab("current")}
            >
              {t("Current allowances")}
            </button>
            <button
              className={fundViewTab === "past" ? "mode-pill active" : "mode-pill"}
              type="button"
              onClick={() => setFundViewTab("past")}
            >
              {t("Past allowances")}
            </button>
          </div>

          {fundViewTab === "current" ? (
            <>
              {currentFundingServiceActivity.length > 0 ? (
                <div className="history-card">
                  <div className="entity-topline">
                    <strong>{t("Active allowance")}</strong>
                    <span className="badge badge-active">{t("Available now")}</span>
                  </div>
                  <p className="meta-line">
                    {t("Remaining allowance")}{" "}
                    <strong>{formatMoney(selectedRecipientBalanceMinor, selectedRecipientCurrency)}</strong>
                  </p>
                  <p className="meta-line">
                    {t("Started from")}{" "}
                    <strong>
                      {formatMoney(
                        currentFundingServiceActivity[0].amountMinor,
                        currentFundingServiceActivity[0].currency
                      )}
                    </strong>
                    {" · "}
                    {currentFundingServiceActivity[0].fundedAt
                      ? `Purchased ${new Date(currentFundingServiceActivity[0].fundedAt).toLocaleDateString()}`
                      : `Created ${new Date(currentFundingServiceActivity[0].createdAt).toLocaleDateString()}`}
                  </p>
                </div>
              ) : null}

              {selectedRecipientAllowances.length === 0 ? (
                <EmptyState
                  title={
                    hasUnallocatedSuccessfulFunding
                      ? "Allowance purchase succeeded and needs category assignment"
                      : latestRecipientFunding
                      ? "Allowance purchase exists, but there are no active category allowances yet"
                      : "No current category allowances"
                  }
                  message={
                    hasUnallocatedSuccessfulFunding
                      ? "Assign this successful allowance purchase across the recipient's active categories below. This does not charge the sponsor again."
                      : latestRecipientFunding
                      ? "Banxito found successful allowance purchase history for this recipient, but no live category allowances are attached yet."
                      : "This recipient does not have active Banxito category allowances yet. Purchase a new allowance to create them."
                  }
                />
              ) : (
                <div className="wallet-grid">
                  {selectedRecipientAllowances.map((wallet) => (
                    <div className="wallet-chip" key={wallet.recipientWalletId}>
                      <span>{t(wallet.categoryName)}</span>
                      <strong>{formatMoney(wallet.balanceMinor, wallet.currency || "USD")}</strong>
                    </div>
                  ))}
                </div>
              )}

              {selectedRecipientAllowances.length > 0 ? (
                <div className="top-off-rule-list">
                  {selectedRecipientAllowances.map((wallet) => (
                    <WalletTopOffRuleCard
                      key={`top-off-${wallet.recipientWalletId}`}
                      wallet={wallet}
                      defaultPaymentMethod={defaultPaymentMethod}
                      onSave={onSaveWalletTopOffRule}
                      onGoPaymentSettings={onGoPaymentSettings}
                    />
                  ))}
                </div>
              ) : null}

              <div className="action-row">
                {hasUnallocatedSuccessfulFunding ? (
                  <button
                    className={fundingMode === "allocate" ? "mode-pill active" : "mode-pill"}
                    type="button"
                    onClick={() => {
                      onResetFundingIntent();
                      setFundingMode("allocate");
                      setAllocationDraft(buildDefaultAllocationDraft(defaultCategoryCode));
                    }}
                  >
                    {t("Assign purchased allowance")}
                  </button>
                ) : null}
                <button
                  className={fundingMode === "reallocate" ? "mode-pill active" : "mode-pill"}
                  type="button"
                  disabled={!hasCurrentBalances}
                  onClick={() => chooseFundingMode("reallocate")}
                >
                  {t("Reassign current allowance")}
                </button>
                <button
                  className={fundingMode === "topup" ? "mode-pill active" : "mode-pill"}
                  type="button"
                  disabled={!hasCurrentBalances}
                  onClick={() => chooseFundingMode("topup")}
                >
                  {t("Add allowance")}
                </button>
                <button
                  className={fundingMode === "new" ? "mode-pill active" : "mode-pill"}
                  type="button"
                  onClick={() => chooseFundingMode("new")}
                >
                  {t("Purchase new allowance")}
                </button>
              </div>

              {fundingMode === "topup" && hasCurrentBalances ? (
                <div className="action-row">
                  <button
                    className={splitMode === "keep" ? "mode-pill active" : "mode-pill"}
                    type="button"
                    onClick={() => chooseSplitMode("keep")}
                  >
                    {t("Keep current split")}
                  </button>
                  <button
                    className={splitMode === "adjust" ? "mode-pill active" : "mode-pill"}
                    type="button"
                    onClick={() => chooseSplitMode("adjust")}
                  >
                    {t("Adjust split before paying")}
                  </button>
                </div>
              ) : null}

              {hasCurrentBalances && fundingMode === "reallocate" ? (
                <section className="surface-card">
                  <div className="section-head">
                    <div>
                      <p className="eyebrow">{t("Reassign current allowance")}</p>
                      <h2>{t("Adjust the active category allowance")}</h2>
                    </div>
                  </div>
                  <form className="stack-form" onSubmit={handleReallocateSubmit}>
                    {reallocationDraft.map((row, index) => (
                      <div className="two-col" key={`${row.categoryCode}-${index}`}>
                        <SelectField
                          label="Category"
                          value={row.categoryCode}
                          onChange={(value) =>
                            setReallocationDraft((current) =>
                              current.map((item, itemIndex) =>
                                itemIndex === index ? { ...item, categoryCode: value } : item
                              )
                            )
                          }
                          options={selectedRecipientCategoryOptions.map((option) => ({
                            value: option.code,
                            label: option.label
                          }))}
                        />
                        <Field
                          label={`Amount in ${selectedRecipientCurrency}`}
                          value={row.amountLocal}
                          onChange={(value) =>
                            setReallocationDraft((current) =>
                              current.map((item, itemIndex) =>
                                itemIndex === index ? { ...item, amountLocal: value } : item
                              )
                            )
                          }
                          placeholder="500.00"
                        />
                      </div>
                    ))}
                    <button
                      className="ghost-button"
                      type="button"
                      onClick={() =>
                        setReallocationDraft((current) => [
                          ...current,
                          { categoryCode: defaultCategoryCode, amountLocal: "" }
                        ])
                      }
                    >
                      {t("Add category")}
                    </button>
                    <div className="helper-block">
                      <p>
                        {t("Remaining allowance:")}{" "}
                        <strong>{formatMoney(selectedRecipientBalanceMinor, selectedRecipientCurrency)}</strong>
                      </p>
                      <p>
                        {t("Reallocated total:")}{" "}
                        <strong>{formatMoney(reallocationDraftTotalMinor, selectedRecipientCurrency)}</strong>
                      </p>
                    </div>
                    {!reallocationMatchesBalance ? (
                      <p className="helper-text">
                        The category amounts need to add up to{" "}
                        <strong>{formatMoney(selectedRecipientBalanceMinor, selectedRecipientCurrency)}</strong>{" "}
                        before you confirm the reallocation.
                      </p>
                    ) : null}
                    {!hasCategoryOptions ? (
                      <p className="helper-text error-text">
                        {t("No active categories are configured for this recipient corridor yet.")}
                      </p>
                    ) : null}
                    <button
                      className="primary-button"
                      disabled={!hasCategoryOptions || !reallocationMatchesBalance || reallocating}
                    >
                      {reallocating ? t("Saving...") : t("Confirm reallocation")}
                    </button>
                  </form>
                </section>
              ) : null}

              {hasUnallocatedSuccessfulFunding && fundingMode === "allocate" ? (
                <section className="surface-card">
                  <div className="section-head">
                    <div>
                      <p className="eyebrow">{t("Assign purchased allowance")}</p>
                      <h2>{t("Assign the successful allowance purchase to categories")}</h2>
                    </div>
                  </div>
                  <div className="helper-block">
                    <p>
                      {t("Amount ready to assign")}:{" "}
                      <strong>{formatMoney(unallocatedFundingAmountMinor, "USD")}</strong>
                    </p>
                    <p>
                      {t("Assigned so far")}:{" "}
                      <strong>{formatMoney(allocatedDraftMinor, "USD")}</strong>
                      {" · "}
                      {t("Left to assign")}:{" "}
                      <strong>
                        {formatMoney(
                          Math.max(unallocatedFundingAmountMinor - allocatedDraftMinor, 0),
                          "USD"
                        )}
                      </strong>
                    </p>
                    <p>
                      {t("This uses the successful allowance payment already received. It will not start a new charge.")}
                    </p>
                  </div>
                  <form
                    className="stack-form"
                    onSubmit={(event) =>
                      onAllocateFunds(event, {
                        fundingTransactionId: latestRecipientFunding.fundingTransactionId,
                        recipientId: latestRecipientFunding.recipientId
                      })
                    }
                  >
                    {allocationDraft.map((row, index) => (
                      <div className="two-col" key={`${row.categoryCode}-${index}`}>
                        <SelectField
                          label="Category"
                          value={row.categoryCode}
                          onChange={(value) =>
                            setAllocationDraft((current) =>
                              current.map((item, itemIndex) =>
                                itemIndex === index ? { ...item, categoryCode: value } : item
                              )
                            )
                          }
                          options={selectedRecipientCategoryOptions.map((option) => ({
                            value: option.code,
                            label: option.label
                          }))}
                        />
                        <Field
                          label="Amount in USD"
                          value={row.amountUsd}
                          onChange={(value) =>
                            setAllocationDraft((current) =>
                              current.map((item, itemIndex) =>
                                itemIndex === index ? { ...item, amountUsd: value } : item
                              )
                            )
                          }
                          placeholder="30.00"
                        />
                      </div>
                    ))}
                    <button
                      className="ghost-button"
                      type="button"
                      onClick={() =>
                        setAllocationDraft((current) => [
                          ...current,
                          { categoryCode: defaultCategoryCode, amountUsd: "" }
                        ])
                      }
                    >
                      {t("Add category")}
                    </button>
                    {!unallocatedFundingMatchesDraft ? (
                      <p className="helper-text">
                        {t("The category amounts need to add up to")}{" "}
                        <strong>{formatMoney(unallocatedFundingAmountMinor, "USD")}</strong>{" "}
                        {t("before you can continue.")}
                      </p>
                    ) : null}
                    {!hasCategoryOptions ? (
                      <p className="helper-text error-text">
                        {t("No active categories are configured for this recipient corridor yet.")}
                      </p>
                    ) : null}
                    <button
                      className="primary-button"
                      disabled={
                        !hasCategoryOptions ||
                        !latestRecipientFunding?.fundingTransactionId ||
                        !unallocatedFundingMatchesDraft
                      }
                    >
                      {t("Assign purchased allowance")}
                    </button>
                  </form>
                </section>
              ) : null}
            </>
          ) : (
            <>
              {pastFundingServiceActivity.length === 0 ? (
                <EmptyState
                  title={
                    hiddenRecipientServiceActivity.length > 0
                      ? "Allowance purchase was started, but not completed yet"
                      : "No past allowances yet"
                  }
                  message={
                    hiddenRecipientServiceActivity.length > 0
                      ? `${hiddenRecipientServiceActivity.length} payment attempt${
                          hiddenRecipientServiceActivity.length === 1 ? "" : "s"
                        } ${hiddenRecipientServiceActivity.length === 1 ? "was" : "were"} started for this recipient, but payment was not completed.`
                      : "Once an allowance has been fully used, Banxito will keep it here as read-only history."
                  }
                />
              ) : (
                <div className="activity-list">
                  {pastFundingServiceActivity.map((transaction) => (
                    <div className="history-card activity-card" key={transaction.fundingTransactionId}>
                      <div className="entity-topline">
                        <strong>{formatMoney(transaction.amountMinor, transaction.currency)}</strong>
                        <span className="badge badge-completed">{t("Read only")}</span>
                      </div>
                      <p className="meta-line">
                        {t("Total charge")} {formatMoney(transaction.totalChargeMinor, transaction.currency)}
                      </p>
                      <p className="meta-line">{t("This allowance has been fully used and cannot be changed.")}</p>
                      <p className="meta-line">
                        {transaction.fundedAt
                          ? `Purchased ${new Date(transaction.fundedAt).toLocaleString()}`
                          : `Created ${new Date(transaction.createdAt).toLocaleString()}`}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </section>
      ) : null}

      {selectedRecipient &&
      fundViewTab === "current" &&
      fundingMode !== "reallocate" &&
      fundingMode !== "allocate" ? (
        <section className="surface-card">
          <div className="section-head">
            <div>
              <p className="eyebrow">{t("Plan allowance purchase")}</p>
              <h2>
                {fundingMode === "topup"
                  ? t("Add allowance to the active fund")
                  : t("Purchase a new service allowance")}
              </h2>
            </div>
          </div>
          <form className="stack-form" onSubmit={onCreateIntent}>
            <Field
              label="Allowance amount in USD"
              value={fundingDraft.amountUsd}
              onChange={(value) => updateFundingDraft({ amountUsd: value })}
              placeholder="40.00"
            />
            <div className={`support-purpose-card ${showCareContext ? "is-expanded" : "is-compact"}`}>
              <div className="support-purpose-summary">
                <div>
                  <p className="eyebrow">{t("Optional")}</p>
                  <h3>{t("Add a purpose or note")}</h3>
                  <p>{t("Add this only if it helps. You can continue without it.")}</p>
                </div>
                <button
                  className="ghost-button"
                  type="button"
                  onClick={() => setShowCareContext((current) => !current)}
                >
                  {showCareContext
                    ? t("Hide purpose and note")
                    : t(hasCareContext ? "Edit purpose or note" : "Add")}
                </button>
              </div>
              {!showCareContext && hasCareContext ? (
                <div className="support-purpose-pills">
                  {fundingDraft.supportPurpose?.trim() ? <span>{t("Purpose added")}</span> : null}
                  {fundingDraft.recipientNote?.trim() ? <span>{t("Note added")}</span> : null}
                </div>
              ) : null}
              {showCareContext ? (
                <div className="support-purpose-fields">
                  <Field
                    label="What is this support for?"
                    value={fundingDraft.supportPurpose ?? ""}
                    onChange={(value) => updateFundingDraft({ supportPurpose: value })}
                    placeholder="Mom's monthly medicine"
                    helper="Tell Banxito the purpose, like monthly medicine or school supplies."
                  />
                  <Field
                    label="Care note for recipient"
                    value={fundingDraft.recipientNote ?? ""}
                    onChange={(value) => updateFundingDraft({ recipientNote: value })}
                    placeholder="Glad this helped. Love you."
                    helper="Draft a gentle note that will be presented to the recipient."
                  />
                </div>
              ) : null}
            </div>
            <div className="helper-block">
              <p>
                {t("Allowance choice")}:{" "}
                <strong>{fundingMode === "topup" ? t("Add allowance") : t("Purchase new allowance")}</strong>
              </p>
              <p>
                {t("Category split")}:{" "}
                <strong>
                  {fundingMode === "topup" && splitMode === "keep"
                    ? t("Keep current category split")
                    : t("Adjust before paying")}
                </strong>
              </p>
              <p>
                {t("Pricing band")}: <strong>{quote.pricingBandLabel}</strong>
              </p>
            </div>
            <div className="fund-quote-grid">
              <div className="quote-panel quote-panel-accent">
                <p className="quote-label">{t("Recipient allowance")}</p>
                <strong>{formatMoney(quote.fundingAmountMinor, "USD")}</strong>
                <p className="helper-text muted">
                  {t("This is the service allowance Banxito will make available to the recipient.")}
                </p>
              </div>
              <div className="quote-panel">
                <div className="quote-label-row">
                  <p className="quote-label">{t("Banxito fee")}</p>
                  <button
                    className="info-chip"
                    type="button"
                    onClick={() => setShowFeeInfo((current) => !current)}
                    aria-expanded={showFeeInfo}
                    aria-label="Show Banxito fee ranges"
                  >
                    i
                  </button>
                </div>
                <strong>{formatMoney(quote.sponsorFeeMinor, "USD")}</strong>
                <p className="helper-text muted">
                  {Math.round(quote.feeRate * 100)}% includes payment processing,
                  cross-border handling, and exchange-rate variation.
                </p>
              </div>
              <div className="quote-panel">
                <p className="quote-label">{t("Total sponsor charge")}</p>
                <strong>{formatMoney(quote.totalChargeMinor, "USD")}</strong>
                <p className="helper-text muted">
                  {t("Your card is charged once for the service allowance plus the Banxito fee.")}
                </p>
              </div>
              <div className="quote-panel">
                <p className="quote-label">{t("Recipient sees")}</p>
                <strong>
                  {quote.recipientEstimatedAmountMinor == null
                    ? t("Estimate pending")
                    : `Approx. ${formatMoney(
                        quote.recipientEstimatedAmountMinor,
                        quote.recipientEstimatedCurrency
                      )}`}
                </strong>
                <p className="helper-text muted">
                  {quote.fxRateLocked
                    ? `Using approx. locked allowance conversion rate 1 USD = ${Number(
                        quote.fxRateLocked
                      ).toFixed(4)} ${quote.recipientEstimatedCurrency}.`
                    : t("Local estimate will appear after this corridor is configured.")}
                </p>
              </div>
            </div>
            {showFeeInfo ? (
              <div className="helper-block">
                <p>{t("Banxito fee ranges:")}</p>
                <p>{t("Up to $99")}: 9%</p>
                <p>$100-$249: 8%</p>
                <p>$250-$499: 7%</p>
                <p>$500+: 6%</p>
              </div>
            ) : null}
            <div className="helper-block">
              <p>{t("Planned category split for this allowance:")}</p>
              <p>
                {t("Enter normal dollar amounts like 30.00 and 10.00. Banxito will apply this split automatically after payment succeeds.")}
              </p>
              {fundingMode === "topup" && hasCurrentBalances ? (
                <p>
                  {splitMode === "keep"
                    ? t("Banxito will preserve the current category proportions for this allowance increase.")
                    : t("Banxito will use the category amounts you set below for this allowance increase.")}
                </p>
              ) : null}
            </div>
            {allocationDraft.map((row, index) => (
              <div className="two-col" key={`${row.categoryCode}-${index}`}>
                <SelectField
                  label="Category"
                  value={row.categoryCode}
                  onChange={(value) =>
                    setAllocationDraft((current) =>
                      current.map((item, itemIndex) =>
                        itemIndex === index ? { ...item, categoryCode: value } : item
                      )
                    )
                  }
                  options={selectedRecipientCategoryOptions.map((option) => ({
                    value: option.code,
                    label: option.label
                  }))}
                />
                <Field
                  label="Amount in USD"
                  value={row.amountUsd}
                  onChange={(value) =>
                    setAllocationDraft((current) =>
                      current.map((item, itemIndex) =>
                        itemIndex === index ? { ...item, amountUsd: value } : item
                      )
                    )
                  }
                  placeholder="30.00"
                />
              </div>
            ))}
            <button
              className="ghost-button"
              type="button"
              onClick={() =>
                setAllocationDraft((current) => [
                  ...current,
                  { categoryCode: defaultCategoryCode, amountUsd: "" }
                ])
              }
            >
              {t("Add category")}
            </button>
            <div className="helper-block">
              <p>
                {t("Planned to assign")}: <strong>{formatMoney(allocatedDraftMinor, "USD")}</strong>
                {" · "}
                {t("Left to assign")}:{" "}
                <strong>
                  {formatMoney(Math.max(quote.fundingAmountMinor - allocatedDraftMinor, 0), "USD")}
                </strong>
              </p>
            </div>
            {quote.fundingAmountMinor > 0 && allocatedDraftMinor !== quote.fundingAmountMinor ? (
              <p className="helper-text">
                {t("The planned category amounts need to add up to")}{" "}
                <strong>{formatMoney(quote.fundingAmountMinor, "USD")}</strong> {t("before you can continue.")}
              </p>
            ) : null}
            {!hasCategoryOptions ? (
              <p className="helper-text error-text">
                {t("No active categories are configured for this recipient corridor yet.")}
              </p>
            ) : null}
            <button
              className="primary-button"
              disabled={
                !hasCategoryOptions ||
                quote.fundingAmountMinor <= 0 ||
                allocatedDraftMinor !== quote.fundingAmountMinor
              }
            >
              {t("Continue allowance purchase")}
            </button>
          </form>
        </section>
      ) : null}

      {showCheckout ? (
        <section className="surface-card">
          <div className="section-head">
            <div>
              <p className="eyebrow">{t("Confirmation summary")}</p>
              <h2>{t("Review and complete payment")}</h2>
            </div>
          </div>
          <div className="checkout-recap">
            <DetailRow
              label="Allowance amount"
              value={formatMoney(paymentState.fundingAmountMinor, "USD")}
            />
            <DetailRow
              label="Banxito fee"
              value={formatMoney(paymentState.sponsorFeeMinor, "USD")}
            />
            <DetailRow
              label="Total charge"
              value={formatMoney(paymentState.totalChargeMinor, "USD")}
            />
            <DetailRow
              label="Recipient estimate"
              value={
                paymentState.recipientEstimatedAmountMinor == null
                  ? t("Not available yet")
                  : `Approx. ${formatMoney(
                      paymentState.recipientEstimatedAmountMinor,
                      paymentState.recipientEstimatedCurrency || "USD"
                    )}`
              }
            />
            <DetailRow
              label="Allowance FX snapshot"
              value={
                paymentState.fxRateLocked
                  ? `1 USD = ${Number(paymentState.fxRateLocked).toFixed(4)} ${
                      paymentState.recipientEstimatedCurrency || "local currency"
                    }`
                  : "Not configured for this corridor"
              }
            />
          </div>
          {!checkoutReady ? (
            <EmptyState
              title="Payment setup missing"
              message="Add the Banxito payment public key to load the secure payment form."
            />
          ) : (
            <Elements
              stripe={stripePromise}
              options={{
                clientSecret: paymentState.clientSecret,
                appearance: stripeElementAppearance
              }}
            >
              <CheckoutCard
                fundingAmountMinor={paymentState.fundingAmountMinor}
                sponsorFeeMinor={paymentState.sponsorFeeMinor}
                totalChargeMinor={paymentState.totalChargeMinor}
                onFundingConfirmed={onFundingConfirmed}
              />
            </Elements>
          )}
          <div className="helper-block">
            <p>
              Use the secure payment form below to continue with the card details you want to use
              for this allowance purchase.
            </p>
            <p>
              Status: <strong>{getFriendlyStatusLabel(latestFund?.status ?? "created")}</strong>
            </p>
            {!quoteMatchesCheckout ? (
              <p>
                Update detected. Re-lock pricing if you changed recipient or amount after
                generating this checkout.
              </p>
            ) : null}
          </div>
        </section>
      ) : null}

      {showFundingSuccess ? (
        <section className="surface-card">
          <div className="section-head">
            <div>
              <p className="eyebrow">Allowance purchase complete</p>
              <h2>Recipient allowances are updated</h2>
            </div>
          </div>
          <div className="checkout-recap">
            <DetailRow
              label="Recipient"
              value={selectedRecipient?.fullName ?? "Recipient allowance purchased"}
            />
            <DetailRow
              label="Allowance amount"
              value={formatMoney(paymentState.fundingAmountMinor, "USD")}
            />
            <DetailRow
              label="Total charge"
              value={formatMoney(paymentState.totalChargeMinor, "USD")}
            />
            <DetailRow
              label="Recipient account"
              value={
                paymentState.recipientEstimatedAmountMinor == null
                  ? "Updated"
                  : `Approx. ${formatMoney(
                      paymentState.recipientEstimatedAmountMinor,
                      paymentState.recipientEstimatedCurrency || "USD"
                    )}`
              }
            />
          </div>
          <div className="helper-block">
            <p>
              Status: <strong>{getFriendlyStatusLabel(latestFund?.status ?? "succeeded")}</strong>
            </p>
            <p>
              The purchase is complete and Banxito applied the planned category split automatically.
              The current allowances above now reflect the updated service access.
            </p>
          </div>
          <div className="action-row">
            <button
              className="ghost-button"
              type="button"
              onClick={() => updateFundingDraft({ amountUsd: "" })}
            >
              Purchase another allowance
            </button>
          </div>
        </section>
      ) : null}
    </div>
  );
}

function CheckoutCard({
  fundingAmountMinor,
  sponsorFeeMinor,
  totalChargeMinor,
  onFundingConfirmed
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [status, setStatus] = useState("");

  async function handleSubmit(event) {
    event.preventDefault();
    if (!stripe || !elements) {
      return;
    }

    setSubmitting(true);
    setStatus("");

    try {
      const submitResult = await elements.submit();
      if (submitResult.error) {
        throw new Error(submitResult.error.message);
      }

      const result = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: window.location.href
        },
        redirect: "if_required"
      });

      if (result.error) {
        throw new Error(result.error.message);
      }

      setStatus("Payment submitted. Syncing Banxito service allowance records...");
      const fundingSucceeded = await onFundingConfirmed();
      if (fundingSucceeded) {
        setCompleted(true);
        setStatus("Allowance purchase succeeded. Recipient service access is being updated.");
      }
    } catch (error) {
      setStatus(error.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form className="stack-form" onSubmit={handleSubmit}>
      <div className="checkout-summary checkout-summary-stack">
        <div className="checkout-line">
        <span>Recipient allowance</span>
          <strong>{formatMoney(fundingAmountMinor, "USD")}</strong>
        </div>
        <div className="checkout-line">
          <span>Banxito fee</span>
          <strong>{formatMoney(sponsorFeeMinor, "USD")}</strong>
        </div>
        <div className="checkout-line checkout-line-total">
          <span>Total charge</span>
          <strong>{formatMoney(totalChargeMinor, "USD")}</strong>
        </div>
      </div>
      <div className="payment-shell">
        <PaymentElement />
      </div>
      {completed ? null : (
        <button className="primary-button" disabled={!stripe || submitting}>
          {submitting ? "Processing..." : "Complete allowance purchase"}
        </button>
      )}
      {status ? <p className="helper-text">{status}</p> : null}
    </form>
  );
}

function ActivityTab({ visible, fundingServiceActivity, recipients }) {
  if (!visible) {
    return null;
  }

  const recipientMap = new Map(
    recipients.map((recipient) => [recipient.recipientId, recipient.fullName])
  );

  const [recipientFilter, setRecipientFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [periodFilter, setPeriodFilter] = useState("30d");

  const filteredServiceActivity = useMemo(() => {
    const now = Date.now();
    const periodStart =
      periodFilter === "7d"
        ? now - 7 * 24 * 60 * 60 * 1000
        : periodFilter === "30d"
          ? now - 30 * 24 * 60 * 60 * 1000
          : periodFilter === "90d"
            ? now - 90 * 24 * 60 * 60 * 1000
            : null;

    return fundingServiceActivity.filter((transaction) => {
      const eventTimestamp = new Date(transaction.fundedAt ?? transaction.createdAt).getTime();

      if (!statusFilter && shouldHideTransactionByDefault(transaction)) {
        return false;
      }

      if (recipientFilter && transaction.recipientId !== recipientFilter) {
        return false;
      }

      if (statusFilter && transaction.status !== statusFilter) {
        return false;
      }

      if (periodStart && eventTimestamp < periodStart) {
        return false;
      }

      return true;
    });
  }, [fundingServiceActivity, periodFilter, recipientFilter, statusFilter]);

  const succeededServiceActivity = filteredServiceActivity.filter(
    (transaction) => transaction.status === "succeeded"
  );
  const pendingServiceActivity = filteredServiceActivity.filter(
    (transaction) => transaction.status !== "succeeded"
  );
  const totalPurchasedMinor = succeededServiceActivity.reduce(
    (sum, transaction) => sum + Number(transaction.amountMinor ?? 0),
    0
  );
  const statusOptions = Array.from(
    new Set(fundingServiceActivity.map((transaction) => transaction.status).filter(Boolean))
  ).map((status) => ({
    value: status,
    label: getFriendlyStatusLabel(status)
  }));

  return (
    <section className="surface-card">
      <div className="section-head">
        <div>
          <p className="eyebrow">Activity & history</p>
          <h2>Allowance timeline</h2>
        </div>
      </div>
      {fundingServiceActivity.length === 0 ? (
        <EmptyState
          title="No allowance purchases yet"
          message="Every new allowance purchase, payment success, and category assignment will show up here."
        />
      ) : (
        fundingServiceActivity.map((transaction) => (
          <div className="history-card" key={transaction.fundingTransactionId}>
            <div className="entity-topline">
              <strong>
                {formatMoney(transaction.amountMinor, transaction.currency)}
              </strong>
                  <span className={`badge badge-${transaction.status}`}>
                    {getFriendlyStatusLabel(transaction.status)}
                  </span>
            </div>
            <p className="meta-line">
              {recipientMap.get(transaction.recipientId) ?? "Recipient"} ·{" "}
              {transaction.paymentProvider}
            </p>
            <p className="meta-line">
              Created {new Date(transaction.createdAt).toLocaleString()}
              {transaction.fundedAt
                ? ` · Purchased ${new Date(transaction.fundedAt).toLocaleString()}`
                : ""}
            </p>
          </div>
        ))
      )}
    </section>
  );
}

function EnhancedActivityTab({
  visible,
  fundingServiceActivity,
  recipients,
  careEvents,
  categoryUsage
}) {
  const [recipientFilter, setRecipientFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [periodFilter, setPeriodFilter] = useState("30d");
  const [categoryFilter, setCategoryFilter] = useState("");
  const { t } = useI18n();

  const recipientMap = new Map(
    recipients.map((recipient) => [recipient.recipientId, recipient.fullName])
  );

  const filteredServiceActivity = useMemo(() => {
    const now = Date.now();
    const periodStart =
      periodFilter === "7d"
        ? now - 7 * 24 * 60 * 60 * 1000
        : periodFilter === "30d"
          ? now - 30 * 24 * 60 * 60 * 1000
          : periodFilter === "90d"
            ? now - 90 * 24 * 60 * 60 * 1000
            : null;

    return fundingServiceActivity.filter((transaction) => {
      const eventTimestamp = new Date(transaction.fundedAt ?? transaction.createdAt).getTime();

      if (!statusFilter && shouldHideTransactionByDefault(transaction)) {
        return false;
      }

      if (recipientFilter && transaction.recipientId !== recipientFilter) {
        return false;
      }

      if (statusFilter && transaction.status !== statusFilter) {
        return false;
      }

      if (periodStart && eventTimestamp < periodStart) {
        return false;
      }

      return true;
    });
  }, [fundingServiceActivity, periodFilter, recipientFilter, statusFilter]);

  const filteredCareEvents = useMemo(() => {
    const now = Date.now();
    const periodStart =
      periodFilter === "7d"
        ? now - 7 * 24 * 60 * 60 * 1000
        : periodFilter === "30d"
          ? now - 30 * 24 * 60 * 60 * 1000
          : periodFilter === "90d"
            ? now - 90 * 24 * 60 * 60 * 1000
            : null;

    return careEvents.filter((event) => {
      const eventTimestamp = new Date(
        event.completedAt ?? event.claimSubmittedAt ?? event.authorizedAt ?? event.requestedAt
      ).getTime();

      if (recipientFilter && event.recipientId !== recipientFilter) {
        return false;
      }

      if (categoryFilter && event.categoryCode !== categoryFilter) {
        return false;
      }

      if (periodStart && eventTimestamp < periodStart) {
        return false;
      }

      return true;
    });
  }, [careEvents, categoryFilter, periodFilter, recipientFilter]);

  const succeededServiceActivity = filteredServiceActivity.filter(
    (transaction) => transaction.status === "succeeded"
  );
  const pendingServiceActivity = filteredServiceActivity.filter(
    (transaction) => transaction.status !== "succeeded"
  );
  const totalPurchasedMinor = succeededServiceActivity.reduce(
    (sum, transaction) => sum + Number(transaction.amountMinor ?? 0),
    0
  );
  const statusOptions = Array.from(
    new Set(fundingServiceActivity.map((transaction) => transaction.status).filter(Boolean))
  ).map((status) => ({
    value: status,
    label: getFriendlyStatusLabel(status)
  }));
  const categoryOptionsForActivity = Array.from(
    new Map(
      careEvents.map((event) => [event.categoryCode, event.categoryName ?? normalizeLabel(event.categoryCode)])
    ).entries()
  ).map(([value, label]) => ({ value, label }));
  const deliveredCount = filteredCareEvents.filter((event) => event.completedAt).length;
  const redeemedMinor = filteredCareEvents
    .filter((event) => event.completedAt)
    .reduce((sum, event) => sum + Number(event.amountMinor ?? 0), 0);
  const receiptCount = filteredCareEvents.filter((event) => event.receiptFileUrl).length;
  const filteredCategoryUsage = categoryFilter
    ? categoryUsage.filter((item) => item.categoryCode === categoryFilter)
    : categoryUsage;

  if (!visible) {
    return null;
  }

  return (
    <div className="screen-stack">
      <section className="surface-card">
        <div className="section-head">
          <div>
            <p className="eyebrow">{t("Activity & history")}</p>
            <h2>{t("Sponsor operations view")}</h2>
          </div>
        </div>
        <div className="metrics-grid activity-metrics">
          <MetricCard label="Visible events" value={String(filteredServiceActivity.length)} />
          <MetricCard label="Allowances purchased" value={formatMoney(totalPurchasedMinor)} />
          <MetricCard label="Care delivered" value={String(deliveredCount)} />
          <MetricCard label="Redeemed in view" value={formatMoney(redeemedMinor)} />
        </div>
      </section>

      <section className="surface-card">
        <div className="section-head">
          <div>
            <p className="eyebrow">{t("Filter activity")}</p>
            <h2>{t("Slice by recipient, status, and period")}</h2>
          </div>
        </div>
        <div className="activity-filter-grid">
          <SelectField
            label="Recipient"
            value={recipientFilter}
            onChange={setRecipientFilter}
            placeholder="All recipients"
            options={recipients.map((recipient) => ({
              value: recipient.recipientId,
              label: recipient.fullName
            }))}
          />
          <SelectField
            label="Status"
            value={statusFilter}
            onChange={setStatusFilter}
            placeholder="All statuses"
            options={statusOptions}
          />
          <SelectField
            label="Period"
            value={periodFilter}
            onChange={setPeriodFilter}
            options={[
              { value: "7d", label: "Last 7 days" },
              { value: "30d", label: "Last 30 days" },
              { value: "90d", label: "Last 90 days" },
              { value: "all", label: "All time" }
            ]}
          />
          <SelectField
            label="Category"
            value={categoryFilter}
            onChange={setCategoryFilter}
            placeholder="All categories"
            options={categoryOptionsForActivity}
          />
        </div>
      </section>

      <section className="surface-card">
        <div className="section-head">
          <div>
            <p className="eyebrow">{t("Service allowance activity")}</p>
            <h2>{t("Sponsor-visible service events")}</h2>
          </div>
        </div>
        {fundingServiceActivity.length === 0 ? (
          <EmptyState
            title="No allowance purchases yet"
            message="New allowance purchases and successful payments will appear here as soon as you purchase allowances for recipients."
          />
        ) : filteredServiceActivity.length === 0 ? (
          <EmptyState
            title="No matching activity"
            message="Try a wider time range or clear one of the filters to see more allowance history."
          />
        ) : (
          <div className="activity-list">
            {filteredServiceActivity
              .slice()
              .sort(
                (left, right) =>
                  new Date(right.fundedAt ?? right.createdAt).getTime() -
                  new Date(left.fundedAt ?? left.createdAt).getTime()
              )
              .map((transaction) => (
                <div className="history-card activity-card" key={transaction.fundingTransactionId}>
                  <div className="entity-topline">
                    <div>
                      <p className="activity-kicker">{t("Allowance purchase")}</p>
                      <strong>
                        {formatMoney(transaction.amountMinor, transaction.currency)}
                      </strong>
                    </div>
                    <span className={`badge badge-${transaction.status}`}>
                      {t(getFriendlyStatusLabel(transaction.status))}
                    </span>
                  </div>
                  <div className="activity-meta-grid">
                    <div>
                      <span className="meta-label">{t("Recipient")}</span>
                      <p className="meta-value">
                        {recipientMap.get(transaction.recipientId) ?? t("Recipient")}
                      </p>
                    </div>
                    <div>
                      <span className="meta-label">{t("Payment method")}</span>
                      <p className="meta-value">
                        {t("Banxito card payment")}
                      </p>
                    </div>
                    <div>
                      <span className="meta-label">{t("Created")}</span>
                      <p className="meta-value">
                        {new Date(transaction.createdAt).toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <span className="meta-label">{t("Completed")}</span>
                      <p className="meta-value">
                        {transaction.fundedAt
                          ? new Date(transaction.fundedAt).toLocaleString()
                          : t("Not completed yet")}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        )}
      </section>

      <section className="surface-card">
        <div className="section-head">
          <div>
            <p className="eyebrow">{t("Category usage")}</p>
            <h2>{t("Where sponsored care services were fulfilled")}</h2>
          </div>
        </div>
        {filteredCategoryUsage.length === 0 ? (
          <EmptyState
            title="No redeemed category usage yet"
            message="Once a provider completes care and a redemption is recorded, the sponsor will see category usage totals here."
          />
        ) : (
          <div className="wallet-grid usage-grid">
            {filteredCategoryUsage.map((item) => (
              <div className="wallet-chip usage-chip" key={item.categoryCode}>
                <div>
                  <strong>{t(item.categoryName)}</strong>
                  <p className="helper-text muted">
                    {item.redeemedCount} redemption{item.redeemedCount === 1 ? "" : "s"} ·{" "}
                    {item.recipientsHelped} recipient{item.recipientsHelped === 1 ? "" : "s"}
                  </p>
                </div>
                <div className="usage-amount">
                  <strong>{formatMoney(item.redeemedMinor)}</strong>
                  <p className="helper-text muted">
                    {item.lastRedeemedAt
                      ? `Last used ${new Date(item.lastRedeemedAt).toLocaleDateString()}`
                      : "No completed delivery yet"}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="surface-card">
        <div className="section-head">
          <div>
            <p className="eyebrow">{t("Delivered care")}</p>
            <h2>{t("Provider events, receipts, and proof of service")}</h2>
          </div>
        </div>
        <div className="metrics-grid activity-metrics care-metrics">
          <MetricCard label="Delivered" value={String(deliveredCount)} />
          <MetricCard label="With receipts" value={String(receiptCount)} />
          <MetricCard
            label="Awaiting completion"
            value={String(filteredCareEvents.filter((event) => !event.completedAt).length)}
          />
          <MetricCard
            label="Claims submitted"
            value={String(filteredCareEvents.filter((event) => event.claimId).length)}
          />
        </div>
        {careEvents.length === 0 ? (
          <EmptyState
            title="No provider redemptions yet"
            message="This feed will populate after providers authorize, redeem, and submit proof for real services delivered."
          />
        ) : filteredCareEvents.length === 0 ? (
          <EmptyState
            title="No matching care events"
            message="Try a different recipient, category, or time range to see more delivered-care history."
          />
        ) : (
          <div className="activity-list">
            {filteredCareEvents
              .slice()
              .sort(
                (left, right) =>
                  new Date(
                    right.completedAt ??
                      right.claimSubmittedAt ??
                      right.authorizedAt ??
                      right.requestedAt
                  ).getTime() -
                  new Date(
                    left.completedAt ??
                      left.claimSubmittedAt ??
                      left.authorizedAt ??
                      left.requestedAt
                  ).getTime()
              )
              .map((event) => (
                <div className="history-card activity-card care-card" key={event.redemptionId}>
                  <div className="entity-topline">
                    <div>
                      <p className="activity-kicker">{t("Delivered service")}</p>
                      <strong>{event.serviceName ?? t(event.categoryName)}</strong>
                    </div>
                    <span className={`badge badge-${event.redemptionStatus}`}>
                      {t(normalizeLabel(event.redemptionStatus))}
                    </span>
                  </div>
                  <div className="activity-meta-grid">
                    <div>
                      <span className="meta-label">{t("Recipient")}</span>
                      <p className="meta-value">{event.recipientName}</p>
                    </div>
                    <div>
                      <span className="meta-label">{t("Provider")}</span>
                      <p className="meta-value">{event.providerName}</p>
                    </div>
                    <div>
                      <span className="meta-label">{t("Category")}</span>
                      <p className="meta-value">{t(event.categoryName)}</p>
                    </div>
                    <div>
                      <span className="meta-label">{t("Amount used")}</span>
                      <p className="meta-value">
                        {formatMoney(event.amountMinor, event.currency)}
                      </p>
                    </div>
                    <div>
                      <span className="meta-label">{t("Completed")}</span>
                      <p className="meta-value">
                        {event.completedAt
                          ? new Date(event.completedAt).toLocaleString()
                          : t("Not completed yet")}
                      </p>
                    </div>
                    <div>
                      <span className="meta-label">{t("Claim status")}</span>
                      <p className="meta-value">
                        {event.claimStatus ? t(normalizeLabel(event.claimStatus)) : t("No claim yet")}
                      </p>
                    </div>
                  </div>
                  <div className="activity-reference">
                    <span className="meta-label">{t("Receipt")}</span>
                    {event.receiptFileUrl ? (
                      <p className="meta-value">
                        <a
                          className="receipt-link"
                          href={event.receiptFileUrl}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {t("Open provider receipt")}
                        </a>
                      </p>
                    ) : (
                      <p className="meta-value">{t("Receipt not uploaded yet")}</p>
                    )}
                  </div>
                </div>
              ))}
          </div>
        )}
      </section>
    </div>
  );
}

function SavedPaymentMethodSetupForm({
  setupIntentId,
  onCancel,
  onSaved,
  runWithSessionRetry
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState("");

  async function handleSubmit(event) {
    event.preventDefault();
    if (!stripe || !elements) {
      return;
    }

    setSubmitting(true);
    setStatus("");

    try {
      const submitResult = await elements.submit();
      if (submitResult.error) {
        throw new Error(submitResult.error.message);
      }

      const result = await stripe.confirmSetup({
        elements,
        redirect: "if_required"
      });

      if (result.error) {
        throw new Error(result.error.message);
      }

      const completedSetupIntentId = result.setupIntent?.id ?? setupIntentId;
      const response = await runWithSessionRetry((activeSession) =>
        apiRequest("/sponsors/me/payment-methods/confirm", {
          method: "POST",
          token: activeSession.idToken,
          body: {
            setupIntentId: completedSetupIntentId,
            makeDefault: true
          }
        })
      );

      setStatus("Saved. Banxito can now use this card for future allowance purchases you approve.");
      await onSaved(response.paymentMethod);
    } catch (error) {
      setStatus(error.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form className="stack-form saved-card-setup" onSubmit={handleSubmit}>
      <div className="payment-shell">
        <PaymentElement />
      </div>
      <div className="action-row compact-actions">
        <button className="primary-button" disabled={!stripe || submitting}>
          {submitting ? "Saving card..." : "Save card securely"}
        </button>
        <button className="ghost-button" type="button" onClick={onCancel}>
          Cancel
        </button>
      </div>
      {status ? <p className="payment-method-note">{status}</p> : null}
    </form>
  );
}

function AccountTab({
  visible,
  summary,
  session,
  onUpdateProfile,
  onChangePassword,
  runWithSessionRetry,
  onPaymentMethodSaved
}) {
  const { t } = useI18n();
  const initialName = splitFullName(summary?.sponsor?.fullName ?? "");
  const [profileForm, setProfileForm] = useState({
    firstName: summary?.sponsor?.firstName ?? initialName.firstName,
    lastName: summary?.sponsor?.lastName ?? initialName.lastName,
    phone: summary?.sponsor?.phone ?? ""
  });
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    nextPassword: "",
    confirmPassword: ""
  });
  const [paymentNotice, setPaymentNotice] = useState("");
  const [setupIntent, setSetupIntent] = useState(null);
  const [startingSetup, setStartingSetup] = useState(false);
  const savedPaymentMethod =
    summary?.sponsor?.defaultPaymentMethod ??
    summary?.sponsor?.paymentMethod ??
    summary?.defaultPaymentMethod ??
    summary?.paymentMethod ??
    null;

  useEffect(() => {
    const nextName = splitFullName(summary?.sponsor?.fullName ?? "");
    setProfileForm({
      firstName: summary?.sponsor?.firstName ?? nextName.firstName,
      lastName: summary?.sponsor?.lastName ?? nextName.lastName,
      phone: summary?.sponsor?.phone ?? ""
    });
  }, [
    summary?.sponsor?.firstName,
    summary?.sponsor?.lastName,
    summary?.sponsor?.fullName,
    summary?.sponsor?.phone
  ]);

  if (!visible) {
    return null;
  }

  async function handleStartSavedCardSetup() {
    if (!stripePromise || !config.stripePublishableKey) {
      setPaymentNotice("Payment setup is missing. Add the Banxito payment public key first.");
      return;
    }

    setStartingSetup(true);
    setPaymentNotice("");
    try {
      const response = await runWithSessionRetry((activeSession) =>
        apiRequest("/sponsors/me/payment-methods/setup-intent", {
          method: "POST",
          token: activeSession.idToken
        })
      );
      setSetupIntent({
        setupIntentId: response.setupIntentId,
        clientSecret: response.clientSecret
      });
    } catch (error) {
      setPaymentNotice(error.message);
    } finally {
      setStartingSetup(false);
    }
  }

  async function handleSavedPaymentMethod() {
    setSetupIntent(null);
    setPaymentNotice("Card saved securely. You can use it for future allowance increases and scheduled allowance purchases.");
    await onPaymentMethodSaved();
  }

  return (
    <div className="screen-stack">
      <section className="surface-card">
        <div className="section-head">
          <div>
            <p className="eyebrow">{t("Account")}</p>
            <h2>{t("Sponsor profile")}</h2>
          </div>
        </div>
        <form
          className="stack-form"
          onSubmit={(event) => {
            event.preventDefault();
            void onUpdateProfile(profileForm);
          }}
        >
          <div className="two-col">
            <Field
              label="First name"
              value={profileForm.firstName}
              onChange={(value) =>
                setProfileForm((current) => ({ ...current, firstName: value }))
              }
            />
            <Field
              label="Last name"
              value={profileForm.lastName}
              onChange={(value) =>
                setProfileForm((current) => ({ ...current, lastName: value }))
              }
            />
          </div>
          <Field label="Email" value={summary?.sponsor?.email ?? session.email} disabled />
          <Field
            label="Phone"
            type="tel"
            value={profileForm.phone}
            onChange={(value) =>
              setProfileForm((current) => ({ ...current, phone: value }))
            }
          />
          <div className="detail-list">
            <DetailRow label="Locale" value={summary?.sponsor?.locale ?? "en-US"} />
          </div>
          <button className="primary-button">{t("Save profile updates")}</button>
        </form>
      </section>

      <section className="surface-card">
        <div className="section-head">
          <div>
            <p className="eyebrow">{t("Payment")}</p>
            <h2>{t("Saved payment method")}</h2>
          </div>
        </div>
        <div className="payment-method-card">
          {savedPaymentMethod ? (
            <div className="card-chip-row">
              <div>
                <span className="field-helper">{t("Current card")}</span>
                <strong>
                  {savedPaymentMethod.brand ?? t("Card")} {t("ending in")}{" "}
                  {savedPaymentMethod.last4 ?? "••••"}
                </strong>
                <p className="field-helper">
                  {t("Used for repeat allowance purchases, allowance increases, and future scheduled support.")}
                </p>
              </div>
              <span className="card-brand-badge">
                {String(savedPaymentMethod.brand ?? t("Card")).toUpperCase()}
              </span>
            </div>
          ) : (
            <div>
              <strong>{t("No saved card yet")}</strong>
              <p className="field-helper">
                {t("Add a card here later to make repeat allowance purchases faster and to support automatic or monthly allowance increases.")}
              </p>
            </div>
          )}
          <div className="action-row compact-actions">
            <button
              className="primary-button"
              type="button"
              onClick={handleStartSavedCardSetup}
              disabled={startingSetup}
            >
              {startingSetup
                ? t("Opening secure form...")
                : savedPaymentMethod
                  ? t("Update card")
                  : t("Add card")}
            </button>
            {savedPaymentMethod ? (
              <button
                className="ghost-button"
                type="button"
                onClick={() =>
                  setPaymentNotice(
                    "For safety, removing saved cards will be added as a separate confirmation step."
                  )
                }
              >
                {t("Remove saved card")}
              </button>
            ) : null}
          </div>
          {setupIntent?.clientSecret ? (
            <Elements
              stripe={stripePromise}
              options={{
                clientSecret: setupIntent.clientSecret,
                appearance: stripeElementAppearance
              }}
            >
              <SavedPaymentMethodSetupForm
                setupIntentId={setupIntent.setupIntentId}
                runWithSessionRetry={runWithSessionRetry}
                onSaved={handleSavedPaymentMethod}
                onCancel={() => setSetupIntent(null)}
              />
            </Elements>
          ) : null}
          {paymentNotice ? <p className="payment-method-note">{paymentNotice}</p> : null}
        </div>
      </section>

      <section className="surface-card">
        <div className="section-head">
          <div>
            <p className="eyebrow">{t("Security")}</p>
            <h2>{t("Change password")}</h2>
          </div>
        </div>
        <form
          className="stack-form"
          onSubmit={async (event) => {
            event.preventDefault();
            const changed = await onChangePassword(passwordForm);
            if (changed) {
              setPasswordForm({
                currentPassword: "",
                nextPassword: "",
                confirmPassword: ""
              });
            }
          }}
        >
          <Field
            label="Current password"
            type="password"
            value={passwordForm.currentPassword}
            onChange={(value) =>
              setPasswordForm((current) => ({ ...current, currentPassword: value }))
            }
          />
          <Field
            label="New password"
            type="password"
            value={passwordForm.nextPassword}
            onChange={(value) =>
              setPasswordForm((current) => ({ ...current, nextPassword: value }))
            }
          />
          <Field
            label="Confirm new password"
            type="password"
            value={passwordForm.confirmPassword}
            onChange={(value) =>
              setPasswordForm((current) => ({ ...current, confirmPassword: value }))
            }
          />
          <button className="ghost-button">{t("Update password")}</button>
        </form>
      </section>
    </div>
  );
}

function LanguageSelect({ compact = false }) {
  const { language, setLanguage, t } = useI18n();

  return (
    <label className={compact ? "language-switch compact" : "language-switch"}>
      <span>{t("Language")}</span>
      <select
        value={language}
        onChange={(event) => setLanguage(event.target.value)}
        aria-label={t("Language")}
      >
        {languageOptions.map((option) => (
          <option key={option.value} value={option.value}>
            {languageDisplayLabels[option.value] ?? option.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function Field({
  label,
  value,
  onChange,
  type = "text",
  disabled = false,
  helper = null,
  trailingAction = null,
  placeholder = ""
}) {
  const { t } = useI18n();
  return (
    <label className="field">
      <span>{t(label)}</span>
      <div className="input-wrap">
        <input
          className="input-shell"
          type={type}
          value={value}
          disabled={disabled}
          placeholder={placeholder ? t(placeholder) : ""}
          onChange={onChange ? (event) => onChange(event.target.value) : undefined}
        />
        {trailingAction ? (
          <button className="inline-action" type="button" onClick={trailingAction.onClick}>
            {t(trailingAction.label)}
          </button>
        ) : null}
      </div>
      {helper ? <span className="field-helper">{t(helper)}</span> : null}
    </label>
  );
}

function PasswordChecklist({ checks }) {
  const { t } = useI18n();
  return (
    <div className="password-checklist">
      {checks.map((check) => (
        <div className="password-check-row" key={check.label}>
          <span className={check.ok ? "password-check-pass" : "password-check-pending"}>
            {check.ok ? t("OK") : t("Add")}
          </span>
          <span className="field-helper">{t(check.label)}</span>
        </div>
      ))}
    </div>
  );
}

function SelectField({
  label,
  value,
  onChange,
  options,
  disabled = false,
  placeholder = ""
}) {
  const { t } = useI18n();
  return (
    <label className="field">
      <span>{t(label)}</span>
      <div className="select-shell">
        <select
          className="input-shell input-select"
          value={value}
          disabled={disabled}
          onChange={(event) => onChange(event.target.value)}
        >
          {placeholder ? <option value="">{t(placeholder)}</option> : null}
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {t(option.label)}
            </option>
          ))}
        </select>
        <span className="select-chevron" aria-hidden="true">
          ▾
        </span>
      </div>
    </label>
  );
}

function MetricCard({ label, value }) {
  const { t } = useI18n();
  return (
    <div className="metric-card">
      <span>{t(label)}</span>
      <strong>{value}</strong>
    </div>
  );
}

function DetailRow({ label, value, mono = false }) {
  const { t } = useI18n();
  return (
    <div className="detail-row">
      <span>{t(label)}</span>
      <strong className={mono ? "mono-line" : ""}>{value}</strong>
    </div>
  );
}

function EmptyState({ title, message }) {
  const { t } = useI18n();
  return (
    <div className="empty-state">
      <strong>{t(title)}</strong>
      <p>{t(message)}</p>
    </div>
  );
}

function SkeletonCards({ count }) {
  return Array.from({ length: count }).map((_, index) => (
    <div className="skeleton-card" key={index} />
  ));
}

export default App;
